'use client';

import { withPromptInventoryBoundary } from '@/components/shared/PromptInventoryBoundary';

import { buildLeaderboardBrandHref } from '@/utils/brandDiscovery';

import { PromptScopeFilter } from '@/components/shared/PromptScopeFilter';
import { usePipelineFilterOptions } from '@/hooks/usePipelineFilterOptions';
import {
  resolvePromptTopicFilter,
  promptTopicFilterNeedsInventory,
  type PromptTopicFilter,
} from '@/utils/promptTopicFilter';
import { projectTopicOptions, topicOrigin } from '@/utils/topicOptions';
import { InlineFilterGroup } from '@/components/shared/InlineFilterControls';

import {
  ComposerShell,
  ComposerInput,
  ComposerControls,
  ComposerActionButton,
} from '@/components/research-assistant/ComposerLayout';
import {
  SlackTaskDeliveryField,
  type TaskSlackDeliveryInput,
} from '@/components/background-tasks/SlackTaskDeliveryField';
import { SEARCHAPI_ENGINE_LABELS } from '@/lib/pipeline-constants';

import {
  Badge,
  Box,
  Button,
  Center,
  Dialog,
  Field,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Link as ChakraLink,
  Portal,
  SimpleGrid,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import type { User } from 'firebase/auth';
import {
  Activity,
  AlertTriangle,
  ArrowUp,
  BarChart3,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  ChartPie,
  PauseCircle,
  PlayCircle,
  Plug,
  Search,
  ShoppingBag,
  Sparkles,
  Square,
  TrendingUp,
  Workflow,
  X,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { toast } from 'sonner';
import { SlackConversationBanner } from '@/components/slack/SlackConversationBanner';
import { slackPublicDelivery } from '@/lib/slack-agent/session-ui';
import { getGrowthActivityFeedLabels } from '@/app/app/home/growth-activity-labels';
import type {
  ComposerDocumentRestoration,
  DocumentAttachment,
  ImageAttachment,
  Message,
  TraceTimelineItem,
} from '@/app/app/research-assistant/types';
import { useAuth } from '@/app/auth-provider';
import { WorkspaceSetupEntry } from '@/components/onboarding/WorkspaceSetupEntry';
import { isProjectWorkspaceReady } from '@/lib/projects/setup';
import {
  getWorkspaceHomeWidgetVisibility,
  type HomeModuleStatus,
} from '@/lib/workspace-home-readiness';
import {
  BACKGROUND_TASK_CREATOR_SKILL_ID,
  BackgroundTaskComposerContext,
} from '@/components/background-tasks/BackgroundTaskIntent';
import {
  AppShellToolbarPortal,
  AppShellToolbarTabButton,
  AppShellToolbarTabs,
} from '@/components/core/app-layout/AppShellToolbar';
import { AppShellHeaderPortal } from '@/components/core/app-layout/AppShellHeader';
import {
  getCompletedOnboardingWorkspaceHref,
  getHybridOnboardingTarget,
  getOnboardingCompletionReceipt,
  getOnboardingWorkspaceIntent,
  getReusableOnboardingSessionProjectId,
} from '@/components/onboarding/hybrid-onboarding-access';
import { DocumentAttachmentChip } from '@/components/research-assistant/DocumentAttachmentChip';
import { ExecutionModeSelector } from '@/components/research-assistant/ExecutionModeSelector';
import { ModelSelector } from '@/components/research-assistant/ModelSelector';
import { RunModeComposerContext } from '@/components/research-assistant/RunModeComposerContext';
import { SessionShareDialog } from '@/components/research-assistant/SessionShareDialog';
import { ThreadHeader } from '@/components/research-assistant/ThreadHeader';
import {
  ComposerAddMenu,
  getSkillTriggerQuery,
  removeSkillTrigger,
  SelectedSkillChips,
  SkillsPicker,
  useEffectiveAgentSkills,
  type SkillPickerItem,
} from '@/components/research-assistant/SkillsPicker';
import type { GrowthWorkspaceActivityEvent } from '@/types/growth-workspace';
import { InlineSpinner, PageLoader } from '@/components/shared/PageLoader';
import { AttuneSelect, StatusBadge } from '@/components/shared/ui';
import { useLivingDocsPanel, useLivingDocsPanelActions } from '@/contexts/LivingDocsPanelContext';
import { useProject } from '@/contexts/ProjectContext';
import { useBrandsPageDiscovery } from '@/hooks/useBrandsPageDiscovery';
import { useDesktopViewport } from '@/hooks/useDesktopViewport';
import { useResearchChat } from '@/hooks/useResearchChat';
import { useResearchModelSelection } from '@/hooks/useResearchModelSelection';
import { useResearchSession } from '@/hooks/useResearchSession';
import {
  createAttuneClientRequestError,
  isAttuneClientRequestError,
  isOptionalAttunePreloadError,
} from '@/lib/attune/client-errors';
import { isAiCmoBetaEnabled } from '@/lib/attune/growth/ai-cmo-feature';
import { formatCodexRelativeTime, titleLabel } from '@/lib/codex-workspace-format';
import {
  getPersistedResearchModelOption,
  ONBOARDING_RESEARCH_MODEL_ID,
  RECOMMENDED_RESEARCH_MODEL_ID,
  type PublicResearchModel,
} from '@/lib/research-assistant/models';
import { capture } from '@/lib/posthog';
import { WORKSPACE_URL_CHANGE_EVENT } from '@/lib/posthog-pageview';
import { isGoogleWorkspaceExportDeliveryEnabledClient } from '@/lib/research-assistant/export-feature-flags';
import {
  isGitHubWebsiteEnabledClient,
  isGitHubBindingEnabledClient,
} from '@/lib/github/feature-flags';
import { isWordPressMcpEnabledClient } from '@/lib/wordpress/feature-flags';
import { resolveExecutionDirective } from '@/lib/research-assistant/execution-directive';
import {
  WORKSPACE_WEBSITE_PREVIEW_LAUNCH_EVENT,
  type WorkspaceWebsitePreviewLaunchDetail,
} from '@/lib/github/preview/workspace-launch';
import type { ResearchRunReplyStatus } from '@/lib/research-assistant/plan-approval';
import type { ResearchExecutionMode } from '@/lib/research-assistant/runtime/run-controller';
import type { ResearchReasoningEffort } from '@/lib/research-assistant/reasoning-effort';
import { LIVING_REPORT_BUILDER_SKILL_ID } from '@/lib/reports/default-skills';
import { DataControlCenter } from '../../data-explorer/DataControlCenter';
import { WorkspaceQuestionSuggestions } from './WorkspaceQuestionSuggestions';
import { getAgentPageHref } from '@/lib/agents/routes';
import { createAsyncResourceCache } from '@/lib/async-resource-cache';
import {
  buildBrandHomeTrendPoints,
  buildBrandHomeWidgetSummary,
  type BrandHomeTrendPoint,
  type BrandHomeTrendRow,
} from '@/lib/workspace-home-brand-widget';
import {
  documentAttachmentsUnavailableMessage,
  imageAttachmentsUnavailableMessage,
  modelSupportsDocumentAttachments,
  modelSupportsImageAttachments,
  modelDocumentAcceptAttribute,
  unsupportedDocumentAttachmentMessage,
} from '@/lib/research-assistant/model-attachments';
import {
  documentFileTypeFromName,
  MAX_DOCUMENT_ATTACHMENTS,
  MAX_DOCUMENT_TOTAL_BYTES,
  mergeRestoredDocumentAttachments,
} from '@/lib/research-assistant/document-attachments';
import { buildConfiguredBrandGroups, matchesBrandValue } from '@/utils/brandMatching';
import { deleteUploadedDocument, uploadDocumentAttachment } from '@/utils/document-utils';
import { fileToImageAttachment, getImageDataUrl, isSupportedImageType } from '@/utils/image-utils';
import { FirstCollectionProgressCard } from '../first-collection-progress-card';
import { HomeWorkWidget } from '../workspace-home-dashboard';

function WorkspaceLazyPanelFallback() {
  return (
    <Center minH="160px" w="full">
      <InlineSpinner />
    </Center>
  );
}

const ChatPanel = dynamic(
  () => import('@/components/research-assistant/ChatPanel').then(module => module.ChatPanel),
  { loading: WorkspaceLazyPanelFallback }
);
const GrowthProgramCockpit = dynamic(
  () =>
    import('@/components/growth-program/GrowthProgramCockpit').then(
      module => module.GrowthProgramCockpit
    ),
  { loading: WorkspaceLazyPanelFallback }
);
const WorkspaceChart = dynamic(
  () => import('../workspace-chart').then(module => module.WorkspaceChart),
  {
    ssr: false,
    loading: () => (
      <Box
        role="status"
        aria-label="Loading chart"
        aria-busy="true"
        h="full"
        w="full"
        borderRadius="6px"
        bg="ui.surface.selected"
      />
    ),
  }
);

function WorkspaceModeLoadingFallback() {
  return <PageLoader variant="section" minH="100%" />;
}

const GrowthPlanWorkspace = dynamic(
  () => import('../workspace-growth-plan').then(module => module.GrowthPlanWorkspace),
  { loading: WorkspaceModeLoadingFallback }
);
const GrowthWorkCenter = dynamic(
  () => import('../workspace-growth-tasks').then(module => module.GrowthWorkCenter),
  { loading: WorkspaceModeLoadingFallback }
);
const TaskDetailDrawer = dynamic(
  () => import('../workspace-growth-tasks').then(module => module.TaskDetailDrawer),
  { loading: () => null }
);
const IntegrationsWorkspace = dynamic(
  () => import('../workspace-integrations').then(module => module.IntegrationsWorkspace),
  { loading: WorkspaceModeLoadingFallback }
);
const AgentWorkspace = dynamic(
  () => import('../workspace-agents').then(module => module.AgentWorkspace),
  { loading: WorkspaceModeLoadingFallback }
);

type WorkspaceMode = 'data' | 'chat' | 'growth-program' | 'tasks' | 'agents' | 'integrations';
export type GrowthWorkTab = 'tasks' | 'approvals' | 'activity' | 'measuring' | 'completed';
export type GrowthIssueListTab = Exclude<GrowthWorkTab, 'activity' | 'approvals'>;
export type BackgroundTaskTab = 'tasks' | 'runs' | 'templates';
type WorkspaceComposerIntent = 'research' | 'background_task';
type WorkspaceRefreshMode = 'passive' | 'interactive';
type ComposerMode = 'preset' | 'custom';
type AgentMarketplaceCategory = 'monitoring' | 'execution';
const ONBOARDING_EXECUTION_MODES: ResearchExecutionMode[] = ['standard'];
const LIVE_SESSION_REFRESH_INTERVAL_MS = 4_000;
let workspaceHardNavigationTimingCaptured = false;
const WORKSPACE_MODE_LABELS: Record<WorkspaceMode, string> = {
  data: 'Data Explorer',
  chat: 'Chat',
  'growth-program': 'Growth Plan',
  tasks: 'Tasks',
  agents: 'Agents',
  integrations: 'Plugins',
};

function workspaceModeFromUrl(value: string | null): WorkspaceMode {
  if (
    value === 'data' ||
    value === 'tasks' ||
    value === 'growth-program' ||
    value === 'agents' ||
    value === 'integrations' ||
    value === 'chat'
  ) {
    return value;
  }
  return 'chat';
}

function workspaceModeHref(mode: WorkspaceMode): string {
  if (mode === 'data') return '/app/data-explorer';
  if (mode === 'chat') return '/app/workspace';
  if (mode === 'agents') return '/app/agents';
  if (mode === 'tasks') return '/app/workspace?mode=tasks&tab=tasks';
  return `/app/workspace?mode=${mode}`;
}

function growthWorkTabFromUrl(value: string | null): GrowthWorkTab {
  if (
    value === 'tasks' ||
    value === 'approvals' ||
    value === 'activity' ||
    value === 'measuring' ||
    value === 'completed'
  ) {
    return value;
  }
  return 'tasks';
}

export type AgentBusyState = {
  id: string;
  action: 'run' | 'pause' | 'resume' | 'prepare' | 'confirm' | 'model' | 'edit';
} | null;
type PendingWorkspaceMessage = {
  content: string;
  images?: ImageAttachment[];
  documents?: DocumentAttachment[];
  selectedSkills?: Array<{ skillId: string; versionId?: string }>;
  executionMode?: ResearchExecutionMode;
  context?: ComposerMessageContext;
};

type ComposerMessageContext = {
  onAccepted?: () => void;
  targetModelId?: string;
  targetActiveReportId?: string | null;
  reasoningEffort?: ResearchReasoningEffort;
};

export type GrowthIssueStatus =
  | 'open'
  | 'claimed'
  | 'in_progress'
  | 'submitted'
  | 'measuring'
  | 'verified'
  | 'closed'
  | 'dismissed'
  | 'regression_candidate';

type GrowthIssueOutcome =
  | 'helped'
  | 'no_movement'
  | 'regressed'
  | 'unknown'
  | 'regression_candidate';

export type AutomationStatus = 'enabled' | 'paused' | 'archived';
export type ScanRunStatus = 'queued' | 'running' | 'completed' | 'failed';
export type TriggerType =
  | 'manual'
  | 'sampler_completed_delay'
  | 'daily_scan'
  | 'weekly_scan'
  | 'twice_monthly_scan'
  | 'monthly_scan';

const CHAT_SESSION_QUERY_KEYS_TO_CLEAR = [
  'sessionId',
  'issue',
  'northStar',
  'promptId',
  'jobId',
  'job_id',
  'jobIds',
  'job_ids',
  'engine',
  'command',
  'question',
  'ctx',
  'mode',
  'tab',
  'approvalId',
  'editor',
] as const;
export type ScanDataSource =
  | 'sampler'
  | 'ai_search'
  | 'gsc'
  | 'ga4'
  | 'bing'
  | 'clarity'
  | 'cloudflare'
  | 'ads'
  | 'webflow'
  | 'shopify'
  | 'wordpress'
  | 'github'
  | 'custom_mcp';

interface ShopifyConnectionSummary {
  id?: string | null;
  shop_domain?: string | null;
  status?: string | null;
  shop?: {
    currencyCode?: string | null;
  } | null;
  capabilities?: {
    readReports?: boolean;
    shopifyAnalytics?: boolean;
  } | null;
}

interface ShopifyStatusSummary {
  connected?: boolean;
  configured?: boolean;
  connections?: ShopifyConnectionSummary[];
  usableConnectionIds?: string[];
}

interface ShopifyRevenueSummaryPayload {
  tableData?: {
    rows?: unknown;
  } | null;
  currencyCode?: string | null;
  error?: string;
  message?: string;
}

interface WorkspaceShopifyRevenueState {
  statusLoading: boolean;
  available: boolean;
  loading: boolean;
  error: string | null;
  totalSales: number;
  orders: number;
  averageOrderValue: number;
  changePct: number | null;
  currencyCode: string | null;
  trend: Array<{ date: string; totalSales: number }>;
}

interface WebflowStatusSummary {
  connected?: boolean;
  writable?: boolean;
  connection?: { status?: string | null; warnings?: string[] | null } | null;
}

interface WordPressStatusSummary {
  enabled?: boolean;
  connected?: boolean;
  binding?: { canRead?: boolean; canWrite?: boolean } | null;
}

interface GitHubStatusSummary {
  installation?: { status?: string | null } | null;
  binding?: {
    status?: string | null;
    framework?: 'astro' | 'next' | null;
    detected_astro?: boolean;
  } | null;
}

interface CustomMcpStatusConnection {
  status?: string | null;
  exposeToMcp?: boolean | null;
  enabledToolCount?: number | null;
  lastSyncStatus?: string | null;
  lastSyncError?: string | null;
}

interface CustomMcpStatusResponse {
  connections?: CustomMcpStatusConnection[];
}

export interface AgentDeliveryConnections {
  webflow: boolean;
  shopify: boolean;
  wordpress: boolean;
  github: boolean;
  customMcp: boolean;
  loading: boolean;
}

export interface ScanPromptTemplate {
  id: string;
  promptId: string;
  name: string;
  description: string;
  body: string;
  version: number;
  marketerRole?: string;
  runtimeSkillId?: string;
  sourceFrameworks?: string[];
  dataSources: ScanDataSource[];
  allowedTools: string[];
  requiredToolSequence?: string[];
  outputContract: string[] | Record<string, unknown>;
  validTaskExample?: string;
  invalidTaskExamples?: string[];
  changelog: string[];
}

export interface GrowthIssueRecord {
  id: string;
  title: string;
  problem_statement: string;
  recommended_action: string;
  category: string;
  severity: 'high' | 'medium' | 'low';
  priority_score: number;
  program_id?: string;
  program_revision?: number;
  bet_id?: string;
  intervention_id?: string;
  manager_priority_score?: number;
  dependency_issue_ids?: string[];
  status: GrowthIssueStatus;
  outcome?: GrowthIssueOutcome;
  source: {
    scan_run_id?: string;
    scan_automation_id?: string;
    job_id?: string;
  };
  evidence: {
    summary?: string;
    marketer_role?: string;
    asset_type?: string;
    asset_id?: string;
    acceptance_criteria?: string;
    verification_plan?: string;
    confidence?: 'low' | 'medium' | 'high';
    affected_domains?: string[];
    affected_urls?: string[];
    lane_tags?: string[];
    evidence_keys: string[];
    prompt_ids: string[];
  };
  page_review?: {
    schema_version: 1;
    review_id: string;
    crawl_id: string;
    page_id: string;
    page_url: string;
    html_sha256: string;
    sampler_job_id: string;
    callouts: Array<{
      id: string;
      order: number;
      priority: 'must_fix' | 'should_fix';
      label: string;
      category: string;
      current_or_gap: string;
      proposed_change: string;
      why: string;
      locator: {
        source_quote: string;
        section_heading: string;
        occurrence: number;
      };
      resolved_anchor: {
        selector: string;
        text_sha256: string;
      };
      evidence: Array<{
        lane: string;
        source: string;
        id: string;
        note: string;
        sample_count?: number;
        sample_denominator?: number;
        fanout_query?: string;
      }>;
      acceptance_criterion: string;
    }>;
  };
  owner?: {
    type: 'user' | 'agent' | 'system' | 'unassigned';
    name: string;
    uid?: string;
    email?: string;
    run_id?: string;
  };
  latest_verification?: {
    outcome: GrowthIssueOutcome;
    comparison_scan_run_id?: string;
    confidence: 'low' | 'medium' | 'high';
    explanation: string;
    recommended_next_step?: string;
  };
  detection_count: number;
  last_detected_at: string | null;
  updated_at: string | null;
}

export interface GrowthIssuePaginationRecord {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  snapshotId?: string;
}

export type GrowthTaskRiskFilter = 'all' | 'high' | 'medium' | 'low';
export type GrowthTaskSortField = 'title' | 'status' | 'priority' | 'owner' | 'updated';
export type GrowthTaskSortDirection = 'asc' | 'desc';

export interface GrowthTaskListQuery {
  riskFilter: GrowthTaskRiskFilter;
  sortField: GrowthTaskSortField;
  sortDirection: GrowthTaskSortDirection;
  dateRange: {
    key: string;
    label: string;
    startDate: string;
    endDate: string;
  };
}

function defaultGrowthTaskListQuery(): GrowthTaskListQuery {
  const end = new Date();
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 36_524);
  return {
    riskFilter: 'all',
    sortField: 'updated',
    sortDirection: 'desc',
    dateRange: {
      key: 'all',
      label: 'All time',
      startDate: toUtcDateKey(start),
      endDate: toUtcDateKey(end),
    },
  };
}

function growthTaskListQueryKey(query: GrowthTaskListQuery): string {
  return [
    query.riskFilter,
    query.sortField,
    query.sortDirection,
    query.dateRange.key,
    query.dateRange.startDate,
    query.dateRange.endDate,
  ].join('\u0000');
}

export interface GrowthInboxSummaryRecord {
  counts_by_status: Record<GrowthIssueStatus, number>;
  closed_this_week: number;
  verified_this_week: number;
  measuring: number;
  in_progress: number;
  helped: number;
  no_movement: number;
  regressed: number;
  unknown: number;
  weekly_streak: number;
}

export interface GrowthWorkAttemptRecord {
  id: string;
  status: string;
  actor: {
    type: 'user' | 'agent';
    name: string;
    uid?: string;
    email?: string;
    run_id?: string;
  };
  logs: Array<{ summary: string; created_at: string }>;
  receipt?: {
    summary: string;
    evidence?: string;
    changed_urls: string[];
    changed_files: string[];
    commit_urls: string[];
    pr_urls: string[];
  };
  created_at: string | null;
  submitted_at: string | null;
}

export interface GrowthLedgerEventRecord {
  id: string;
  type: string;
  summary: string;
  created_at: string | null;
}

export type GrowthActivityEventRecord = GrowthWorkspaceActivityEvent & {
  actor?: {
    type: 'user' | 'agent' | 'system';
    name: string;
    uid?: string;
    email?: string;
    run_id?: string;
  };
  issue?: GrowthWorkspaceActivityEvent['issue'] & {
    problem_statement?: string;
    recommended_action?: string;
    outcome?: GrowthIssueOutcome;
    latest_verification?: GrowthIssueRecord['latest_verification'];
    owner?: GrowthIssueRecord['owner'];
    evidence?: Pick<
      GrowthIssueRecord['evidence'],
      | 'summary'
      | 'marketer_role'
      | 'asset_type'
      | 'asset_id'
      | 'confidence'
      | 'lane_tags'
      | 'affected_urls'
      | 'affected_domains'
      | 'evidence_keys'
      | 'prompt_ids'
    >;
  };
  receipt_preview?: {
    summary: string;
    measurement_required?: boolean;
    evidence?: string;
    changed_urls: string[];
    changed_files: string[];
    commit_urls: string[];
    pr_urls: string[];
    changed_url_count: number;
    changed_file_count: number;
    commit_url_count: number;
    pr_url_count: number;
  };
};

export interface GrowthIssueDetail {
  issue: GrowthIssueRecord;
  attempts: GrowthWorkAttemptRecord[];
  events: GrowthLedgerEventRecord[];
}

export interface ScanAutomationTemplate {
  id: string;
  name: string;
  description: string;
  kind: string;
  source: string;
  prompt_template_id: string;
  default_trigger: {
    type: TriggerType;
    delay_minutes?: number;
    day_of_week?: number;
    day_of_month?: number;
    days_of_month?: [number, number];
  };
  data_sources: ScanDataSource[];
  customer_value?: string;
  best_for?: string[];
  output_behavior?: 'report' | 'growth_candidates' | 'verify' | 'execute';
  required_skills?: Array<{ skillId: string; versionId?: string; displayName?: string }>;
  optional_skills?: Array<{ skillId: string; versionId?: string; displayName?: string }>;
  legacy?: boolean;
}

export interface ScanAutomationRecord {
  slack_delivery?: TaskSlackDeliveryInput;
  id: string;
  name: string;
  description: string;
  kind: string;
  source: string;
  template_id: string;
  prompt_template_id: string;
  prompt_version: number;
  prompt_snapshot?: ScanPromptTemplate;
  model_id?: string;
  status: AutomationStatus;
  trigger: {
    type: TriggerType;
    delay_minutes?: number;
    day_of_week?: number;
    day_of_month?: number;
    days_of_month?: [number, number];
  };
  data_sources: ScanDataSource[];
  program_context?: {
    program_id: string;
    program_revision: number;
    specialist_id?: string;
  };
  setup_pending?: boolean;
  approval?: {
    created_via: 'ui' | 'mcp' | 'admin';
    approved_by: string;
    approved_at: string | null;
  };
  last_run_id?: string;
  last_run_at: string | null;
  updated_at: string | null;
}

export interface ScanRunRecord {
  slack_publication?: { state: 'pending' | 'delivered' | 'suppressed' | 'failed'; error?: string };
  id: string;
  scan_automation_id?: string;
  status: ScanRunStatus;
  trigger_reason: string;
  source_job_id?: string;
  model_id?: string;
  session_id?: string;
  thread_url?: string;
  created_growth_issue_ids: string[];
  updated_growth_issue_ids: string[];
  skill_usage?: Array<{
    skillId: string;
    versionId: string;
    version?: number;
    contentSha256?: string;
  }>;
  created_at: string | null;
  completed_at: string | null;
}

interface WebsiteTrendRow {
  date?: string;
  ga4_sessions?: number | string | null;
  ga4_conversions?: number | string | null;
  gsc_impressions?: number | string | null;
  bing_impressions?: number | string | null;
}

export interface WebsitePerformancePayload {
  trend?: WebsiteTrendRow[];
  providers?: {
    ga4?: {
      totals?: Record<string, unknown>;
      pages?: Record<string, unknown>[];
      chatbotReferrals?: { rows?: Record<string, unknown>[] };
      chatbotLandingPages?: { rows?: Record<string, unknown>[] };
    };
    gsc?: { totals?: Record<string, unknown>; queries?: Record<string, unknown>[] };
    bing?: { totals?: Record<string, unknown>; queries?: Record<string, unknown>[] };
    clarity?: { totals?: Record<string, unknown>; uxSignals?: Record<string, unknown>[] };
    cloudflare?: { totals?: Record<string, unknown> };
  };
  sourceCoverage?: Record<string, unknown>[];
}

interface WebsiteOpportunityRow {
  id?: string;
  title?: string;
  severity?: string;
  impact?: number | string | null;
  affected?: string;
  evidence?: string[];
  sources?: string[];
  suggestedAction?: string;
}

interface WebsiteOpportunitiesPayload {
  rows?: WebsiteOpportunityRow[];
}

interface CloudflareCrawlerTrendRow {
  date?: string;
  requests?: number | string | null;
  crawler_count?: number | string | null;
}

interface CloudflareCrawlerBreakdownRow {
  crawler_operator?: string | null;
  crawler_name?: string | null;
  crawler_category?: string | null;
  requests?: number | string | null;
  path_count?: number | string | null;
}

interface CloudflareCrawlerPayload {
  totals?: Record<string, unknown>;
  crawlerBreakdowns?: {
    trend?: CloudflareCrawlerTrendRow[];
    crawlers?: CloudflareCrawlerBreakdownRow[];
  };
}

interface CitationSourceShareRow {
  scope_key?: string | null;
  scope_type?: string | null;
  date?: string | null;
  domain?: string | null;
  citation_count?: number | string | null;
  total_source_placements?: number | string | null;
  top3_share_pct?: number | string | null;
}

type TrafficMetricKey = 'sessions' | 'impressions';
type CloudflareCrawlerMetricKey = 'requests' | 'activeCrawlers';
type BrandHomeTrendMetricKey = 'visibilityPct' | 'rank3Pct' | 'rank1Pct' | 'top3SovPct';
type WorkspaceHomeChartMetricKey = 'visibilityPct' | 'shareOfVoicePct';
type TrafficChartPoint = { date: string } & Record<TrafficMetricKey, number>;
type CloudflareCrawlerChartPoint = { date: string } & Record<CloudflareCrawlerMetricKey, number>;
type WorkspaceHomeChartPoint = { date: string } & Record<WorkspaceHomeChartMetricKey, number>;

interface VisibilityEngineBreakdownRow {
  engine?: string;
  total_answers?: number | string | null;
  mentioned_count?: number | string | null;
  top3_count?: number | string | null;
  visibility_pct?: number | string | null;
}

export interface VisibilitySummaryPayload {
  kpis?: Record<string, unknown>;
  slices?: {
    engineBreakdown?: VisibilityEngineBreakdownRow[];
  };
}

interface WorkspaceHomeBrandState {
  defaultBrandName: string;
  brandHref: string;
  promptGroupFilter: ReactNode;
  loading: boolean;
  error: string | null;
  hasConfiguredBrand: boolean;
  hasData: boolean;
  summary: ReturnType<typeof buildBrandHomeWidgetSummary>;
  trendPoints: BrandHomeTrendPoint[];
}

interface WorkspaceTrendMetric<Key extends string> {
  key: Key;
  label: string;
  color: string;
  fill: string;
}

type TrafficMetric = WorkspaceTrendMetric<TrafficMetricKey>;
type CloudflareCrawlerMetric = WorkspaceTrendMetric<CloudflareCrawlerMetricKey>;

const EMPTY_WORKSPACE_SHOPIFY_REVENUE: WorkspaceShopifyRevenueState = {
  statusLoading: false,
  available: false,
  loading: false,
  error: null,
  totalSales: 0,
  orders: 0,
  averageOrderValue: 0,
  changePct: null,
  currencyCode: null,
  trend: [],
};

interface BrandHomeTrendMetric {
  key: BrandHomeTrendMetricKey;
  label: string;
  color: string;
  fill: string;
  formatter: (value: number) => string;
  domain: [number, number];
  reversed?: boolean;
}

const TRAFFIC_METRICS: TrafficMetric[] = [
  {
    key: 'sessions',
    label: 'Sessions',
    color: 'var(--chakra-colors-status-success-fg)',
    fill: 'workspaceSessionsFill',
  },
  {
    key: 'impressions',
    label: 'Search impressions',
    color: 'var(--chakra-colors-status-info-fg)',
    fill: 'workspaceImpressionsFill',
  },
];

const CLOUDFLARE_CRAWLER_METRICS: CloudflareCrawlerMetric[] = [
  {
    key: 'requests',
    label: 'Crawler requests',
    color: 'var(--chakra-colors-status-warning-fg)',
    fill: 'workspaceCrawlerRequestsFill',
  },
  {
    key: 'activeCrawlers',
    label: 'Active crawlers',
    color: 'var(--chakra-colors-status-info-fg)',
    fill: 'workspaceActiveCrawlersFill',
  },
];

const BRAND_VISIBILITY_COLOR = 'var(--chakra-colors-data-visibility)';
const BRAND_RANK3_COLOR = 'var(--chakra-colors-data-rank3)';
const BRAND_RANK1_COLOR = 'var(--chakra-colors-data-rank1)';
const BRAND_SOV_COLOR = 'var(--chakra-colors-data-purple)';
const WORKSPACE_RISK_COLOR = 'var(--chakra-colors-status-danger-fg)';
const WORKSPACE_ENGINE_COLORS = [
  'var(--chakra-colors-data-visibility)',
  'var(--chakra-colors-status-info-fg)',
  'var(--chakra-colors-status-warning-fg)',
  'var(--chakra-colors-data-purple)',
  'var(--chakra-colors-ui-text-tertiary)',
];
const WORKSPACE_HOME_CHART_COLORS = [
  'var(--chakra-colors-data-visibility)',
  'var(--chakra-colors-status-info-fg)',
  'var(--chakra-colors-status-warning-fg)',
  'var(--chakra-colors-data-purple)',
  'var(--chakra-colors-status-success-fg)',
  'var(--chakra-colors-ui-text-tertiary)',
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface PreparedAutomation {
  normalizedDraft: {
    slackDelivery?: TaskSlackDeliveryInput;
    mode?: ComposerMode;
    templateId: string;
    name: string;
    description: string;
    trigger: {
      type: TriggerType;
      delay_minutes?: number;
      day_of_week?: number;
      day_of_month?: number;
      days_of_month?: [number, number];
    };
    dataSources: ScanDataSource[];
    modelId: string;
    createdVia: 'ui' | 'mcp' | 'admin';
  };
  prompt: ScanPromptTemplate;
  preflightToken: string;
  preflightTokenExpiresAt: string;
}

interface PreparedNorthStar {
  normalizedDraft: {
    title: string;
    bodyMarkdown: string;
  };
  preflightToken: string;
  preflightTokenExpiresAt: string;
}

export interface NorthStarDocumentRecord {
  id: string;
  title: string;
  body_markdown: string;
  version: number;
  status: 'active' | 'draft' | 'archived';
  updated_at: string | null;
  activated_at: string | null;
}

const ACTIVE_GROWTH_WORK_STATUS_LIST: GrowthIssueStatus[] = [
  'open',
  'regression_candidate',
  'claimed',
  'in_progress',
  'submitted',
];
const MEASURING_GROWTH_WORK_STATUS_LIST: GrowthIssueStatus[] = ['measuring'];
const COMPLETED_GROWTH_WORK_STATUS_LIST: GrowthIssueStatus[] = ['verified', 'closed'];
const ACTIVE_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>(ACTIVE_GROWTH_WORK_STATUS_LIST);
const MEASURING_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>(
  MEASURING_GROWTH_WORK_STATUS_LIST
);
const COMPLETED_GROWTH_WORK_STATUSES = new Set<GrowthIssueStatus>(
  COMPLETED_GROWTH_WORK_STATUS_LIST
);

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NEW_TASK_MONTH_DAYS = Array.from({ length: 28 }, (_, index) => index + 1);
const LEGACY_MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

const DATA_SOURCE_LABELS: Record<ScanDataSource, string> = {
  sampler: 'Answer samples',
  ai_search: 'AI Search',
  gsc: 'Search Console',
  ga4: 'GA4',
  bing: 'Bing',
  clarity: 'Clarity',
  cloudflare: 'Cloudflare',
  ads: 'Ads',
  webflow: 'Webflow',
  shopify: 'Shopify',
  wordpress: 'WordPress.com',
  github: 'Website via GitHub',
  custom_mcp: 'Custom MCP',
};

const NEW_TASK_SCHEDULE_OPTIONS: {
  value: TriggerType;
  label: string;
  description: string;
}[] = [
  {
    value: 'daily_scan',
    label: 'Daily',
    description: 'Run once each day using the latest completed project data.',
  },
  {
    value: 'weekly_scan',
    label: 'Weekly',
    description: 'Choose one weekday for the recurring task.',
  },
  {
    value: 'twice_monthly_scan',
    label: 'Twice Monthly',
    description: 'Choose 2 distinct days between 1 and 28.',
  },
  {
    value: 'monthly_scan',
    label: 'Monthly',
    description: 'Choose one day between 1 and 28.',
  },
];

const LEGACY_SCHEDULE_OPTIONS: {
  value: TriggerType;
  label: string;
  description: string;
}[] = [
  {
    value: 'manual',
    label: 'Manual',
    description: 'Legacy on-demand task.',
  },
  {
    value: 'sampler_completed_delay',
    label: 'After Data Refresh',
    description: 'Legacy task that runs after a completed data refresh.',
  },
];

const AGENT_SUMMARIES: Record<string, string> = {
  growth_orchestrator:
    'Monitors connected evidence, suppresses duplicates, and routes ready work orders to the right specialist.',
  citation_authority:
    'Monitors cited pages and publisher gaps that can change how AI answers reference the brand.',
  owned_pages_seo:
    'Monitors search demand, owned pages, metadata, and internal links for SEO work orders.',
  ai_narrative:
    'Monitors answer wording, prompt clusters, visibility, and sentiment patterns that need positioning work.',
  analytics_cro:
    'Monitors analytics, page context, and UX signals to identify conversion and experience fixes.',
  growth_outcome_verification:
    'Monitors submitted work against fresh evidence and records helped, no-movement, regressed, or unknown outcomes.',
  recency_refresh:
    'Finds cited or high-impression posts that are outdated and turns them into refresh work orders.',
  work_order_execution:
    'Claims work orders and prepares changes through connected WordPress.com, Webflow, Shopify, or custom MCP tools.',
  post_sampler_gap:
    'Checks fresh answer samples for specialist-ready Activity Feed work and duplicate suppression.',
  analytics_manual:
    'Uses analytics and search evidence to create concrete CRO and owned-page work.',
  custom_prompt:
    'Runs the approved custom instructions and creates Activity Feed work only when evidence is specific.',
};

const AGENT_TASK_NAMES: Record<string, string> = {
  growth_orchestrator: 'Coordinate growth work',
  growth_outcome_verification: 'Verify growth outcomes',
  citation_authority: 'Find citation opportunities',
  owned_pages_seo: 'Review owned-page SEO',
  ai_narrative: 'Monitor brand narrative',
  analytics_cro: 'Find conversion opportunities',
  recency_refresh: 'Refresh outdated content',
  work_order_execution: 'Execute approved work',
  post_sampler_gap: 'Review new answer samples',
  analytics_manual: 'Review analytics opportunities',
};

const LEGACY_AGENT_NAMES = new Set([
  'Marketing Operations Lead',
  'Growth Outcome Verifier',
  'Growth Outcome Verification Scan',
  'Citation Strategist',
  'Citation Authority Scan',
  'Owned SEO Strategist',
  'Owned Pages SEO Scan',
  'AI Narrative Strategist',
  'AI Narrative Scan',
  'Analytics CRO Strategist',
  'Analytics CRO Scan',
  'Recency Agent',
  'Recency Refresh Scan',
  'Work Order Executor',
]);

const LEGACY_AUTOMATION_TEMPLATE_IDS = new Set([
  'post_sampler_gap_scan_v1',
  'analytics_opportunity_scan_v1',
]);

const LEGACY_AUTOMATION_KINDS = new Set(['post_sampler_gap', 'analytics_manual']);

const DEFAULT_ALLOWED_TOOLS =
  'run_attune_data_tool, submit_background_findings, search_citations, get_brand_perception';

const DEFAULT_CUSTOM_PROMPT = `Run this approved custom Attune Background Task.

Context:
- projectId: {{project_id}}
- brandName: {{brand_name}}
- scanRunId: {{scan_run_id}}
- dateWindow: {{date_window}}
- connectedDataSources: {{connected_data_sources}}
- northStarVersion: {{north_star_version}}
- northStarDocument:
{{north_star_markdown}}

Goal:
Use the selected Skills and authorized evidence to answer the approved task objective.

Operating contract:
1. Use summary tools only to select bounded detail reads. Keep exact evidence IDs.
2. Keep observed evidence, interpretation, and recommendation separate.
3. Read growth_inbox.list before proposing duplicate work.
4. Do not call record_growth_issue. Submit one bounded result through submit_background_findings.
5. Return monitor signals or hypotheses when exact work is not ready.
6. A task candidate must name one exact asset, atomic action, acceptance criteria, confidence reason, five score inputs, duplicate check, and next-scan measurement.
7. Native Attune evidence cannot prove purchases, pipeline, revenue, causality, or improvement.

Output:
1. Scope Reviewed
2. Evidence References
3. Opportunity, Evidence, Confidence, Next Work, and Measurement
4. Work Not Created and Why
5. Limitations`;

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (
    status === 'enabled' ||
    status === 'completed' ||
    status === 'verified' ||
    status === 'closed'
  ) {
    return 'success';
  }
  if (
    status === 'paused' ||
    status === 'submitted' ||
    status === 'measuring' ||
    status === 'queued' ||
    status === 'running'
  ) {
    return 'warning';
  }
  if (status === 'failed' || status === 'regressed' || status === 'dismissed') {
    return 'danger';
  }
  if (status === 'open' || status === 'claimed' || status === 'in_progress') {
    return 'info';
  }
  return 'neutral';
}

function severityTone(severity: GrowthIssueRecord['severity']): 'success' | 'warning' | 'danger' {
  if (severity === 'high') return 'danger';
  if (severity === 'medium') return 'warning';
  return 'success';
}

function numeric(value: unknown): number {
  const next = Number(value || 0);
  return Number.isFinite(next) ? next : 0;
}

function formatCompactNumber(value: unknown): string {
  const next = numeric(value);
  return new Intl.NumberFormat(undefined, {
    notation: next >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: next >= 1000 ? 1 : 0,
  }).format(next);
}

function formatCurrency(value: unknown): string {
  const next = numeric(value);
  if (next <= 0) return '$0';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    notation: next >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: 0,
  }).format(next);
}

function optionalNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function formatShopifyRevenue(value: number, currencyCode: string | null): string {
  const currency =
    currencyCode && /^[A-Z]{3}$/.test(currencyCode.toUpperCase())
      ? currencyCode.toUpperCase()
      : 'USD';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    notation: Math.abs(value) >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) >= 10_000 ? 1 : 0,
  }).format(value);
}

function formatShopifyRevenueChange(value: number | null): string {
  if (value === null) return '30-day total';
  if (Math.abs(value) < 0.05) return 'Flat vs prior';
  return `${value > 0 ? '+' : ''}${value.toFixed(Math.abs(value) >= 10 ? 0 : 1)}% vs prior`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(value >= 10 ? 0 : 1)}%`;
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function formatChartDate(value: unknown): string {
  const raw = String(value || '');
  if (!raw) return '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const date = new Date(isDateOnly ? `${raw}T00:00:00Z` : raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(isDateOnly ? { timeZone: 'UTC' } : {}),
  });
}

function getLast30DayRange() {
  const now = new Date();
  const endUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const startUtc = endUtc - 29 * MS_PER_DAY;
  return {
    startDate: toUtcDateKey(new Date(startUtc)),
    endDate: toUtcDateKey(new Date(endUtc)),
  };
}

function toUtcDateKey(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function normalizeEngineLabel(value: unknown): string {
  const engine = String(value || '').trim();
  if (!engine) return 'Unknown';
  if (engine.toLowerCase() === 'chatgpt') return 'ChatGPT';
  if (engine.toLowerCase() === 'gemini') return 'Gemini';
  if (engine.toLowerCase() === 'google_aio') return 'Google AIO';
  if (SEARCHAPI_ENGINE_LABELS[engine.toLowerCase()])
    return SEARCHAPI_ENGINE_LABELS[engine.toLowerCase()];
  if (engine.toLowerCase() === 'perplexity') return 'Perplexity';
  if (engine.toLowerCase() === 'claude') return 'Claude';
  return engine.charAt(0).toUpperCase() + engine.slice(1);
}

function opportunitySeverity(value: unknown): 'high' | 'medium' | 'low' {
  const severity = String(value || '').toLowerCase();
  if (severity === 'high') return 'high';
  if (severity === 'low') return 'low';
  return 'medium';
}

function opportunityLeakType(title: unknown): string {
  const normalized = String(title || '').toLowerCase();
  if (normalized.includes('ctr')) return 'Snippet mismatch';
  if (normalized.includes('low ai citation') || normalized.includes('citations')) {
    return 'Missing in answer';
  }
  if (
    normalized.includes('rage') ||
    normalized.includes('dead click') ||
    normalized.includes('ux')
  ) {
    return 'Trust friction';
  }
  if (normalized.includes('query demand')) return 'Demand gap';
  if (normalized.includes('blog')) return 'Path leakage';
  return 'Opportunity';
}

function opportunitySortValue(row: WebsiteOpportunityRow): number {
  const severityWeight =
    opportunitySeverity(row.severity) === 'high'
      ? 100000
      : opportunitySeverity(row.severity) === 'medium'
        ? 10000
        : 1000;
  return severityWeight + numeric(row.impact);
}

function useWorkspaceShopifyRevenue(user: User | null, projectId: string | null) {
  const [state, setState] = useState<WorkspaceShopifyRevenueState>({
    ...EMPTY_WORKSPACE_SHOPIFY_REVENUE,
    statusLoading: true,
  });

  useEffect(() => {
    if (!user || !projectId) {
      setState(EMPTY_WORKSPACE_SHOPIFY_REVENUE);
      return;
    }

    setState({ ...EMPTY_WORKSPACE_SHOPIFY_REVENUE, statusLoading: true });
    const currentUser = user;
    const currentProjectId = projectId;
    const controller = new AbortController();
    async function load() {
      try {
        const token = await currentUser.getIdToken();
        const statusResponse = await fetch(
          `/api/integrations/shopify/status?projectId=${encodeURIComponent(currentProjectId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30_000)]),
          }
        );
        const statusPayload = (await statusResponse
          .json()
          .catch(() => ({}))) as ShopifyStatusSummary & { error?: string };
        if (!statusResponse.ok) {
          throw new Error(
            'error' in statusPayload && statusPayload.error
              ? statusPayload.error
              : 'Shopify status unavailable'
          );
        }

        const usableConnectionIds = new Set(statusPayload.usableConnectionIds || []);
        const connection = statusPayload.connections?.find(item => {
          const status = (item.status || '').toLowerCase();
          return Boolean(
            item.id &&
              usableConnectionIds.has(item.id) &&
              (status === 'active' || status === 'warning') &&
              item.shop_domain &&
              (item.capabilities?.shopifyAnalytics || item.capabilities?.readReports)
          );
        });
        if (!connection?.shop_domain) {
          if (!controller.signal.aborted) setState(EMPTY_WORKSPACE_SHOPIFY_REVENUE);
          return;
        }

        if (!controller.signal.aborted) {
          setState({
            ...EMPTY_WORKSPACE_SHOPIFY_REVENUE,
            available: true,
            loading: true,
            currencyCode: connection.shop?.currencyCode || null,
          });
        }

        const revenueResponse = await fetch('/api/attune/shopify/analytics-query', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30_000)]),
          body: JSON.stringify({
            projectId: currentProjectId,
            shopDomain: connection.shop_domain,
            query:
              'FROM sales SHOW total_sales, orders, average_order_value TIMESERIES day WITH TOTALS SINCE -30d UNTIL today COMPARE TO previous_period ORDER BY day ASC',
          }),
        });
        const revenuePayload = (await revenueResponse
          .json()
          .catch(() => ({}))) as ShopifyRevenueSummaryPayload;
        if (!revenueResponse.ok) {
          throw new Error(
            revenuePayload.error || revenuePayload.message || 'Shopify revenue unavailable'
          );
        }
        const rows = Array.isArray(revenuePayload.tableData?.rows)
          ? revenuePayload.tableData.rows
          : [];
        const row =
          rows[0] && typeof rows[0] === 'object' ? (rows[0] as Record<string, unknown>) : {};
        const trend = rows
          .filter((item): item is Record<string, unknown> =>
            Boolean(item && typeof item === 'object' && !Array.isArray(item))
          )
          .map(item => ({
            date: formatChartDate(item.day),
            totalSales: numeric(item.total_sales),
          }))
          .filter(item => Boolean(item.date));
        const totalSales =
          optionalNumeric(row.total_sales__totals) ??
          trend.reduce((sum, item) => sum + item.totalSales, 0);
        const previousTotalSales = optionalNumeric(
          row.comparison_total_sales__previous_period__totals
        );
        if (!controller.signal.aborted) {
          setState({
            statusLoading: false,
            available: true,
            loading: false,
            error: null,
            totalSales,
            orders: numeric(row.orders__totals ?? row.orders),
            averageOrderValue: numeric(row.average_order_value__totals ?? row.average_order_value),
            changePct:
              previousTotalSales && previousTotalSales !== 0
                ? ((totalSales - previousTotalSales) / Math.abs(previousTotalSales)) * 100
                : null,
            currencyCode: revenuePayload.currencyCode || connection.shop?.currencyCode || null,
            trend,
          });
        }
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setState(current =>
          current.available
            ? {
                ...current,
                loading: false,
                error:
                  loadError instanceof Error ? loadError.message : 'Shopify revenue unavailable',
              }
            : EMPTY_WORKSPACE_SHOPIFY_REVENUE
        );
      }
    }

    void load();
    return () => controller.abort();
  }, [projectId, user]);

  return state;
}

const homeTrafficCache = createAsyncResourceCache<WebsitePerformancePayload>(60_000);

function useWorkspaceWebsitePerformance(user: User | null, projectId: string | null) {
  const { startDate, endDate } = getLast30DayRange();
  const key = user && projectId ? JSON.stringify([user.uid, projectId, startDate, endDate]) : null;
  const cached = key ? homeTrafficCache.get(key) : undefined;
  const [state, setState] = useState<{
    key: string | null;
    payload: WebsitePerformancePayload | null;
    error: string | null;
  }>({ key, payload: cached || null, error: null });

  useEffect(() => {
    if (!user || !projectId || !key) return;
    let cancelled = false;
    setState({ key, payload: homeTrafficCache.get(key) || null, error: null });
    // A shared request survives widget unmounts; each subscriber ignores obsolete results.
    void homeTrafficCache
      .load(key, async () => {
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/website/traffic-trend', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(30_000),
          body: JSON.stringify({ projectId, startDate, endDate }),
        });
        const nextPayload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(nextPayload.error || 'Website analytics unavailable');
        return nextPayload as WebsitePerformancePayload;
      })
      .then(payload => {
        if (!cancelled) setState({ key, payload, error: null });
      })
      .catch(error => {
        if (!cancelled)
          setState({
            key,
            payload: null,
            error: error instanceof Error ? error.message : 'Website analytics unavailable',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [endDate, key, projectId, startDate, user]);

  const payload = cached || (state.key === key ? state.payload : null);
  const error = state.key === key ? state.error : null;
  return { payload, error, loading: Boolean(key && !payload && !error) };
}

function useWorkspaceOpportunities(user: User | null, projectId: string | null) {
  const [payload, setPayload] = useState<WebsiteOpportunitiesPayload | null>(null);
  const [loading, setLoading] = useState(() => Boolean(user && projectId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setPayload(null);
      setLoading(false);
      setError(null);
      return;
    }

    const currentUser = user;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const range = getLast30DayRange();
        const response = await fetch('/api/attune/website/opportunities', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            projectId,
            startDate: range.startDate,
            endDate: range.endDate,
            limit: 8,
          }),
        });
        const nextPayload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(nextPayload.error || 'Website opportunities unavailable');
        if (!controller.signal.aborted) setPayload(nextPayload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setPayload(null);
        setError(
          loadError instanceof Error ? loadError.message : 'Website opportunities unavailable'
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [projectId, user]);

  return { payload, loading, error };
}

function useWorkspaceCrawlerActivity(user: User | null, projectId: string | null) {
  const [payload, setPayload] = useState<CloudflareCrawlerPayload | null>(null);
  const [loading, setLoading] = useState(() => Boolean(user && projectId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setPayload(null);
      setLoading(false);
      setError(null);
      return;
    }

    const currentUser = user;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const range = getLast30DayRange();
        const response = await fetch('/api/attune/analytics/ai-crawlers', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            projectId,
            startDate: range.startDate,
            endDate: range.endDate,
            provider: 'cloudflare',
            includeBreakdowns: true,
            limit: 30,
          }),
        });
        const nextPayload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw createAttuneClientRequestError(
            response,
            nextPayload,
            'Cloudflare crawler data unavailable'
          );
        }
        if (!controller.signal.aborted) setPayload(nextPayload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setPayload(null);
        if (isOptionalAttunePreloadError(loadError)) {
          setError(null);
          return;
        }
        setError(
          loadError instanceof Error ? loadError.message : 'Cloudflare crawler data unavailable'
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [projectId, user]);

  return { payload, loading, error };
}

function useWorkspaceCitationShare(user: User | null, projectId: string | null) {
  const [rows, setRows] = useState<CitationSourceShareRow[]>([]);
  const [loading, setLoading] = useState(() => Boolean(user && projectId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    const currentUser = user;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/pipeline-ui', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            query_type: 'citation_source_sov_time_series',
            params: {
              project_id: projectId,
              limit: 6,
              include_prompt_breakdown: false,
            },
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Citation share unavailable');
        if (!controller.signal.aborted) setRows(Array.isArray(payload.data) ? payload.data : []);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setRows([]);
        setError(loadError instanceof Error ? loadError.message : 'Citation share unavailable');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [projectId, user]);

  return { rows, loading, error };
}

function useWorkspaceVisibilitySummary(user: User | null, projectId: string | null) {
  const [payload, setPayload] = useState<VisibilitySummaryPayload | null>(null);
  const [loading, setLoading] = useState(() => Boolean(user && projectId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setPayload(null);
      setLoading(false);
      setError(null);
      return;
    }

    const currentUser = user;
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/attune/visibility/summary', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({ projectId, limit: 90 }),
        });
        const nextPayload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(nextPayload.error || 'Visibility summary unavailable');
        if (!controller.signal.aborted) setPayload(nextPayload);
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setPayload(null);
        setError(loadError instanceof Error ? loadError.message : 'Visibility summary unavailable');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [projectId, user]);

  return { payload, loading, error };
}

function formatTrigger(
  trigger: ScanAutomationRecord['trigger'] | ScanAutomationTemplate['default_trigger']
) {
  if (trigger.type === 'manual') return 'Manual';
  if (trigger.type === 'sampler_completed_delay') {
    return `After refresh +${trigger.delay_minutes || 60}m`;
  }
  if (trigger.type === 'daily_scan') return 'Daily';
  if (trigger.type === 'weekly_scan') {
    return `Weekly ${WEEKDAYS[trigger.day_of_week ?? 1] || 'Mon'}`;
  }
  if (trigger.type === 'twice_monthly_scan') {
    const [first, second] = normalizeTwiceMonthlyDays(trigger.days_of_month);
    return `Twice monthly · days ${first} & ${second}`;
  }
  if (trigger.type === 'monthly_scan') {
    return `Monthly day ${trigger.day_of_month || 1}`;
  }
  return titleLabel(trigger.type);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Never';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

function clampWeekday(value: unknown): number {
  return typeof value === 'number' && value >= 0 && value <= 6 ? Math.floor(value) : 1;
}

function clampMonthDay(value: unknown, maximum = 28): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return Math.min(maximum, Math.max(1, Math.floor(value)));
}

function normalizeTwiceMonthlyDays(value: unknown): [number, number] {
  const raw = Array.isArray(value) ? value : [];
  const first = clampMonthDay(raw[0], 28);
  const proposedSecond = clampMonthDay(raw[1], 28);
  const second = proposedSecond === first ? (first === 15 ? 16 : 15) : proposedSecond;
  return [Math.min(first, second), Math.max(first, second)];
}

function nextDateForWeekday(dayOfWeek: number, from = new Date()): Date {
  const next = new Date(from);
  next.setHours(0, 0, 0, 0);
  const rawOffset = (dayOfWeek - next.getDay() + 7) % 7;
  const offset = rawOffset === 0 ? 7 : rawOffset;
  next.setDate(next.getDate() + offset);
  return next;
}

function nextDateForMonthDay(dayOfMonth: number, from = new Date()): Date {
  const next = new Date(from.getFullYear(), from.getMonth(), 1);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(dayOfMonth, lastDay));
  if (next < new Date(from.getFullYear(), from.getMonth(), from.getDate())) {
    next.setMonth(next.getMonth() + 1, 1);
    const nextLastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(dayOfMonth, nextLastDay));
  }
  return next;
}

function nextDateForDaily(from = new Date()): Date {
  const next = new Date(from);
  next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function nextDateForTwiceMonthly(daysOfMonth: [number, number], from = new Date()): Date {
  const candidates = daysOfMonth
    .map(day => nextDateForMonthDay(day, from))
    .sort((left, right) => left.getTime() - right.getTime());
  return candidates[0] || nextDateForMonthDay(daysOfMonth[0], from);
}

function formatCalendarDay(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function formatTriggerDetail(trigger: {
  type: string;
  delay_minutes?: number;
  day_of_week?: number;
  day_of_month?: number;
  days_of_month?: [number, number];
}): string {
  if (trigger.type === 'sampler_completed_delay') {
    return `Waits ${trigger.delay_minutes || 60} minutes after each completed data refresh.`;
  }
  if (trigger.type === 'daily_scan') {
    return 'Runs once each day using the latest completed project data.';
  }
  if (trigger.type === 'weekly_scan') {
    return `Runs weekly on ${WEEKDAYS[clampWeekday(trigger.day_of_week)]}.`;
  }
  if (trigger.type === 'twice_monthly_scan') {
    const [first, second] = normalizeTwiceMonthlyDays(trigger.days_of_month);
    return `Runs twice monthly on days ${first} and ${second}.`;
  }
  if (trigger.type === 'monthly_scan') {
    return `Runs monthly on day ${clampMonthDay(trigger.day_of_month, 31)}.`;
  }
  return 'Runs only when started from this workspace or the background task tools.';
}

function formatNextAgentRun(
  trigger: ScanAutomationRecord['trigger'],
  status: AutomationStatus
): string {
  if (status !== 'enabled') return status === 'paused' ? 'Paused' : 'Not scheduled';
  if (trigger.type === 'manual') return 'On demand';
  if (trigger.type === 'sampler_completed_delay') return 'After the next data refresh';
  if (trigger.type === 'daily_scan') {
    return `Next ${formatCalendarDay(nextDateForDaily())}`;
  }
  if (trigger.type === 'weekly_scan') {
    return `Next ${formatCalendarDay(nextDateForWeekday(clampWeekday(trigger.day_of_week)))}`;
  }
  if (trigger.type === 'twice_monthly_scan') {
    return `Next ${formatCalendarDay(
      nextDateForTwiceMonthly(normalizeTwiceMonthlyDays(trigger.days_of_month))
    )}`;
  }
  if (trigger.type === 'monthly_scan') {
    return `Next ${formatCalendarDay(
      nextDateForMonthDay(clampMonthDay(trigger.day_of_month, 31))
    )}`;
  }
  return 'Schedule pending';
}

function summarizeAgent(kind: string | undefined, fallback?: string): string {
  if (kind && AGENT_SUMMARIES[kind]) return AGENT_SUMMARIES[kind];
  return fallback || 'Creates or updates Activity Feed work from connected Attune evidence.';
}

function agentTaskName(kind: string | undefined, fallback?: string): string {
  return (kind && AGENT_TASK_NAMES[kind]) || fallback || 'Background Task';
}

function automationDisplayName(automation: ScanAutomationRecord): string {
  return LEGACY_AGENT_NAMES.has(automation.name)
    ? agentTaskName(automation.kind, automation.name)
    : automation.name;
}

function agentMarketplaceCategory(kind: string | undefined): AgentMarketplaceCategory {
  return kind === 'work_order_execution' ? 'execution' : 'monitoring';
}

function isExecutionAgent(kind: string | undefined): boolean {
  return agentMarketplaceCategory(kind) === 'execution';
}

function agentRoleLabel(
  template: Pick<ScanAutomationTemplate, 'name' | 'kind'> | null | undefined,
  prompt?: Pick<ScanPromptTemplate, 'marketerRole'> | null
): string {
  return agentTaskName(template?.kind, prompt?.marketerRole || template?.name || 'Background Task');
}

function researchModelLabel(models: PublicResearchModel[], modelId?: string | null): string {
  const normalizedModelId = String(modelId || '').trim();
  if (!normalizedModelId) return 'Default model';
  const model = models.find(item => item.id === normalizedModelId);
  return model?.label || normalizedModelId;
}

function researchModelsWithPersistedCurrent(
  models: PublicResearchModel[],
  modelId?: string | null
): PublicResearchModel[] {
  const normalizedModelId = String(modelId || '').trim();
  if (!normalizedModelId || models.some(model => model.id === normalizedModelId)) return models;
  const currentModel = getPersistedResearchModelOption(normalizedModelId);
  if (!currentModel) return models;
  const opusIndex = models.findIndex(model => model.id === 'anthropic/claude-opus-5');
  return opusIndex < 0
    ? [...models, currentModel]
    : [...models.slice(0, opusIndex), currentModel, ...models.slice(opusIndex)];
}

function onboardingResearchModels(models: PublicResearchModel[]): PublicResearchModel[] {
  return models.filter(model => model.id === ONBOARDING_RESEARCH_MODEL_ID);
}

function onboardingSelectedResearchModel(
  models: PublicResearchModel[],
  selectedModelId?: string | null
): PublicResearchModel | null {
  const onboardingModels = onboardingResearchModels(models);
  return (
    onboardingModels.find(model => model.id === selectedModelId && model.available) ||
    onboardingModels.find(model => model.available) ||
    onboardingModels[0] ||
    null
  );
}

function customMcpIsLive(status: CustomMcpStatusResponse | null): boolean {
  return Boolean(
    status?.connections?.some(connection => {
      const connectionStatus = (connection.status || '').toLowerCase();
      const syncStatus = (connection.lastSyncStatus || '').toLowerCase();
      const hasError =
        connectionStatus.includes('error') ||
        connectionStatus.includes('fail') ||
        syncStatus.includes('error') ||
        syncStatus.includes('fail') ||
        Boolean(connection.lastSyncError);
      return (
        connectionStatus === 'connected' &&
        !hasError &&
        connection.exposeToMcp !== false &&
        Number(connection.enabledToolCount || 0) > 0
      );
    })
  );
}

function githubIsLive(status: GitHubStatusSummary | null): boolean {
  return Boolean(
    status?.installation?.status === 'connected' &&
      status.binding?.status === 'active' &&
      isGitHubBindingEnabledClient(status.binding)
  );
}

function workspaceRefreshErrorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : '';
}

function isTransientWorkspaceRefreshError(error: unknown): boolean {
  const name = error instanceof Error ? error.name.toLowerCase() : '';
  const message = workspaceRefreshErrorText(error).toLowerCase();

  return (
    name === 'aborterror' ||
    message.includes('aborted') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('unable to resolve') ||
    message.includes('err_name') ||
    message.includes('enotfound') ||
    message.includes('econnreset') ||
    message.includes('timeout') ||
    (typeof navigator !== 'undefined' && navigator.onLine === false)
  );
}

function formatWorkspaceRefreshError(error: unknown, fallback: string): string {
  const message = workspaceRefreshErrorText(error).trim();
  if (isTransientWorkspaceRefreshError(error)) {
    return `${fallback}. Check your connection and try again.`;
  }
  return message || fallback;
}

function showWorkspaceRefreshError(error: unknown, fallback: string, mode: WorkspaceRefreshMode) {
  if (mode === 'passive') return;
  toast.error(formatWorkspaceRefreshError(error, fallback));
}

async function readWorkspaceJson<T>(url: string, token: string): Promise<T | null> {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return null;
  return response.json() as Promise<T>;
}

function isLegacyAutomationTemplate(template: ScanAutomationTemplate): boolean {
  return (
    template.legacy === true ||
    LEGACY_AUTOMATION_TEMPLATE_IDS.has(template.id) ||
    LEGACY_AUTOMATION_KINDS.has(template.kind) ||
    /backward-compatible/i.test(template.description)
  );
}

function growthTaskBucket(issue: GrowthIssueRecord): GrowthIssueListTab | null {
  if (MEASURING_GROWTH_WORK_STATUSES.has(issue.status)) return 'measuring';
  if (COMPLETED_GROWTH_WORK_STATUSES.has(issue.status)) return 'completed';
  if (ACTIVE_GROWTH_WORK_STATUSES.has(issue.status)) return 'tasks';
  return null;
}

function growthWorkTabForIssue(issue: GrowthIssueRecord): GrowthWorkTab {
  return growthTaskBucket(issue) || 'activity';
}

function statusesForGrowthWorkTab(tab: GrowthWorkTab): GrowthIssueStatus[] | null {
  if (tab === 'activity' || tab === 'approvals') return null;
  if (tab === 'measuring') return MEASURING_GROWTH_WORK_STATUS_LIST;
  if (tab === 'completed') return COMPLETED_GROWTH_WORK_STATUS_LIST;
  return ACTIVE_GROWTH_WORK_STATUS_LIST;
}

function growthStatusCount(summary: GrowthInboxSummaryRecord, statuses: GrowthIssueStatus[]) {
  return statuses.reduce((total, status) => total + (summary.counts_by_status[status] || 0), 0);
}

function isRenderableMessage(message: Message) {
  return Boolean(
    message.content.trim() ||
      message.thinkingContent?.trim() ||
      message.toolUsage?.length ||
      message.traceTimeline?.length ||
      message.runStatus ||
      message.runReceipt
  );
}

function feedActionLabel(type: string) {
  if (type === 'created') return 'Task created';
  if (type === 'updated') return 'Task updated';
  if (type === 'claimed') return 'Task claimed';
  if (type === 'owner_changed') return 'Owner changed';
  if (type === 'work_log') return 'Work logged';
  if (type === 'ship_receipt') return 'Work submitted';
  if (type === 'outcome_recorded') return 'Outcome recorded';
  if (type === 'status_changed') return 'Status changed';
  if (type === 'scan_run') return 'Scan run';
  return titleLabel(type);
}

function mergeIssueRows(current: GrowthIssueRecord[], next: GrowthIssueRecord[]) {
  const seen = new Set<string>();
  return [...current, ...next].filter(issue => {
    if (seen.has(issue.id)) return false;
    seen.add(issue.id);
    return true;
  });
}

const WORKSPACE_CMO_ROW_HEIGHT = '208px';

function WorkspaceComposer({
  value,
  setValue,
  models,
  selectedModel,
  selectedModelId,
  onModelChange,
  executionMode,
  executionModes,
  onExecutionModeChange,
  pendingImages,
  pendingDocuments,
  isProcessingAttachment,
  canAttachImages,
  canAttachDocuments,
  imageUnavailableMessage,
  disabled,
  loading,
  placeholder = 'Ask Attune anything…',
  onSubmit,
  onStop,
  onAttachFiles,
  onPaste,
  onRemoveImage,
  onRemoveDocument,
  availableSkills,
  selectedSkills,
  skillsLoading,
  skillsError,
  skillSelectionEnabled,
  onSelectedSkillsChange,
  runReplyStatus = null,
  backgroundTaskIntent = false,
  backgroundTaskIntentAvailable = false,
  onBackgroundTaskIntentChange,
  textareaRef,
}: {
  value: string;
  setValue: (value: string) => void;
  models: PublicResearchModel[];
  selectedModel: PublicResearchModel | null;
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
  executionMode: ResearchExecutionMode;
  executionModes: ResearchExecutionMode[];
  onExecutionModeChange: (mode: ResearchExecutionMode) => void;
  pendingImages: ImageAttachment[];
  pendingDocuments: DocumentAttachment[];
  isProcessingAttachment: boolean;
  canAttachImages: boolean;
  canAttachDocuments: boolean;
  imageUnavailableMessage: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  onSubmit: () => void;
  onStop: () => void;
  onAttachFiles: (files: FileList | null) => void;
  onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  onRemoveImage: (imageId: string) => void;
  onRemoveDocument: (document: DocumentAttachment) => void;
  availableSkills: SkillPickerItem[];
  selectedSkills: SkillPickerItem[];
  skillsLoading: boolean;
  skillsError: string | null;
  skillSelectionEnabled: boolean;
  onSelectedSkillsChange: (skills: SkillPickerItem[]) => void;
  runReplyStatus?: ResearchRunReplyStatus | null;
  backgroundTaskIntent?: boolean;
  backgroundTaskIntentAvailable?: boolean;
  onBackgroundTaskIntentChange?: (active: boolean) => void;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [skillPickerDismissed, setSkillPickerDismissed] = useState(false);
  const awaitingRunReply = Boolean(runReplyStatus);
  const awaitingPlanApproval = runReplyStatus?.phase === 'awaiting_approval';
  const canAttach =
    !disabled &&
    !awaitingRunReply &&
    !backgroundTaskIntent &&
    (canAttachImages || canAttachDocuments) &&
    pendingImages.length + pendingDocuments.length < MAX_DOCUMENT_ATTACHMENTS &&
    !isProcessingAttachment;
  const canSubmit =
    !isProcessingAttachment &&
    (awaitingRunReply
      ? value.trim().length > 0
      : value.trim().length > 0 || pendingImages.length + pendingDocuments.length > 0);
  const skillQuery = getSkillTriggerQuery(value);
  const skillPickerOpen =
    skillSelectionEnabled &&
    skillQuery !== null &&
    !skillPickerDismissed &&
    !disabled &&
    !awaitingRunReply &&
    !backgroundTaskIntent;
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' && skillPickerOpen) {
      event.preventDefault();
      setSkillPickerDismissed(true);
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  };
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    onAttachFiles(event.target.files);
    event.target.value = '';
  };

  return (
    <ComposerShell data-section="workspaceHomeComposer" data-composer-shell>
      {runReplyStatus ? (
        <RunModeComposerContext mode={runReplyStatus.mode} phase={runReplyStatus.phase} />
      ) : null}
      {backgroundTaskIntent && !runReplyStatus ? (
        <BackgroundTaskComposerContext onClose={() => onBackgroundTaskIntentChange?.(false)} />
      ) : null}
      {backgroundTaskIntent ? null : (
        <SkillsPicker
          skills={availableSkills}
          selectedSkills={selectedSkills}
          open={skillPickerOpen}
          query={skillQuery || ''}
          loading={skillsLoading}
          error={skillsError}
          placement="top-start"
          onSelect={skill => {
            onSelectedSkillsChange(
              selectedSkills.some(item => item.skillId === skill.skillId)
                ? selectedSkills
                : [...selectedSkills, skill]
            );
            setValue(removeSkillTrigger(value));
            setSkillPickerDismissed(true);
          }}
          onClose={() => setSkillPickerDismissed(true)}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={[
          canAttachImages ? 'image/png,image/jpeg,image/webp,image/gif' : '',
          canAttachDocuments ? modelDocumentAcceptAttribute(selectedModel) : '',
        ]
          .filter(Boolean)
          .join(',')}
        multiple
        hidden
        onChange={handleFileChange}
      />
      {pendingImages.length > 0 || pendingDocuments.length > 0 || isProcessingAttachment ? (
        <HStack px="3" pt="3" gap="2" flexWrap="wrap">
          {pendingDocuments.map(document => (
            <DocumentAttachmentChip
              key={document.id}
              attachment={document}
              onRemove={() => onRemoveDocument(document)}
            />
          ))}
          {pendingImages.map(image => (
            <Box key={image.id} position="relative">
              <Box
                borderRadius="8px"
                overflow="hidden"
                borderWidth="1px"
                borderColor="ui.border.default"
                bg="ui.surface.selected"
                maxW="100px"
              >
                <Image
                  src={getImageDataUrl(image)}
                  alt={image.name || 'Pending image'}
                  maxH="80px"
                  objectFit="contain"
                />
              </Box>
              <IconButton
                aria-label="Remove image"
                size="xs"
                position="absolute"
                top="-1"
                right="-1"
                rounded="full"
                bg="red.500"
                color="white"
                _hover={{ bg: 'red.600' }}
                onClick={() => onRemoveImage(image.id)}
              >
                <X size={12} />
              </IconButton>
            </Box>
          ))}
          {isProcessingAttachment ? (
            <Box
              borderRadius="8px"
              borderWidth="1px"
              borderColor="ui.border.default"
              borderStyle="dashed"
              bg="ui.surface.selected"
              w="80px"
              h="80px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <InlineSpinner />
            </Box>
          ) : null}
        </HStack>
      ) : null}
      {backgroundTaskIntent ? null : (
        <SelectedSkillChips
          skills={selectedSkills}
          disabled={disabled || awaitingRunReply}
          onRemove={skillId =>
            onSelectedSkillsChange(selectedSkills.filter(skill => skill.skillId !== skillId))
          }
        />
      )}
      <ComposerInput
        ref={textareaRef}
        value={value}
        onChange={event => {
          setValue(event.target.value);
          setSkillPickerDismissed(false);
        }}
        onKeyDown={handleKeyDown}
        onPaste={event => {
          if (
            awaitingRunReply &&
            Array.from(event.clipboardData.items).some(item => item.type.startsWith('image/'))
          ) {
            event.preventDefault();
            toast.error('Run replies are text-only.');
            return;
          }
          onPaste(event);
        }}
        placeholder={
          runReplyStatus?.phase === 'awaiting_user'
            ? 'Reply with your answers…'
            : awaitingPlanApproval
              ? 'Reply with changes, or type “approve” to run this plan…'
              : backgroundTaskIntent
                ? 'Describe what should run, how often, and what it should produce…'
                : placeholder
        }
        disabled={disabled}
      />
      <ComposerControls
        leading={
          <>
            {backgroundTaskIntent ? (
              <Box />
            ) : (
              <ComposerAddMenu
                skills={availableSkills}
                selectedSkills={selectedSkills}
                disabled={disabled || awaitingRunReply}
                attachDisabled={!canAttach}
                selectionEnabled={skillSelectionEnabled}
                onAttachFiles={() => fileInputRef.current?.click()}
                onScheduleAgent={
                  backgroundTaskIntentAvailable && !loading && !awaitingRunReply
                    ? () => onBackgroundTaskIntentChange?.(true)
                    : undefined
                }
                onSelectSkill={skill =>
                  onSelectedSkillsChange(
                    selectedSkills.some(item => item.skillId === skill.skillId)
                      ? selectedSkills
                      : [...selectedSkills, skill]
                  )
                }
              />
            )}
            <ExecutionModeSelector
              value={runReplyStatus?.mode || executionMode}
              modes={executionModes}
              disabled={disabled || loading || awaitingRunReply || backgroundTaskIntent}
              onChange={onExecutionModeChange}
            />
          </>
        }
        trailing={
          <>
            <ModelSelector
              models={models}
              selectedModel={selectedModel}
              selectedModelId={selectedModelId}
              disabled={loading}
              onChange={onModelChange}
            />
            {loading ? (
              <ComposerActionButton aria-label="Stop response" onClick={onStop}>
                <Square size={14} />
              </ComposerActionButton>
            ) : (
              <ComposerActionButton
                aria-label="Send message"
                disabled={disabled || !canSubmit}
                onClick={onSubmit}
              >
                <ArrowUp size={18} />
              </ComposerActionButton>
            )}
          </>
        }
      />
    </ComposerShell>
  );
}

function AiCmoWidgetTitle() {
  return (
    <HStack gap="2">
      <Text fontSize="sm" fontWeight="800" color="ui.text.primary">
        AI CMO
      </Text>
      <Text
        as="span"
        px="1.5"
        borderRadius="full"
        borderWidth="1px"
        borderColor="accent.muted"
        color="accent.fg"
        fontSize="8px"
        fontWeight="850"
        lineHeight="16px"
        textTransform="uppercase"
        letterSpacing="0.06em"
      >
        Beta
      </Text>
    </HStack>
  );
}

function AiCmoPanel({
  user,
  projectId,
  onEditProposal,
}: {
  user: User | null;
  projectId: string | null;
  onEditProposal: () => void;
}) {
  return (
    <Box
      data-section="workspaceAiCmo"
      layerStyle="surfacePanel"
      bg="ui.surface.panel"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="8px"
      overflow="hidden"
      minH={{ base: 'auto', xl: '220px' }}
      h={{ base: 'auto', xl: WORKSPACE_CMO_ROW_HEIGHT }}
      display="flex"
      flexDirection="column"
    >
      <Flex
        align="center"
        px="3.5"
        py="2.5"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        flexShrink={0}
      >
        <AiCmoWidgetTitle />
      </Flex>

      <Box flex="1" minH={0} display="flex">
        <GrowthProgramCockpit
          user={user}
          projectId={projectId}
          onEditProposal={onEditProposal}
          compact
        />
      </Box>
    </Box>
  );
}

function WorkspaceHome({
  user,
  projectId,
  attachmentProjectId,
  projectName,
  models,
  selectedModel,
  selectedModelId,
  onModelChange,
  executionMode,
  executionModes,
  onExecutionModeChange,
  loading,
  sessionPending,
  onSend,
  documentRestoration,
  onDocumentRestorationConsumed,
  onStop,
  emptyTitle,
  emptySubtitle,
  composerPlaceholder,
  hideHomeWidgets = true,
  activityEvents,
  activityLoading,
  taskIssues,
  taskIssuesLoading,
  homeIssuesError = false,
  homeActivityError = false,
  onOpenTaskIssue,
  onOpenActivityEvent,
  backgroundTaskIntent = false,
  backgroundTaskDraft = '',
  growthPlanComposerDraft = '',
  initialComposerDraft = '',
  initialComposerSkillId = null,
  aiCmoEnabled = false,
  aiCmoLoading = false,
  aiCmoConfigured = null,
  aiCmoStatusError = false,
  onBackgroundTaskIntentChange,
  onBackgroundTaskDraftConsumed,
  onGrowthPlanComposerDraftConsumed,
  onInitialComposerIntentConsumed,
}: {
  user: User | null;
  projectId: string | null;
  attachmentProjectId: string | null;
  projectName: string;
  models: PublicResearchModel[];
  selectedModel: PublicResearchModel | null;
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
  executionMode: ResearchExecutionMode;
  executionModes: ResearchExecutionMode[];
  onExecutionModeChange: (mode: ResearchExecutionMode) => void;
  loading: boolean;
  sessionPending: boolean;
  onSend: (
    content: string,
    images?: ImageAttachment[],
    selectedSkills?: Array<{ skillId: string; versionId?: string }>,
    executionMode?: ResearchExecutionMode,
    documents?: DocumentAttachment[]
  ) => boolean | void | Promise<boolean | void>;
  documentRestoration?: ComposerDocumentRestoration | null;
  onDocumentRestorationConsumed?: (id: string) => void;
  onStop: () => void;
  emptyTitle?: string;
  emptySubtitle?: string;
  composerPlaceholder?: string;
  hideHomeWidgets?: boolean;
  activityEvents?: GrowthActivityEventRecord[];
  activityLoading?: boolean;
  taskIssues?: GrowthIssueRecord[];
  taskIssuesLoading?: boolean;
  homeIssuesError?: boolean;
  homeActivityError?: boolean;
  onOpenTaskIssue?: (issue: GrowthIssueRecord) => void;
  onOpenActivityEvent?: (event: GrowthActivityEventRecord) => void;
  backgroundTaskIntent?: boolean;
  backgroundTaskDraft?: string;
  growthPlanComposerDraft?: string;
  initialComposerDraft?: string;
  initialComposerSkillId?: string | null;
  aiCmoEnabled?: boolean;
  aiCmoLoading?: boolean;
  aiCmoConfigured?: boolean | null;
  aiCmoStatusError?: boolean;
  onBackgroundTaskIntentChange?: (active: boolean) => void;
  onBackgroundTaskDraftConsumed?: () => void;
  onGrowthPlanComposerDraftConsumed?: () => void;
  onInitialComposerIntentConsumed?: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<SkillPickerItem[]>([]);
  const [pendingImages, setPendingImages] = useState<ImageAttachment[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<DocumentAttachment[]>([]);
  const pendingDocumentsRef = useRef<DocumentAttachment[]>([]);
  const attachmentProjectRef = useRef<string | null | undefined>(attachmentProjectId);
  const processingAttachmentRef = useRef(false);
  const [isProcessingAttachment, setIsProcessingAttachment] = useState(false);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const initialComposerIntentHandledRef = useRef<string | null>(null);
  const effectiveSkills = useEffectiveAgentSkills(projectId);
  const canAttachImages = modelSupportsImageAttachments(selectedModel);
  const canAttachDocuments = modelSupportsDocumentAttachments(selectedModel);
  const canAttachInComposer = (canAttachImages || canAttachDocuments) && !backgroundTaskIntent;
  const attachmentPolicyKey = [
    attachmentProjectId || '',
    selectedModelId,
    canAttachInComposer ? 'enabled' : 'disabled',
    canAttachImages ? 'images' : 'no-images',
    canAttachDocuments ? 'documents' : 'no-documents',
  ].join(':');
  const attachmentPolicyRef = useRef({
    key: attachmentPolicyKey,
    generation: 0,
    canAttachInComposer,
    canAttachImages,
    canAttachDocuments,
  });
  if (attachmentPolicyRef.current.key !== attachmentPolicyKey) {
    attachmentPolicyRef.current = {
      key: attachmentPolicyKey,
      generation: attachmentPolicyRef.current.generation + 1,
      canAttachInComposer,
      canAttachImages,
      canAttachDocuments,
    };
  } else {
    attachmentPolicyRef.current.canAttachInComposer = canAttachInComposer;
    attachmentPolicyRef.current.canAttachImages = canAttachImages;
    attachmentPolicyRef.current.canAttachDocuments = canAttachDocuments;
  }
  const imageUnavailableMessage = imageAttachmentsUnavailableMessage(selectedModel);
  const documentUnavailableMessage = documentAttachmentsUnavailableMessage(selectedModel);
  const pendingAttachmentCount = pendingImages.length + pendingDocuments.length;
  const pendingDocumentBytes = pendingDocuments.reduce(
    (total, document) => total + document.sizeBytes,
    0
  );
  const backgroundTaskCreatorSkill = effectiveSkills.skills.find(
    skill => skill.skillId === BACKGROUND_TASK_CREATOR_SKILL_ID
  );
  const showAiCmoSetup =
    aiCmoEnabled && ((!aiCmoLoading && aiCmoConfigured === false) || aiCmoStatusError);
  const editPendingGrowthProgram = useCallback(() => {
    setDraft('Revise the pending Growth Plan: ');
    requestAnimationFrame(() => composerInputRef.current?.focus());
  }, []);

  useEffect(() => {
    pendingDocumentsRef.current = pendingDocuments;
  }, [pendingDocuments]);

  const discardPendingDocuments = useCallback(() => {
    const documents = pendingDocumentsRef.current;
    pendingDocumentsRef.current = [];
    setPendingDocuments([]);
    documents.forEach(document => {
      void deleteUploadedDocument(document).catch(() => undefined);
    });
  }, []);

  const restoreRejectedDocuments = useCallback(
    (documents: DocumentAttachment[], originalProjectId: string | null | undefined) => {
      const policy = attachmentPolicyRef.current;
      if (
        attachmentProjectRef.current !== originalProjectId ||
        !policy.canAttachInComposer ||
        !policy.canAttachDocuments
      ) {
        documents.forEach(document => {
          void deleteUploadedDocument(document).catch(() => undefined);
        });
        return;
      }

      const restored = mergeRestoredDocumentAttachments(pendingDocumentsRef.current, documents);
      pendingDocumentsRef.current = restored.documents;
      setPendingDocuments(restored.documents);
      restored.rejected.forEach(document => {
        void deleteUploadedDocument(document).catch(() => undefined);
      });
    },
    []
  );

  useEffect(() => {
    if (!documentRestoration) return;
    restoreRejectedDocuments(documentRestoration.documents, documentRestoration.projectId);
    onDocumentRestorationConsumed?.(documentRestoration.id);
  }, [documentRestoration, onDocumentRestorationConsumed, restoreRejectedDocuments]);

  useEffect(() => {
    attachmentProjectRef.current = attachmentProjectId;
    setPendingImages([]);
    setPendingDocuments([]);
    return () => {
      attachmentProjectRef.current = undefined;
      const unsentDocuments = pendingDocumentsRef.current;
      pendingDocumentsRef.current = [];
      unsentDocuments.forEach(document => {
        void deleteUploadedDocument(document).catch(() => undefined);
      });
    };
  }, [attachmentProjectId]);

  useEffect(() => {
    if (!backgroundTaskDraft) return;
    setDraft(backgroundTaskDraft);
    onBackgroundTaskDraftConsumed?.();
  }, [backgroundTaskDraft, onBackgroundTaskDraftConsumed]);

  useEffect(() => {
    if (!growthPlanComposerDraft) return;
    setDraft(growthPlanComposerDraft);
    onGrowthPlanComposerDraftConsumed?.();
    requestAnimationFrame(() => composerInputRef.current?.focus());
  }, [growthPlanComposerDraft, onGrowthPlanComposerDraftConsumed]);

  useEffect(() => {
    if (!backgroundTaskIntent) return;
    setPendingImages([]);
    discardPendingDocuments();
    setSelectedSkills([]);
    if (executionMode !== 'standard') onExecutionModeChange('standard');
  }, [backgroundTaskIntent, discardPendingDocuments, executionMode, onExecutionModeChange]);

  useEffect(() => {
    if (canAttachImages || pendingImages.length === 0) return;
    setPendingImages([]);
  }, [canAttachImages, pendingImages.length]);

  useEffect(() => {
    if (canAttachDocuments || pendingDocuments.length === 0) return;
    discardPendingDocuments();
  }, [canAttachDocuments, discardPendingDocuments, pendingDocuments.length]);

  useEffect(() => {
    if (effectiveSkills.controls.explicitInvocationEnabled) return;
    setSelectedSkills([]);
  }, [effectiveSkills.controls.explicitInvocationEnabled]);

  useEffect(() => {
    setSelectedSkills([]);
  }, [projectId]);

  useEffect(() => {
    if (effectiveSkills.loading) return;
    const effectiveIds = new Set(effectiveSkills.skills.map(skill => skill.skillId));
    setSelectedSkills(current => current.filter(skill => effectiveIds.has(skill.skillId)));
  }, [effectiveSkills.loading, effectiveSkills.skills]);

  useEffect(() => {
    if (!initialComposerDraft && !initialComposerSkillId) {
      initialComposerIntentHandledRef.current = null;
      return;
    }
    const intentKey = `${initialComposerSkillId || ''}:${initialComposerDraft}`;
    if (initialComposerIntentHandledRef.current === intentKey || effectiveSkills.loading) return;
    if (!effectiveSkills.controls.explicitInvocationEnabled) {
      initialComposerIntentHandledRef.current = intentKey;
      onInitialComposerIntentConsumed?.();
      return;
    }

    const initialSkill = initialComposerSkillId
      ? effectiveSkills.skills.find(skill => skill.skillId === initialComposerSkillId)
      : null;
    if (initialSkill) {
      setSelectedSkills(current =>
        current.some(skill => skill.skillId === initialSkill.skillId)
          ? current
          : [...current, initialSkill]
      );
    }
    if (initialComposerDraft) setDraft(initialComposerDraft);
    initialComposerIntentHandledRef.current = intentKey;
    onInitialComposerIntentConsumed?.();
    requestAnimationFrame(() => composerInputRef.current?.focus());
  }, [
    effectiveSkills.controls.explicitInvocationEnabled,
    effectiveSkills.loading,
    effectiveSkills.skills,
    initialComposerDraft,
    initialComposerSkillId,
    onInitialComposerIntentConsumed,
  ]);

  const selectedSkillIds = useCallback(
    () =>
      backgroundTaskIntent
        ? [
            {
              skillId: BACKGROUND_TASK_CREATOR_SKILL_ID,
              versionId: backgroundTaskCreatorSkill?.versionId,
            },
          ]
        : selectedSkills.map(skill => ({
            skillId: skill.skillId,
            versionId: skill.versionId,
          })),
    [backgroundTaskCreatorSkill?.versionId, backgroundTaskIntent, selectedSkills]
  );

  const send = useCallback(
    async (content: string) => {
      if (
        (!content.trim() && pendingAttachmentCount === 0) ||
        sessionPending ||
        isProcessingAttachment
      )
        return;
      if (pendingImages.length > 0 && !canAttachImages) {
        setPendingImages([]);
        toast.error(imageUnavailableMessage);
        return;
      }
      if (pendingDocuments.length > 0 && !canAttachDocuments) {
        discardPendingDocuments();
        toast.error(documentUnavailableMessage);
        return;
      }
      const documentFormatError = unsupportedDocumentAttachmentMessage(
        selectedModel,
        pendingDocuments
      );
      if (documentFormatError) {
        toast.error(documentFormatError);
        return;
      }
      const images = pendingImages;
      const documents = pendingDocuments;
      const attachmentProjectId = attachmentProjectRef.current;
      pendingDocumentsRef.current = [];
      const skills = selectedSkillIds();
      const requestedTaskSkills =
        backgroundTaskIntent && selectedSkills.length > 0
          ? `\n\nUse these Skills in the task setup: ${selectedSkills
              .map(skill => `“${skill.displayName}”`)
              .join(', ')}.`
          : '';
      const message =
        (content.trim() ||
          (documents.length > 0 ? '(See attached document)' : '(See attached image)')) +
        requestedTaskSkills;
      setDraft('');
      setPendingImages([]);
      setPendingDocuments([]);
      if (!backgroundTaskIntent) setSelectedSkills([]);
      let streamAccepted: boolean | void = true;
      try {
        streamAccepted = await onSend(
          message,
          images.length > 0 ? images : undefined,
          skills.length > 0 ? skills : undefined,
          executionMode,
          documents.length > 0 ? documents : undefined
        );
      } catch {
        streamAccepted = false;
      }
      if (streamAccepted === false && documents.length > 0) {
        restoreRejectedDocuments(documents, attachmentProjectId);
      }
    },
    [
      backgroundTaskIntent,
      canAttachDocuments,
      canAttachImages,
      discardPendingDocuments,
      selectedModel,
      documentUnavailableMessage,
      executionMode,
      imageUnavailableMessage,
      isProcessingAttachment,
      onSend,
      pendingAttachmentCount,
      pendingDocuments,
      pendingImages,
      restoreRejectedDocuments,
      selectedSkills,
      selectedSkillIds,
      sessionPending,
    ]
  );

  const addAttachment = useCallback(
    async (file: File) => {
      const activeAttachmentProjectId = attachmentProjectId;
      const attachmentGeneration = attachmentPolicyRef.current.generation;
      if (processingAttachmentRef.current) return;
      if (!canAttachInComposer) {
        toast.error(documentUnavailableMessage);
        return;
      }
      if (pendingAttachmentCount >= MAX_DOCUMENT_ATTACHMENTS) {
        toast.error(`Maximum ${MAX_DOCUMENT_ATTACHMENTS} attachments per message`);
        return;
      }
      processingAttachmentRef.current = true;
      setIsProcessingAttachment(true);
      try {
        if (isSupportedImageType(file.type)) {
          if (!canAttachImages) {
            toast.error(imageUnavailableMessage);
            return;
          }
          const attachment = await fileToImageAttachment(file);
          const currentPolicy = attachmentPolicyRef.current;
          if (
            attachmentProjectRef.current !== activeAttachmentProjectId ||
            currentPolicy.generation !== attachmentGeneration ||
            !currentPolicy.canAttachInComposer ||
            !currentPolicy.canAttachImages
          ) {
            return;
          }
          setPendingImages(current => [...current, attachment]);
          return;
        }
        if (!documentFileTypeFromName(file.name)) {
          toast.error('Unsupported file type.');
          return;
        }
        if (!canAttachDocuments) {
          toast.error(documentUnavailableMessage);
          return;
        }
        const documentFormatError = unsupportedDocumentAttachmentMessage(selectedModel, [file]);
        if (documentFormatError) {
          toast.error(documentFormatError);
          return;
        }
        if (pendingDocumentBytes + file.size > MAX_DOCUMENT_TOTAL_BYTES) {
          toast.error('Document attachments must total 10 MB or less.');
          return;
        }
        const attachment = await uploadDocumentAttachment(file, activeAttachmentProjectId);
        const currentPolicy = attachmentPolicyRef.current;
        if (
          attachmentProjectRef.current !== activeAttachmentProjectId ||
          currentPolicy.generation !== attachmentGeneration ||
          !currentPolicy.canAttachInComposer ||
          !currentPolicy.canAttachDocuments
        ) {
          await deleteUploadedDocument(attachment).catch(() => undefined);
          return;
        }
        setPendingDocuments(current => {
          const next = [...current, attachment];
          pendingDocumentsRef.current = next;
          return next;
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to process attachment');
      } finally {
        processingAttachmentRef.current = false;
        setIsProcessingAttachment(false);
      }
    },
    [
      canAttachDocuments,
      canAttachImages,
      canAttachInComposer,
      documentUnavailableMessage,
      selectedModel,
      imageUnavailableMessage,
      pendingAttachmentCount,
      pendingDocumentBytes,
      attachmentProjectId,
    ]
  );

  const handleAttachFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const selectedFiles = Array.from(files);
      const availableSlots = MAX_DOCUMENT_ATTACHMENTS - pendingAttachmentCount;
      if (selectedFiles.length > availableSlots) {
        toast.error(`Maximum ${MAX_DOCUMENT_ATTACHMENTS} attachments per message`);
      }
      void (async () => {
        let remainingDocumentBytes = MAX_DOCUMENT_TOTAL_BYTES - pendingDocumentBytes;
        for (const file of selectedFiles.slice(0, Math.max(availableSlots, 0))) {
          if (!isSupportedImageType(file.type) && documentFileTypeFromName(file.name)) {
            if (file.size > remainingDocumentBytes) {
              toast.error('Document attachments must total 10 MB or less.');
              continue;
            }
            remainingDocumentBytes -= file.size;
          }
          await addAttachment(file);
        }
      })();
    },
    [addAttachment, pendingAttachmentCount, pendingDocumentBytes]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      for (const item of Array.from(event.clipboardData.items)) {
        if (!item.type.startsWith('image/')) continue;
        event.preventDefault();
        const file = item.getAsFile();
        if (file) void addAttachment(file);
        return;
      }
    },
    [addAttachment]
  );

  return (
    <Box
      data-section="workspaceHomeScroller"
      h="full"
      minH={0}
      overflowY="auto"
      overflowX="hidden"
      px={hideHomeWidgets ? 0 : { base: 2, md: 0 }}
      pt={hideHomeWidgets ? 0 : { base: 3, md: 6 }}
      pb={hideHomeWidgets ? 3 : { base: 3, md: 6 }}
      scrollbarGutter={hideHomeWidgets ? 'auto' : 'stable'}
      scrollbarWidth={hideHomeWidgets ? 'none' : undefined}
      overscrollBehavior="contain"
      css={{
        '@container app-content (max-width: 47.9975rem)': {
          paddingInline: hideHomeWidgets ? '0' : 'var(--chakra-spacing-2)',
          paddingBlock: hideHomeWidgets ? undefined : 'var(--chakra-spacing-3)',
        },
      }}
    >
      <Box
        data-section="workspaceHomeContent"
        w="full"
        minH="full"
        maxW={hideHomeWidgets ? '792px' : '1320px'}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        mx="auto"
        px={hideHomeWidgets ? 4 : { base: 0, md: 4, '2xl': 6 }}
        css={{
          '@container app-content (max-width: 47.9975rem)': {
            paddingInline: hideHomeWidgets ? 'var(--chakra-spacing-4)' : '0',
          },
        }}
      >
        <VStack align="stretch" gap="4" flex="1" mb={hideHomeWidgets ? '0' : '6'}>
          <VStack
            data-section="workspaceHomePrompt"
            align="stretch"
            gap={{ base: 3, md: 4 }}
            w="full"
            flex="1"
            minW={0}
            css={{
              '@container app-content (max-width: 47.9975rem)': {
                gap: 'var(--chakra-spacing-3)',
              },
            }}
          >
            <Box
              data-section="workspaceWelcome"
              flex={hideHomeWidgets ? '1' : undefined}
              minH={hideHomeWidgets ? '200px' : undefined}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={hideHomeWidgets ? '10' : '0'}
              textAlign="center"
              css={{ '@container app-content (max-width: 47.9975rem)': { textAlign: 'center' } }}
            >
              {hideHomeWidgets ? (
                <Image
                  src="/logo_mini.svg"
                  alt=""
                  boxSize="9"
                  mb="5"
                  opacity={0.28}
                  filter="grayscale(1)"
                  _dark={{ filter: 'grayscale(1) brightness(0) invert(1)' }}
                />
              ) : null}
              <Heading
                as="h1"
                fontSize={{ base: 'lg', md: 'xl' }}
                lineHeight={{ base: '1.2', md: '1.15' }}
                fontWeight="400"
                letterSpacing="-0.02em"
                color="ui.text.primary"
                css={{
                  '@container app-content (max-width: 47.9975rem)': {
                    fontSize: 'var(--chakra-font-sizes-xl)',
                    lineHeight: '1.2',
                  },
                }}
              >
                {emptyTitle || `What should we work on${projectName ? ` in ${projectName}` : ''}?`}
              </Heading>
              {hideHomeWidgets && !emptyTitle && !backgroundTaskIntent && projectId ? (
                <WorkspaceQuestionSuggestions
                  user={user}
                  projectId={projectId}
                  disabled={sessionPending || loading || Boolean(draft.trim())}
                  onSelect={question => {
                    setDraft(question);
                    requestAnimationFrame(() => composerInputRef.current?.focus());
                  }}
                />
              ) : null}
              {!hideHomeWidgets || emptySubtitle ? (
                <Text mt="2" fontSize="sm" lineHeight="1.5" color="ui.text.secondary" maxW="760px">
                  {emptySubtitle || 'Review performance, follow your work, or start a new task.'}
                </Text>
              ) : null}
            </Box>
            <WorkspaceComposer
              value={draft}
              setValue={setDraft}
              models={models}
              selectedModel={selectedModel}
              selectedModelId={selectedModelId}
              onModelChange={onModelChange}
              executionMode={executionMode}
              executionModes={executionModes}
              onExecutionModeChange={onExecutionModeChange}
              pendingImages={pendingImages}
              pendingDocuments={pendingDocuments}
              isProcessingAttachment={isProcessingAttachment}
              canAttachImages={canAttachImages}
              canAttachDocuments={canAttachDocuments}
              imageUnavailableMessage={imageUnavailableMessage}
              disabled={sessionPending}
              loading={loading}
              placeholder={composerPlaceholder}
              onSubmit={() => send(draft)}
              onStop={onStop}
              onAttachFiles={handleAttachFiles}
              onPaste={handlePaste}
              onRemoveImage={imageId =>
                setPendingImages(current => current.filter(image => image.id !== imageId))
              }
              onRemoveDocument={document => {
                setPendingDocuments(current => {
                  const next = current.filter(item => item.id !== document.id);
                  pendingDocumentsRef.current = next;
                  return next;
                });
                void deleteUploadedDocument(document).catch(() => {
                  toast.error(`Failed to remove ${document.name} from storage.`);
                });
              }}
              availableSkills={effectiveSkills.skills}
              selectedSkills={selectedSkills}
              skillsLoading={effectiveSkills.loading}
              skillsError={effectiveSkills.error}
              skillSelectionEnabled={effectiveSkills.controls.explicitInvocationEnabled}
              onSelectedSkillsChange={setSelectedSkills}
              backgroundTaskIntent={backgroundTaskIntent}
              backgroundTaskIntentAvailable={Boolean(backgroundTaskCreatorSkill)}
              onBackgroundTaskIntentChange={onBackgroundTaskIntentChange}
              textareaRef={composerInputRef}
            />
            {!hideHomeWidgets && showAiCmoSetup ? (
              <AiCmoPanel
                user={user}
                projectId={projectId}
                onEditProposal={editPendingGrowthProgram}
              />
            ) : null}
          </VStack>
        </VStack>
        {hideHomeWidgets ? null : (
          <MemoizedWorkspaceHomeWidgets
            key={projectId}
            user={user}
            projectId={projectId}
            taskIssues={taskIssues}
            taskIssuesLoading={taskIssuesLoading}
            homeIssuesError={homeIssuesError}
            homeActivityError={homeActivityError}
            activityEvents={activityEvents || []}
            activityLoading={Boolean(activityLoading)}
            onOpenTaskIssue={onOpenTaskIssue}
            onOpenActivityEvent={onOpenActivityEvent}
          />
        )}
      </Box>
    </Box>
  );
}

function WorkspaceActivityBridge({
  events,
  summary,
  loading,
  onOpenActivityFeed,
}: {
  events: GrowthActivityEventRecord[];
  summary: GrowthInboxSummaryRecord | null;
  loading: boolean;
  onOpenActivityFeed: () => void;
}) {
  const latestEvent = events[0] || null;
  const latestLabels = latestEvent ? getGrowthActivityFeedLabels(latestEvent) : null;
  const activeCount = summary ? growthStatusCount(summary, ACTIVE_GROWTH_WORK_STATUS_LIST) : null;
  const measuringCount = summary
    ? growthStatusCount(summary, MEASURING_GROWTH_WORK_STATUS_LIST)
    : null;
  const completedCount = summary
    ? growthStatusCount(summary, COMPLETED_GROWTH_WORK_STATUS_LIST)
    : null;

  return (
    <Flex
      align={{ base: 'stretch', md: 'center' }}
      justify="space-between"
      direction={{ base: 'column', md: 'row' }}
      gap="3"
      layerStyle="surfaceCard"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="12px"
      px="4"
      py="3.5"
    >
      <HStack gap="3" align="center" minW={0}>
        <Box
          boxSize="9"
          borderRadius="glassInput"
          bg="ui.surface.selected"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon boxSize="4" color="ui.text.secondary">
            <Workflow />
          </Icon>
        </Box>
        <Box minW={0}>
          <HStack gap="2.5" align="center" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="800" color="ui.text.primary">
              Recent Activity
            </Text>
            {activeCount !== null ? (
              <Text
                as="span"
                fontSize="xs"
                color="ui.text.tertiary"
                fontWeight="700"
                fontVariantNumeric="tabular-nums"
              >
                {activeCount} active
              </Text>
            ) : null}
            {measuringCount !== null ? (
              <Text
                as="span"
                fontSize="xs"
                color="ui.text.tertiary"
                fontWeight="700"
                fontVariantNumeric="tabular-nums"
              >
                {measuringCount} measuring
              </Text>
            ) : null}
            {completedCount !== null ? (
              <Text
                as="span"
                fontSize="xs"
                color="ui.text.tertiary"
                fontWeight="700"
                fontVariantNumeric="tabular-nums"
              >
                {completedCount} done
              </Text>
            ) : null}
          </HStack>
          {loading && !latestLabels ? (
            <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35">
              Loading recent work…
            </Text>
          ) : latestLabels ? (
            <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35" lineClamp={2}>
              {feedActionLabel(latestEvent.type)}: {latestLabels.primary}
              {latestEvent.created_at
                ? ` · ${formatCodexRelativeTime(latestEvent.created_at)}`
                : ''}
            </Text>
          ) : (
            <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35">
              Ready when you are—put Attune to work.
            </Text>
          )}
        </Box>
      </HStack>
      <Button
        size="sm"
        variant="outline"
        borderRadius="glassInput"
        color="ui.text.primary"
        flexShrink={0}
        onClick={onOpenActivityFeed}
      >
        <Activity size={14} />
        View activity
        <ChevronRight size={14} />
      </Button>
    </Flex>
  );
}

function useWorkspaceModuleStatus(user: User | null, projectId: string | null) {
  const [state, setState] = useState<{ projectId: string; modules: HomeModuleStatus[] } | null>(
    null
  );
  useEffect(() => {
    if (!user || !projectId) return;
    const controller = new AbortController();
    const activeUser = user;
    const activeProjectId = projectId;
    async function load() {
      try {
        const token = await activeUser.getIdToken();
        const response = await fetch(
          `/api/projects/${encodeURIComponent(activeProjectId)}/setup?include_modules=true`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]),
          }
        );
        if (!response.ok) throw new Error('Workspace modules unavailable');
        const payload = await response.json();
        if (!controller.signal.aborted)
          setState({
            projectId: activeProjectId,
            modules: Array.isArray(payload.modules) ? payload.modules : [],
          });
      } catch {
        // Settle discovery even on failure; existing traffic data can establish visibility.
        if (!controller.signal.aborted) setState({ projectId: activeProjectId, modules: [] });
      }
    }
    void load();
    return () => controller.abort();
  }, [projectId, user]);
  return {
    modules: state?.projectId === projectId ? state.modules : [],
    loading: Boolean(user && projectId && state?.projectId !== projectId),
  };
}

function WorkspaceHomeWidgets({
  user,
  projectId,
  taskIssues,
  taskIssuesLoading = false,
  homeIssuesError = false,
  homeActivityError = false,
  activityEvents,
  activityLoading = false,
  onOpenTaskIssue,
  onOpenActivityEvent,
}: {
  user: User | null;
  projectId: string | null;
  taskIssues?: GrowthIssueRecord[];
  taskIssuesLoading?: boolean;
  homeIssuesError?: boolean;
  homeActivityError?: boolean;
  activityEvents: GrowthActivityEventRecord[];
  activityLoading?: boolean;
  onOpenTaskIssue?: (issue: GrowthIssueRecord) => void;
  onOpenActivityEvent?: (event: GrowthActivityEventRecord) => void;
}) {
  const { currentProject } = useProject();
  const websitePerformance = useWorkspaceWebsitePerformance(user, projectId);
  const shopifyRevenue = useWorkspaceShopifyRevenue(user, projectId);
  const { modules, loading: modulesLoading } = useWorkspaceModuleStatus(user, projectId);
  const visibility = getWorkspaceHomeWidgetVisibility({
    project: currentProject,
    modules,
    trafficSourceCount: websitePerformance.payload?.sourceCoverage?.length,
    trafficRowCount: websitePerformance.payload?.trend?.length,
    issueCount: taskIssues?.length,
    activityCount: activityEvents.length,
    workLoading: taskIssuesLoading || activityLoading,
    workError: homeIssuesError || homeActivityError,
  });
  // Resolve membership before revealing the grid. Metric requests still complete independently.
  const layoutReady =
    Boolean(user && projectId) &&
    !modulesLoading &&
    !shopifyRevenue.statusLoading &&
    (visibility.traffic || !websitePerformance.loading);
  const onboardingJobId = currentProject?.onboarding_completed
    ? null
    : currentProject?.onboarding_job_id || null;

  return (
    <VStack align="stretch" gap="3" w="full">
      <FirstCollectionProgressCard user={user} jobId={onboardingJobId} />
      <Box position="relative" minH="300px" aria-busy={!layoutReady}>
        {!layoutReady ? (
          <Box position="absolute" top="0" insetInline="0" h="300px">
            <WidgetLoading label="Loading workspace…" />
          </Box>
        ) : null}
        <SimpleGrid
          visibility={layoutReady ? 'visible' : 'hidden'}
          aria-hidden={!layoutReady}
          columns={{ base: 1, xl: 2 }}
          gap="3"
          alignItems="stretch"
          css={{
            '@container app-content (max-width: 47.9975rem)': { gridTemplateColumns: '1fr' },
          }}
        >
          {visibility.intelligence ? (
            <Box h="full" minW="0">
              <BrandVisibilityHomeWidget user={user} projectId={projectId} />
            </Box>
          ) : null}
          {visibility.traffic ? (
            <Box h="full" minW="0">
              <TrafficImpressionsWidget performance={websitePerformance} />
            </Box>
          ) : null}
          {shopifyRevenue.available ? <ShopifyRevenueWidget revenue={shopifyRevenue} /> : null}
          <Box
            gridColumn={{
              base: 'auto',
              xl: shopifyRevenue.available ? 'auto' : 'span 2',
            }}
            minH="252px"
            h="full"
            css={{ '@container app-content (max-width: 47.9975rem)': { gridColumn: 'auto' } }}
          >
            <HomeWorkWidget
              issues={taskIssues || []}
              loading={taskIssuesLoading}
              issuesLoadError={homeIssuesError}
              activityLoadError={homeActivityError}
              events={activityEvents}
              activityLoading={activityLoading}
              onOpenIssue={onOpenTaskIssue}
              onOpenActivityEvent={onOpenActivityEvent}
            />
          </Box>
        </SimpleGrid>
      </Box>
    </VStack>
  );
}

const MemoizedWorkspaceHomeWidgets = memo(WorkspaceHomeWidgets);

function WorkspaceHomeChartWidgets(props: { user: User | null; projectId: string | null }) {
  const { currentProject } = useProject();
  if (!getWorkspaceHomeWidgetVisibility({ project: currentProject }).intelligence) return null;
  return <ConfiguredWorkspaceHomeChartWidgets {...props} />;
}

function ConfiguredWorkspaceHomeChartWidgets({
  user,
  projectId,
}: {
  user: User | null;
  projectId: string | null;
}) {
  const { currentProject, loading: projectLoading } = useProject();
  const citationShare = useWorkspaceCitationShare(user, projectId);
  const brandSettings = currentProject?.brand_settings;
  const defaultBrandName = brandSettings?.brand_name?.trim() || currentProject?.name?.trim() || '';
  const defaultBrandSlug = brandSettings?.brand_slug?.trim() || defaultBrandName;
  const configuredBrandVariations = useMemo(
    () => brandSettings?.brand_variations || [],
    [brandSettings?.brand_variations]
  );
  const brandMergeGroups = useMemo(
    () => currentProject?.brand_merge_groups || [],
    [currentProject?.brand_merge_groups]
  );
  const projectDefaultBrand = useMemo(
    () =>
      buildConfiguredBrandGroups({
        brandName: defaultBrandName,
        projectBrandSlug: defaultBrandSlug,
        brandVariations: configuredBrandVariations,
        brandMergeGroups,
      }).find(group => group.isProjectDefaultBrand) || null,
    [brandMergeGroups, configuredBrandVariations, defaultBrandName, defaultBrandSlug]
  );
  const dashboardBrandGroups = useMemo(() => {
    const groups: Array<{ canonical_name: string; variations: string[] }> = [];
    if (defaultBrandName) {
      groups.push({
        canonical_name: defaultBrandName,
        variations: configuredBrandVariations.map(variation => variation.trim()).filter(Boolean),
      });
    }
    brandMergeGroups.forEach(group => {
      const canonicalName = group.canonical_name?.trim();
      if (!canonicalName) return;
      groups.push({
        canonical_name: canonicalName,
        variations: (group.variations || []).map(variation => variation.trim()).filter(Boolean),
      });
    });
    return groups;
  }, [brandMergeGroups, configuredBrandVariations, defaultBrandName]);
  const leaderboardParams = useMemo(
    () => ({
      brand_groups: dashboardBrandGroups,
      limit: 200,
    }),
    [dashboardBrandGroups]
  );
  const {
    brands: leaderboardBrands,
    timeSeries: leaderboardTimeSeries,
    loading: marketLoading,
    error: marketError,
  } = useBrandsPageDiscovery(leaderboardParams);
  const homeBrandRow = useMemo(() => {
    const matchSet = projectDefaultBrand?.matchSet;
    if (!matchSet) return null;
    return (
      leaderboardBrands.find(
        brand =>
          matchesBrandValue(String(brand.brand || ''), matchSet) ||
          matchesBrandValue(String(brand.brand_lowercase || ''), matchSet)
      ) || null
    );
  }, [leaderboardBrands, projectDefaultBrand?.matchSet]);
  const homeBrandTrendRows = useMemo<BrandHomeTrendRow[]>(() => {
    const matchSet = projectDefaultBrand?.matchSet;
    if (!matchSet) return [];
    return leaderboardTimeSeries.filter(
      row =>
        matchesBrandValue(String(row.brand || ''), matchSet) ||
        matchesBrandValue(String(row.brand_lowercase || ''), matchSet)
    );
  }, [leaderboardTimeSeries, projectDefaultBrand?.matchSet]);
  const trendPoints = useMemo(
    () => buildBrandHomeTrendPoints(homeBrandTrendRows),
    [homeBrandTrendRows]
  );
  const summary = useMemo(
    () => buildBrandHomeWidgetSummary(homeBrandRow, trendPoints),
    [homeBrandRow, trendPoints]
  );
  const marketChartPoints = useMemo<WorkspaceHomeChartPoint[]>(
    () =>
      trendPoints.map(point => ({
        date: point.date,
        visibilityPct: point.visibilityPct,
        shareOfVoicePct: point.top3SovPct,
      })),
    [trendPoints]
  );
  const marketState = {
    loading:
      Boolean(user && projectId && currentProject && !projectLoading && projectDefaultBrand) &&
      marketLoading,
    error: marketError,
    hasConfiguredBrand: Boolean(defaultBrandName && projectDefaultBrand),
    hasData: Boolean(homeBrandRow),
  };

  return (
    <SimpleGrid columns={{ base: 1, xl: 3 }} gap="3" alignItems="stretch">
      <WorkspaceCitationShareWidget
        rows={citationShare.rows}
        loading={citationShare.loading}
        error={citationShare.error}
      />
      <WorkspaceMarketLineWidget
        title="Visibility"
        href="/app/ai-search/competitors/charts"
        actionLabel="Open Market"
        value={formatPercent(summary.visibilityPct)}
        metric={{
          key: 'visibilityPct',
          label: 'Visibility',
          color: BRAND_VISIBILITY_COLOR,
          fill: 'workspaceHomeVisibilityFill',
        }}
        data={marketChartPoints}
        state={marketState}
      />
      <WorkspaceMarketLineWidget
        title="Share of Voice"
        href="/app/ai-search/competitors/charts"
        actionLabel="Open Market"
        value={formatPercent(summary.top3SovPct)}
        metric={{
          key: 'shareOfVoicePct',
          label: 'Share of Voice',
          color: BRAND_SOV_COLOR,
          fill: 'workspaceHomeShareOfVoiceFill',
        }}
        data={marketChartPoints}
        state={marketState}
      />
    </SimpleGrid>
  );
}

function WorkspaceMarketLineWidget({
  title,
  href,
  actionLabel,
  value,
  metric,
  data,
  state,
}: {
  title: string;
  href: string;
  actionLabel: string;
  value: string;
  metric: WorkspaceTrendMetric<WorkspaceHomeChartMetricKey>;
  data: WorkspaceHomeChartPoint[];
  state: {
    loading: boolean;
    error: string | null;
    hasConfiguredBrand: boolean;
    hasData: boolean;
  };
}) {
  const hasTrend = data.some(point => point[metric.key] > 0);

  return (
    <WorkspaceWidgetShell icon={<TrendingUp />} title={title} href={href} actionLabel={actionLabel}>
      {!state.hasConfiguredBrand ? (
        <WidgetMessage>Choose a brand to start tracking progress.</WidgetMessage>
      ) : state.loading && !state.hasData ? (
        <WidgetLoading label={`Loading ${title.toLowerCase()}…`} />
      ) : state.error && !state.hasData ? (
        <WidgetMessage>{state.error}</WidgetMessage>
      ) : !state.hasData ? (
        <WidgetMessage>Run a scan to see {title.toLowerCase()}.</WidgetMessage>
      ) : (
        <VStack align="stretch" gap="3" p="4" minH="236px">
          <Text
            fontSize="3xl"
            color="ui.text.primary"
            fontWeight="850"
            lineHeight="1"
            fontVariantNumeric="tabular-nums"
          >
            {value}
          </Text>
          <Box h="132px" mt="auto">
            {hasTrend ? (
              <WorkspaceChart
                kind="metric-area"
                data={data}
                metric={metric}
                tooltip={<WorkspaceTrendTooltip metric={metric} />}
                xKey="date"
                yFloor={5}
                margin={{ top: 10, right: 4, bottom: 8, left: 4 }}
              />
            ) : (
              <Flex
                align="center"
                justify="center"
                h="full"
                borderTopWidth="1px"
                borderColor="ui.border.subtle"
              >
                <Text fontSize="xs" color="ui.text.tertiary">
                  Your trend starts after the next scan
                </Text>
              </Flex>
            )}
          </Box>
        </VStack>
      )}
    </WorkspaceWidgetShell>
  );
}

interface WorkspaceCitationShareSeries {
  key: string;
  label: string;
  color: string;
}

type WorkspaceCitationShareChartRow = {
  date: string;
  dateLabel: string;
} & Record<string, number | string>;

function buildWorkspaceCitationShareChart(rows: CitationSourceShareRow[]) {
  const totalRows = rows.filter(row => (row.scope_key || 'total') === 'total');
  const totalsByDomain = new Map<string, number>();
  totalRows.forEach(row => {
    const domain = String(row.domain || '').trim();
    if (!domain) return;
    totalsByDomain.set(domain, (totalsByDomain.get(domain) || 0) + numeric(row.citation_count));
  });
  const domains = Array.from(totalsByDomain.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 5)
    .map(([domain]) => domain);
  const series: WorkspaceCitationShareSeries[] = domains.map((domain, index) => ({
    key: `source_${index}`,
    label: domain,
    color: WORKSPACE_HOME_CHART_COLORS[index % WORKSPACE_HOME_CHART_COLORS.length],
  }));
  const includeOther = domains.length > 0;
  if (includeOther) {
    series.push({
      key: 'other',
      label: 'Other',
      color: 'var(--chakra-colors-ui-text-tertiary)',
    });
  }
  const sourceKeyByDomain = new Map(domains.map((domain, index) => [domain, `source_${index}`]));
  const dates = Array.from(
    new Set(totalRows.map(row => String(row.date || '').trim()).filter(Boolean))
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  const rowsByDate = new Map<string, CitationSourceShareRow[]>();
  totalRows.forEach(row => {
    const date = String(row.date || '').trim();
    if (!date) return;
    rowsByDate.set(date, [...(rowsByDate.get(date) || []), row]);
  });
  const chartRows: WorkspaceCitationShareChartRow[] = dates.map(date => {
    const chartRow: WorkspaceCitationShareChartRow = { date, dateLabel: formatChartDate(date) };
    let selectedShare = 0;
    (rowsByDate.get(date) || []).forEach(row => {
      const domain = String(row.domain || '').trim();
      const key = sourceKeyByDomain.get(domain);
      if (!key) return;
      const share = numeric(row.top3_share_pct);
      chartRow[key] = share;
      selectedShare += share;
    });
    if (includeOther) chartRow.other = Math.max(0, 100 - selectedShare);
    return chartRow;
  });

  return { rows: chartRows, series };
}

function WorkspaceCitationShareTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; value?: number | string | null }>;
  label?: string;
  series: WorkspaceCitationShareSeries[];
}) {
  const rows = (payload || [])
    .map(entry => {
      const item = series.find(candidate => candidate.key === entry.dataKey);
      const value = Number(entry.value);
      return item && Number.isFinite(value) && value > 0 ? { ...item, value } : null;
    })
    .filter((entry): entry is WorkspaceCitationShareSeries & { value: number } => Boolean(entry))
    .sort((a, b) => b.value - a.value);
  if (!active || rows.length === 0) return null;

  return (
    <Box
      bg="ui.surface.popover"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="glassInput"
      boxShadow="none"
      px="3"
      py="2"
      minW="180px"
    >
      <Text fontSize="xs" color="ui.text.tertiary" fontWeight="800" mb="1">
        {label}
      </Text>
      <VStack align="stretch" gap="1">
        {rows.map(row => (
          <HStack key={row.key} gap="2" justify="space-between">
            <HStack gap="2" minW={0}>
              <Box boxSize="2" borderRadius="full" bg={row.color} flexShrink={0} />
              <Text fontSize="xs" color="ui.text.secondary" lineClamp={1}>
                {row.label}
              </Text>
            </HStack>
            <Text fontSize="xs" color="ui.text.primary" fontWeight="850">
              {formatPercent(row.value)}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

function WorkspaceCitationShareWidget({
  rows,
  loading,
  error,
}: {
  rows: CitationSourceShareRow[];
  loading: boolean;
  error: string | null;
}) {
  const chart = useMemo(() => buildWorkspaceCitationShareChart(rows), [rows]);
  const latestRow = chart.rows[chart.rows.length - 1] || null;
  const legendRows = latestRow
    ? chart.series
        .map(series => ({ ...series, value: Number(latestRow[series.key]) || 0 }))
        .filter(row => row.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 4)
    : [];
  const hasChart = chart.rows.length > 0 && chart.series.length > 0;

  return (
    <WorkspaceWidgetShell
      icon={<ChartPie />}
      title="Citation Share"
      href="/app/citation-report/charts"
      actionLabel="Open Authority"
    >
      {loading && !hasChart ? (
        <WidgetLoading label="Loading citation share…" />
      ) : error && !hasChart ? (
        <WidgetMessage>{error}</WidgetMessage>
      ) : !hasChart ? (
        <WidgetMessage>Run a scan to see who earns citations.</WidgetMessage>
      ) : (
        <VStack align="stretch" gap="3" p="4" minH="236px">
          <Box h="132px">
            <WorkspaceChart
              kind="stacked-bar"
              data={chart.rows}
              series={chart.series}
              tooltip={<WorkspaceCitationShareTooltip series={chart.series} />}
            />
          </Box>
          <SimpleGrid columns={2} gap="2" mt="auto">
            {legendRows.map(row => (
              <HStack key={row.key} gap="2" minW={0}>
                <Box boxSize="2" borderRadius="full" bg={row.color} flexShrink={0} />
                <Text fontSize="xs" color="ui.text.secondary" lineClamp={1} flex="1" minW={0}>
                  {row.label}
                </Text>
                <Text fontSize="xs" color="ui.text.primary" fontWeight="850" flexShrink={0}>
                  {formatPercent(row.value)}
                </Text>
              </HStack>
            ))}
          </SimpleGrid>
        </VStack>
      )}
    </WorkspaceWidgetShell>
  );
}

function BrandVisibilityKpiWidget({ brand }: { brand: WorkspaceHomeBrandState }) {
  const visibilityMetrics = useMemo<BrandHomeTrendMetric[]>(
    () => [
      {
        key: 'visibilityPct',
        label: 'Visibility',
        color: BRAND_VISIBILITY_COLOR,
        fill: 'workspaceKpiVisibilityFill',
        formatter: formatPercent,
        domain: [0, 100],
      },
      {
        key: 'rank3Pct',
        label: 'Rank 1-3',
        color: BRAND_RANK3_COLOR,
        fill: 'workspaceKpiRank3Fill',
        formatter: formatPercent,
        domain: [0, 100],
      },
    ],
    []
  );

  return (
    <WorkspaceWidgetShell
      icon={<Activity />}
      title="Brand Visibility"
      href={brand.brandHref}
      actionLabel="Open Brand"
      headerSlot={brand.promptGroupFilter}
    >
      {!brand.hasConfiguredBrand ? (
        <WidgetMessage>Choose a brand to start tracking progress.</WidgetMessage>
      ) : brand.loading && !brand.hasData ? (
        <WidgetLoading label="Loading brand visibility…" />
      ) : brand.error && !brand.hasData ? (
        <WidgetMessage>{brand.error}</WidgetMessage>
      ) : !brand.hasData ? (
        <WidgetMessage>Run a scan to see your brand’s visibility.</WidgetMessage>
      ) : (
        <WorkspaceKpiBody
          eyebrow="Visibility"
          value={formatPercent(brand.summary.visibilityPct)}
          badge={`${formatPercent(brand.summary.rank3Pct)} rank 1-3`}
          badgeColor="status.success.fg"
        >
          <BrandHomeTrendChart data={brand.trendPoints} metrics={visibilityMetrics} />
          <SimpleGrid columns={2} gap="2">
            <WorkspaceMiniStat label="Rank 1" value={formatPercent(brand.summary.rank1Pct)} />
            <WorkspaceMiniStat label="Top-3" value={formatCompactNumber(brand.summary.top3Count)} />
          </SimpleGrid>
        </WorkspaceKpiBody>
      )}
    </WorkspaceWidgetShell>
  );
}

function AnswerShareKpiWidget({ brand }: { brand: WorkspaceHomeBrandState }) {
  const top3SovMetric = useMemo<BrandHomeTrendMetric[]>(
    () => [
      {
        key: 'top3SovPct',
        label: 'Top-3 SOV',
        color: 'var(--chakra-colors-status-warning-fg)',
        fill: 'workspaceKpiTop3SovFill',
        formatter: formatPercent,
        domain: [0, 100],
      },
    ],
    []
  );

  return (
    <WorkspaceWidgetShell
      icon={<Sparkles />}
      title="Answer Share"
      href={brand.brandHref}
      actionLabel="Open Rank"
    >
      {!brand.hasConfiguredBrand ? (
        <WidgetMessage>Choose a brand to start tracking progress.</WidgetMessage>
      ) : brand.loading && !brand.hasData ? (
        <WidgetLoading label="Loading answer share…" />
      ) : brand.error && !brand.hasData ? (
        <WidgetMessage>{brand.error}</WidgetMessage>
      ) : !brand.hasData ? (
        <WidgetMessage>Run a scan to see how often your brand is the answer.</WidgetMessage>
      ) : (
        <WorkspaceKpiBody
          eyebrow="Top-3 SOV"
          value={formatPercent(brand.summary.top3SovPct)}
          badge={`${formatPercent(brand.summary.totalSovPct)} total SOV`}
          badgeColor="status.warning.fg"
        >
          <BrandHomeTrendChart data={brand.trendPoints} metrics={top3SovMetric} />
          <SimpleGrid columns={2} gap="2">
            <WorkspaceMiniStat
              label="Top-3 citations"
              value={formatCompactNumber(brand.summary.top3Count)}
            />
            <WorkspaceMiniStat label="Rank 1" value={formatPercent(brand.summary.rank1Pct)} />
          </SimpleGrid>
        </WorkspaceKpiBody>
      )}
    </WorkspaceWidgetShell>
  );
}

function OpportunityRiskWidget({
  performance,
  opportunities,
  opportunityRows,
}: {
  performance: ReturnType<typeof useWorkspaceWebsitePerformance>;
  opportunities: ReturnType<typeof useWorkspaceOpportunities>;
  opportunityRows: WebsiteOpportunityRow[];
}) {
  const totalRiskScore = opportunityRows.reduce((sum, row) => sum + numeric(row.impact), 0);
  const highRiskCount = opportunityRows.filter(
    row => opportunitySeverity(row.severity) === 'high'
  ).length;
  const ga4Revenue = numeric(performance.payload?.providers?.ga4?.totals?.revenue);
  const chartData = opportunityRows.slice(0, 8).map((row, index) => ({
    label: String(index + 1),
    impact: numeric(row.impact),
  }));

  return (
    <WorkspaceWidgetShell
      icon={<AlertTriangle />}
      title="Opportunity Risk"
      href="/app/pages/overview"
      actionLabel="Open Analytics"
    >
      {opportunities.loading && opportunityRows.length === 0 ? (
        <WidgetLoading label="Loading opportunities…" />
      ) : opportunities.error && opportunityRows.length === 0 ? (
        <WidgetMessage>{opportunities.error}</WidgetMessage>
      ) : opportunityRows.length === 0 ? (
        <WidgetMessage>All clear—no urgent opportunities in this range.</WidgetMessage>
      ) : (
        <WorkspaceKpiBody
          eyebrow="Risk score"
          value={formatCompactNumber(totalRiskScore)}
          badge={`${highRiskCount} high risk`}
          badgeColor={highRiskCount > 0 ? WORKSPACE_RISK_COLOR : 'status.success.fg'}
        >
          <Box h="68px" mt="auto">
            <WorkspaceChart
              kind="single-area"
              data={chartData}
              dataKey="impact"
              xKey="label"
              color={WORKSPACE_RISK_COLOR}
              fill="workspaceRiskFill"
              yFloor={1}
            />
          </Box>
          <SimpleGrid columns={2} gap="2">
            <WorkspaceMiniStat
              label="Impacted signals"
              value={formatCompactNumber(opportunityRows.length)}
            />
            <WorkspaceMiniStat label="GA4 revenue" value={formatCurrency(ga4Revenue)} />
          </SimpleGrid>
        </WorkspaceKpiBody>
      )}
    </WorkspaceWidgetShell>
  );
}

function AiChannelMixWidget({
  visibilitySummary,
}: {
  visibilitySummary: ReturnType<typeof useWorkspaceVisibilitySummary>;
}) {
  const engineRows = useMemo(
    () =>
      (visibilitySummary.payload?.slices?.engineBreakdown || [])
        .map((row, index) => ({
          name: normalizeEngineLabel(row.engine),
          value: numeric(row.total_answers),
          mentions: numeric(row.mentioned_count),
          visibilityPct: numeric(row.visibility_pct),
          color: WORKSPACE_ENGINE_COLORS[index % WORKSPACE_ENGINE_COLORS.length],
        }))
        .filter(row => row.value > 0)
        .slice(0, 5),
    [visibilitySummary.payload?.slices?.engineBreakdown]
  );
  const totalAnswers = engineRows.reduce((sum, row) => sum + row.value, 0);
  const totalMentions = engineRows.reduce((sum, row) => sum + row.mentions, 0);

  return (
    <WorkspaceWidgetShell
      icon={<ChartPie />}
      title="AI Channel Mix"
      href="/app/visibility"
      actionLabel="Open Visibility"
    >
      {visibilitySummary.loading && engineRows.length === 0 ? (
        <WidgetLoading label="Loading channel mix…" />
      ) : visibilitySummary.error && engineRows.length === 0 ? (
        <WidgetMessage>{visibilitySummary.error}</WidgetMessage>
      ) : engineRows.length === 0 ? (
        <WidgetMessage>Run a scan to see where answers come from.</WidgetMessage>
      ) : (
        <VStack align="stretch" gap="3" p="4" minH="226px">
          <Box position="relative" h="126px">
            <WorkspaceChart
              kind="donut"
              data={engineRows}
              colors={engineRows.map(row => row.color)}
              nameKey="name"
              outerRadius="86%"
              paddingAngle={2}
            />
            <Center position="absolute" inset="0" pointerEvents="none">
              <VStack gap="0">
                <Text fontSize="xl" fontWeight="850" color="ui.text.primary">
                  {formatCompactNumber(totalAnswers)}
                </Text>
                <Text fontSize="10px" color="ui.text.tertiary" fontWeight="800">
                  answers
                </Text>
              </VStack>
            </Center>
          </Box>
          <VStack align="stretch" gap="1.5">
            {engineRows.map(row => (
              <HStack key={row.name} justify="space-between" gap="3">
                <HStack gap="2" minW={0}>
                  <Box boxSize="2.5" borderRadius="full" bg={row.color} flexShrink={0} />
                  <Text fontSize="xs" color="ui.text.secondary" fontWeight="750" lineClamp={1}>
                    {row.name}
                  </Text>
                </HStack>
                <Text fontSize="xs" color="ui.text.primary" fontWeight="850">
                  {formatPercent((row.value / totalAnswers) * 100)}
                </Text>
              </HStack>
            ))}
          </VStack>
          <WorkspaceMiniStat label="Brand mentions" value={formatCompactNumber(totalMentions)} />
        </VStack>
      )}
    </WorkspaceWidgetShell>
  );
}

function WorkspaceKpiBody({
  eyebrow,
  value,
  badge,
  badgeColor,
  children,
}: {
  eyebrow: string;
  value: string;
  badge: string;
  badgeColor: string;
  children: ReactNode;
}) {
  return (
    <VStack align="stretch" gap="3" p="4" minH="226px">
      <HStack justify="space-between" align="start" gap="3">
        <Box minW={0}>
          <Text
            fontSize="10px"
            color="ui.text.tertiary"
            fontWeight="900"
            textTransform="uppercase"
            letterSpacing="0.12em"
            lineClamp={1}
          >
            {eyebrow}
          </Text>
          <Text
            mt="1"
            fontSize={{ base: '2xl', md: '3xl' }}
            color="ui.text.primary"
            fontWeight="850"
            lineHeight="1"
            fontVariantNumeric="tabular-nums"
          >
            {value}
          </Text>
        </Box>
        <Text
          fontSize="xs"
          color={badgeColor}
          fontWeight="850"
          fontVariantNumeric="tabular-nums"
          whiteSpace="nowrap"
          pt="0.5"
        >
          {badge}
        </Text>
      </HStack>
      {children}
    </VStack>
  );
}

function WorkspaceMiniStat({ label, value }: { label: string; value: string }) {
  return (
    <Box
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="glassInput"
      bg="ui.surface.input"
      px="2.5"
      py="2"
      minW={0}
    >
      <Text fontSize="10px" color="ui.text.tertiary" fontWeight="850" lineClamp={1}>
        {label}
      </Text>
      <Text mt="0.5" fontSize="sm" color="ui.text.primary" fontWeight="850" lineClamp={1}>
        {value}
      </Text>
    </Box>
  );
}

function BrandVisibilityHomeWidget({
  user,
  projectId,
}: {
  user: User | null;
  projectId: string | null;
}) {
  const { currentProject, loading: projectLoading } = useProject();

  const brandSettings = currentProject?.brand_settings;
  const defaultBrandName = brandSettings?.brand_name?.trim() || currentProject?.name?.trim() || '';
  const defaultBrandSlug = brandSettings?.brand_slug?.trim() || defaultBrandName;
  const configuredBrandVariations = useMemo(
    () => brandSettings?.brand_variations || [],
    [brandSettings?.brand_variations]
  );
  const brandMergeGroups = useMemo(
    () => currentProject?.brand_merge_groups || [],
    [currentProject?.brand_merge_groups]
  );

  const projectDefaultBrand = useMemo(
    () =>
      buildConfiguredBrandGroups({
        brandName: defaultBrandName,
        projectBrandSlug: defaultBrandSlug,
        brandVariations: configuredBrandVariations,
        brandMergeGroups,
      }).find(group => group.isProjectDefaultBrand) || null,
    [brandMergeGroups, configuredBrandVariations, defaultBrandName, defaultBrandSlug]
  );

  const dashboardBrandGroups = useMemo(() => {
    const groups: Array<{ canonical_name: string; variations: string[] }> = [];
    if (defaultBrandName) {
      groups.push({
        canonical_name: defaultBrandName,
        variations: configuredBrandVariations.map(variation => variation.trim()).filter(Boolean),
      });
    }

    brandMergeGroups.forEach(group => {
      const canonicalName = group.canonical_name?.trim();
      if (!canonicalName) return;
      groups.push({
        canonical_name: canonicalName,
        variations: (group.variations || []).map(variation => variation.trim()).filter(Boolean),
      });
    });

    return groups;
  }, [brandMergeGroups, configuredBrandVariations, defaultBrandName]);

  const promptGroups = useMemo(
    () => projectTopicOptions(currentProject),
    [currentProject?.prompt_groups, currentProject?.topic_options]
  );
  const [selectedPromptGroupId, setSelectedPromptGroupId] = useState('');
  const [selectedPromptSubtopicId, setSelectedPromptSubtopicId] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<PromptTopicFilter | undefined>();
  const [customPromptIds, setCustomPromptIds] = useState<string[]>([]);
  const {
    prompts,
    promptsReady,
    dataProjectId: promptInventoryProjectId,
    error: promptsError,
    refresh: refreshPrompts,
  } = usePipelineFilterOptions({
    sections: ['prompts'],
  });
  const availablePrompts = useMemo(
    () => (promptInventoryProjectId === projectId ? prompts : []),
    [promptInventoryProjectId, projectId, prompts]
  );
  const promptInventoryReady = promptsReady && promptInventoryProjectId === projectId;
  const topicFilterResult = useMemo(
    () =>
      selectedTopicFilter
        ? resolvePromptTopicFilter(
            selectedTopicFilter,
            promptGroups,
            promptInventoryReady
              ? availablePrompts.map(prompt => prompt.prompt_id)
              : customPromptIds
          )
        : null,
    [selectedTopicFilter, promptGroups, customPromptIds, availablePrompts, promptInventoryReady]
  );
  const selectedPromptGroup = useMemo(
    () => promptGroups.find(group => group.id === selectedPromptGroupId) ?? null,
    [promptGroups, selectedPromptGroupId]
  );
  const selectedPromptIds = useMemo(
    () => (selectedPromptId ? [selectedPromptId] : topicFilterResult?.promptIds || []),
    [selectedPromptId, topicFilterResult]
  );

  const selectedPromptSubtopicExists = Boolean(
    selectedPromptGroup?.subtopics?.some(child => child.id === selectedPromptSubtopicId)
  );
  const promptScopeReady =
    (!promptTopicFilterNeedsInventory(selectedTopicFilter) || promptInventoryReady) &&
    (!selectedPromptGroupId || Boolean(selectedPromptGroup)) &&
    (!selectedPromptSubtopicId || selectedPromptSubtopicExists) &&
    (!topicFilterResult || (topicFilterResult.valid && topicFilterResult.promptIds.length > 0));

  useEffect(() => {
    if (selectedTopicFilter) return;
    if (selectedPromptGroupId && !selectedPromptGroup) {
      setSelectedPromptGroupId('');
      setSelectedPromptSubtopicId('');
      setSelectedTopicFilter(undefined);
      setSelectedPromptId('');
      setCustomPromptIds([]);
    } else if (selectedPromptSubtopicId && !selectedPromptSubtopicExists) {
      setSelectedPromptSubtopicId('');
    }
  }, [
    selectedPromptGroup,
    selectedPromptGroupId,
    selectedPromptSubtopicId,
    selectedPromptSubtopicExists,
    selectedTopicFilter,
  ]);

  const brandVariations = projectDefaultBrand?.brandVariations || [];
  const brandHref = defaultBrandSlug
    ? (promptScopeReady || Boolean(selectedTopicFilter)) &&
      (selectedPromptGroupId || selectedPromptSubtopicId || selectedTopicFilter || selectedPromptId)
      ? buildLeaderboardBrandHref(defaultBrandSlug, {
          weekSelection: 'all',
          promptGroupId: selectedPromptGroupId,
          promptGroupOrigin: topicOrigin(selectedPromptGroup),
          promptSubtopicId: selectedPromptSubtopicId,
          promptIds:
            selectedTopicFilter && !topicFilterResult?.valid ? customPromptIds : selectedPromptIds,
          topicFilter: selectedTopicFilter,
          promptId: selectedPromptId,
        })
      : `/app/ai-search/${encodeURIComponent(defaultBrandSlug)}`
    : '/app/ai-search/competitors';
  const leaderboardEnabled = Boolean(
    user &&
      projectId &&
      currentProject &&
      !projectLoading &&
      promptScopeReady &&
      defaultBrandName &&
      brandVariations.length > 0
  );
  const leaderboardParams = useMemo(
    () => ({
      enabled: leaderboardEnabled,
      brand_groups: dashboardBrandGroups,
      ...(promptScopeReady
        ? {
            prompt_group_id: selectedPromptGroupId || undefined,
            prompt_group_origin: topicOrigin(selectedPromptGroup),
            prompt_subtopic_id: selectedPromptSubtopicId || undefined,
            prompt_ids: selectedPromptIds.length ? selectedPromptIds : undefined,
          }
        : {}),
      limit: 200,
    }),
    [
      dashboardBrandGroups,
      leaderboardEnabled,
      promptScopeReady,
      selectedPromptIds,
      selectedPromptGroupId,
      selectedPromptSubtopicId,
      selectedPromptGroup,
    ]
  );

  const {
    brands: leaderboardBrands,
    timeSeries: leaderboardTimeSeries,
    loading,
    error,
  } = useBrandsPageDiscovery(leaderboardParams);

  const homeBrandRow = useMemo(() => {
    const matchSet = projectDefaultBrand?.matchSet;
    if (!matchSet) return null;
    return (
      leaderboardBrands.find(
        brand =>
          matchesBrandValue(String(brand.brand || ''), matchSet) ||
          matchesBrandValue(String(brand.brand_lowercase || ''), matchSet)
      ) || null
    );
  }, [leaderboardBrands, projectDefaultBrand?.matchSet]);

  const homeBrandTrendRows = useMemo<BrandHomeTrendRow[]>(() => {
    const matchSet = projectDefaultBrand?.matchSet;
    if (!matchSet) return [];
    return leaderboardTimeSeries.filter(
      row =>
        matchesBrandValue(String(row.brand || ''), matchSet) ||
        matchesBrandValue(String(row.brand_lowercase || ''), matchSet)
    );
  }, [leaderboardTimeSeries, projectDefaultBrand?.matchSet]);

  const trendPoints = useMemo(
    () => buildBrandHomeTrendPoints(homeBrandTrendRows),
    [homeBrandTrendRows]
  );
  const summary = useMemo(
    () => buildBrandHomeWidgetSummary(homeBrandRow, trendPoints),
    [homeBrandRow, trendPoints]
  );

  const visibilityMetric = useMemo<BrandHomeTrendMetric>(
    () => ({
      key: 'visibilityPct',
      label: 'Visibility',
      color: BRAND_VISIBILITY_COLOR,
      fill: 'workspaceBrandVisibilityFill',
      formatter: formatPercent,
      domain: [0, 100],
    }),
    []
  );
  const rank3Metric = useMemo<BrandHomeTrendMetric>(
    () => ({
      key: 'rank3Pct',
      label: 'Rank 1-3',
      color: BRAND_RANK3_COLOR,
      fill: 'workspaceBrandRank3Fill',
      formatter: formatPercent,
      domain: [0, 100],
    }),
    []
  );
  const rank1Metric = useMemo<BrandHomeTrendMetric>(
    () => ({
      key: 'rank1Pct',
      label: 'Rank 1',
      color: BRAND_RANK1_COLOR,
      fill: 'workspaceBrandRank1Fill',
      formatter: formatPercent,
      domain: [0, 100],
    }),
    []
  );
  const top3SovMetric = useMemo<BrandHomeTrendMetric>(
    () => ({
      key: 'top3SovPct',
      label: 'Top-3 SOV',
      color: BRAND_RANK3_COLOR,
      fill: 'workspaceBrandTop3SovFill',
      formatter: formatPercent,
      domain: [0, 100],
    }),
    []
  );
  const visibilityMetrics = useMemo(
    () => [visibilityMetric, rank3Metric, rank1Metric],
    [rank1Metric, rank3Metric, visibilityMetric]
  );
  const promptGroupFilter = (
    <PromptScopeFilter
      topics={promptGroups}
      topicId={selectedPromptGroupId}
      subtopicId={selectedPromptSubtopicId}
      promptId={selectedPromptId}
      topicFilter={selectedTopicFilter}
      prompts={availablePrompts}
      promptInventoryReady={promptsReady && promptInventoryProjectId === projectId}
      onChange={(topicId, promptId, subtopicId = '', filter) => {
        setSelectedPromptGroupId(topicId);
        setSelectedPromptSubtopicId(subtopicId);
        setSelectedPromptId(promptId);
        setSelectedTopicFilter(
          filter ? { include: filter.include, exclude: filter.exclude } : undefined
        );
        setCustomPromptIds(filter?.promptIds || []);
      }}
    />
  );

  return (
    <WorkspaceWidgetShell
      icon={<Activity />}
      title="Brand Visibility"
      href={brandHref}
      actionLabel="Open Brand"
      headerSlot={promptGroupFilter}
      pairedMetrics
    >
      {withPromptInventoryBoundary(
        <>
          {!defaultBrandName ? (
            <WidgetMessage>Choose a brand to start tracking progress.</WidgetMessage>
          ) : leaderboardEnabled && loading && !homeBrandRow ? (
            <BrandVisibilityWidgetLoading />
          ) : error && !homeBrandRow ? (
            <WidgetMessage>{error}</WidgetMessage>
          ) : !homeBrandRow ? (
            <WidgetMessage>
              No brand visibility data is available for these prompts yet.
            </WidgetMessage>
          ) : (
            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              minH="176px"
              h="full"
              css={{
                '@container app-content (max-width: 47.9975rem)': { gridTemplateColumns: '1fr' },
              }}
            >
              <BrandHomeMetricPanel
                eyebrow="Visibility"
                value={formatPercent(summary.visibilityPct)}
                badge={`${formatPercent(summary.rank3Pct)} Rank 1-3`}
                divided={false}
              >
                <BrandHomeTrendChart data={trendPoints} metrics={visibilityMetrics} />
                <VStack align="stretch" gap="2.5">
                  <BrandHomeProgressStat
                    label="Rank 1-3"
                    value={summary.rank3Pct}
                    color={BRAND_RANK3_COLOR}
                  />
                  <BrandHomeProgressStat
                    label="Rank 1"
                    value={summary.rank1Pct}
                    color={BRAND_RANK1_COLOR}
                  />
                </VStack>
              </BrandHomeMetricPanel>

              <BrandHomeMetricPanel
                eyebrow="Top-3 SOV"
                value={formatPercent(summary.top3SovPct)}
                badge={`${formatCompactNumber(summary.top3Count)} top-3`}
                divided
              >
                <BrandHomeTrendChart data={trendPoints} metrics={[top3SovMetric]} />
                <VStack align="stretch" gap="2.5">
                  <BrandHomeProgressStat
                    label="Total SOV"
                    value={summary.totalSovPct}
                    color={BRAND_SOV_COLOR}
                  />
                  <BrandHomeProgressStat
                    label="Top-3 SOV"
                    value={summary.top3SovPct}
                    color={BRAND_RANK3_COLOR}
                  />
                </VStack>
              </BrandHomeMetricPanel>
            </SimpleGrid>
          )}
        </>,
        {
          filter: selectedTopicFilter,
          ready: promptInventoryReady,
          error: promptsError,
          invalid: Boolean(
            topicFilterResult && (!topicFilterResult.valid || !topicFilterResult.promptIds.length)
          ),
          onRetry: refreshPrompts,
          onClear: () => {
            setSelectedTopicFilter(undefined);
            setCustomPromptIds([]);
            setSelectedPromptGroupId('');
            setSelectedPromptSubtopicId('');
            setSelectedPromptId('');
          },
        }
      )}
    </WorkspaceWidgetShell>
  );
}

function BrandVisibilityWidgetLoading() {
  return (
    <Box
      minH={{ base: '560px', sm: '280px' }}
      h="full"
      css={{
        '@container app-content (max-width: 47.9975rem)': { minHeight: '560px' },
      }}
    >
      <WidgetLoading label="Loading brand visibility…" />
    </Box>
  );
}

function BrandHomeMetricPanel({
  eyebrow,
  value,
  badge,
  divided,
  children,
}: {
  eyebrow: string;
  value: string;
  badge: string;
  divided: boolean;
  children: ReactNode;
}) {
  return (
    <VStack
      align="stretch"
      gap="2.5"
      px="4"
      py="4"
      minH="176px"
      h="full"
      borderLeftWidth={{ base: '0', sm: divided ? '1px' : '0' }}
      borderLeftColor="ui.border.subtle"
      borderTopWidth={{ base: divided ? '1px' : '0', sm: '0' }}
      borderTopColor="ui.border.subtle"
      css={{
        '@container app-content (max-width: 47.9975rem)': {
          borderLeftWidth: '0',
          borderTopWidth: divided ? '1px' : '0',
        },
      }}
    >
      <HStack justify="space-between" align="start" gap="2" flexWrap="wrap">
        <Box minW="max-content">
          <Text
            fontSize="xs"
            color="ui.text.tertiary"
            fontWeight="600"
            letterSpacing="0.01em"
            whiteSpace="nowrap"
          >
            {eyebrow}
          </Text>
          <Text
            fontSize="2xl"
            color="ui.text.primary"
            fontWeight="700"
            fontVariantNumeric="tabular-nums"
            lineHeight="1.05"
            mt="1"
            whiteSpace="nowrap"
          >
            {value}
          </Text>
        </Box>
        <Text
          fontSize="xs"
          color="ui.text.secondary"
          fontWeight="500"
          fontVariantNumeric="tabular-nums"
          whiteSpace="nowrap"
          pt="0.5"
        >
          {badge}
        </Text>
      </HStack>
      {children}
    </VStack>
  );
}

const BrandHomeTrendChart = memo(function BrandHomeTrendChart({
  data,
  metrics,
}: {
  data: BrandHomeTrendPoint[];
  metrics: BrandHomeTrendMetric[];
}) {
  const chartData = useMemo(
    () =>
      data.map(point => ({
        ...point,
        dateLabel: formatChartDate(point.date),
      })),
    [data]
  );
  const datesWithValues = new Set(
    chartData
      .filter(point =>
        metrics.some(metric => {
          const value = point[metric.key];
          return typeof value === 'number' && Number.isFinite(value);
        })
      )
      .map(point => point.date)
  );
  const hasTrend = datesWithValues.size >= 2;

  if (!hasTrend) {
    return (
      <Flex
        align="center"
        justify="center"
        h="68px"
        borderTopWidth="1px"
        borderColor="ui.border.subtle"
      >
        <Text fontSize="xs" color="ui.text.tertiary">
          Your trend starts after the next scan
        </Text>
      </Flex>
    );
  }

  return (
    <Box h="68px" mt="auto">
      <WorkspaceChart
        kind="multi-area"
        data={chartData}
        metrics={metrics}
        tooltip={<BrandHomeTrendTooltip metrics={metrics} />}
      />
    </Box>
  );
});

function BrandHomeTrendTooltip({
  active,
  payload,
  metrics,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    value?: number | string | null;
    payload?: BrandHomeTrendPoint & { dateLabel: string };
  }>;
  metrics: BrandHomeTrendMetric[];
}) {
  const point = payload?.[0]?.payload;
  const rows = (payload || [])
    .map(entry => {
      const metric = metrics.find(item => item.key === entry.dataKey);
      const value = Number(entry.value);
      return metric && Number.isFinite(value) ? { metric, value } : null;
    })
    .filter((entry): entry is { metric: BrandHomeTrendMetric; value: number } => Boolean(entry));
  if (!active || !point || rows.length === 0) return null;

  return (
    <Box
      bg="ui.surface.popover"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="glassInput"
      boxShadow="none"
      backdropFilter="blur(12px)"
      px="3"
      py="2.5"
      minW="152px"
    >
      <Text fontSize="xs" color="ui.text.tertiary" fontWeight="800" mb="1.5">
        {point.dateLabel}
      </Text>
      <VStack align="stretch" gap="1.5">
        {rows.map(({ metric, value }) => (
          <HStack key={metric.key} justify="space-between" gap="4">
            <HStack gap="1.5" minW="0">
              <Box boxSize="2" borderRadius="full" bg={metric.color} flexShrink={0} />
              <Text fontSize="xs" color="ui.text.secondary" lineClamp={1}>
                {metric.label}
              </Text>
            </HStack>
            <Text
              fontSize="xs"
              color="ui.text.primary"
              fontWeight="800"
              fontVariantNumeric="tabular-nums"
            >
              {metric.formatter(value)}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

function BrandHomeProgressStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Box>
      <HStack justify="space-between" gap="3" mb="1">
        <Text fontSize="10px" color="ui.text.tertiary" fontWeight="800" textTransform="uppercase">
          {label}
        </Text>
        <Text
          fontSize="xs"
          color="ui.text.secondary"
          fontWeight="800"
          fontVariantNumeric="tabular-nums"
        >
          {formatPercent(value)}
        </Text>
      </HStack>
      <Box h="6px" borderRadius="full" bg="ui.surface.selected" overflow="hidden">
        <Box h="full" w={`${clampPercent(value)}%`} bg={color} borderRadius="full" />
      </Box>
    </Box>
  );
}

function ShopifyRevenueWidget({ revenue }: { revenue: WorkspaceShopifyRevenueState }) {
  const badgeColor =
    revenue.changePct === null
      ? 'ui.text.tertiary'
      : revenue.changePct >= 0
        ? 'status.success.fg'
        : 'status.warning.fg';

  return (
    <WorkspaceWidgetShell
      icon={<ShoppingBag />}
      title="Shopify Revenue"
      bodyMinH="252px"
      href="/app/revenue"
      actionLabel="Open Revenue"
    >
      {revenue.loading ? (
        <WidgetLoading label="Loading Shopify revenue…" />
      ) : revenue.error ? (
        <WidgetMessage>{revenue.error}</WidgetMessage>
      ) : (
        <WorkspaceKpiBody
          eyebrow="Last 30 days"
          value={formatShopifyRevenue(revenue.totalSales, revenue.currencyCode)}
          badge={formatShopifyRevenueChange(revenue.changePct)}
          badgeColor={badgeColor}
        >
          <Box h="74px" mt="auto">
            {revenue.trend.some(item => item.totalSales > 0) ? (
              <WorkspaceChart
                kind="shopify-area"
                data={revenue.trend}
                valueFormatter={value => formatShopifyRevenue(numeric(value), revenue.currencyCode)}
              />
            ) : (
              <Flex align="center" justify="center" h="full" color="ui.text.tertiary">
                <Text fontSize="xs">No revenue trend in this period.</Text>
              </Flex>
            )}
          </Box>
          <SimpleGrid columns={2} gap="2">
            <WorkspaceMiniStat label="Orders" value={formatCompactNumber(revenue.orders)} />
            <WorkspaceMiniStat
              label="Average order"
              value={formatShopifyRevenue(revenue.averageOrderValue, revenue.currencyCode)}
            />
          </SimpleGrid>
        </WorkspaceKpiBody>
      )}
    </WorkspaceWidgetShell>
  );
}

function TrafficImpressionsWidget({
  performance,
}: {
  performance: ReturnType<typeof useWorkspaceWebsitePerformance>;
}) {
  const chartData = useMemo<TrafficChartPoint[]>(
    () =>
      (performance.payload?.trend || []).map(row => ({
        date: formatChartDate(row.date),
        sessions: numeric(row.ga4_sessions),
        impressions: numeric(row.gsc_impressions) + numeric(row.bing_impressions),
      })),
    [performance.payload?.trend]
  );

  const totals = useMemo(
    () =>
      chartData.reduce(
        (acc, row) => ({
          sessions: acc.sessions + row.sessions,
          impressions: acc.impressions + row.impressions,
        }),
        { sessions: 0, impressions: 0 }
      ),
    [chartData]
  );

  return (
    <WorkspaceWidgetShell
      icon={<BarChart3 />}
      title="Website traffic"
      pairedMetrics
      href="/app/pages/overview"
      actionLabel="Open Analytics"
      headerSlot={
        <Text fontSize="xs" color="ui.text.tertiary" mr="2">
          Last 30 days
        </Text>
      }
    >
      {performance.loading ? (
        <WidgetLoading label="Loading analytics…" />
      ) : performance.error ? (
        <WidgetMessage>{performance.error}</WidgetMessage>
      ) : chartData.length === 0 ? (
        <WidgetMessage>
          No website analytics are available for the last 30 days. Check your connected sources.
        </WidgetMessage>
      ) : (
        <SimpleGrid
          columns={{ base: 1, sm: 2 }}
          minH="176px"
          h="full"
          css={{
            '@container app-content (max-width: 47.9975rem)': { gridTemplateColumns: '1fr' },
          }}
        >
          {TRAFFIC_METRICS.map((metric, index) => (
            <WorkspaceMetricTrend
              key={metric.key}
              metric={metric}
              data={chartData}
              total={totals[metric.key]}
              divided={index > 0}
            />
          ))}
        </SimpleGrid>
      )}
    </WorkspaceWidgetShell>
  );
}

function CloudflareCrawlerWidget({
  crawlerActivity,
  projectId,
}: {
  crawlerActivity: ReturnType<typeof useWorkspaceCrawlerActivity>;
  projectId: string | null;
}) {
  const { payload } = crawlerActivity;
  const chartData = useMemo<CloudflareCrawlerChartPoint[]>(
    () =>
      (payload?.crawlerBreakdowns?.trend || []).map(row => ({
        date: formatChartDate(row.date),
        requests: numeric(row.requests),
        activeCrawlers: numeric(row.crawler_count),
      })),
    [payload?.crawlerBreakdowns?.trend]
  );

  const totals = useMemo(() => {
    const fallback = chartData.reduce(
      (acc, row) => ({
        requests: acc.requests + row.requests,
        activeCrawlers: Math.max(acc.activeCrawlers, row.activeCrawlers),
      }),
      { requests: 0, activeCrawlers: 0 }
    );
    const totalRequests = payload?.totals?.requests;
    const totalCrawlerCount = payload?.totals?.crawler_count;
    return {
      requests: totalRequests == null ? fallback.requests : numeric(totalRequests),
      activeCrawlers:
        totalCrawlerCount == null ? fallback.activeCrawlers : numeric(totalCrawlerCount),
    };
  }, [chartData, payload?.totals]);
  const topCrawlers = useMemo(
    () =>
      (payload?.crawlerBreakdowns?.crawlers || [])
        .filter(row => numeric(row.requests) > 0)
        .slice(0, 3),
    [payload?.crawlerBreakdowns?.crawlers]
  );

  const reportParams = new URLSearchParams({ range: '30d' });
  if (projectId) reportParams.set('projectId', projectId);

  return (
    <WorkspaceWidgetShell
      icon={<Cloud />}
      title="Crawler Signals"
      href={`/app/pages/analytics/cloudflare?${reportParams.toString()}`}
      actionLabel="Open Cloudflare"
    >
      {crawlerActivity.loading ? (
        <WidgetLoading label="Loading crawler activity…" />
      ) : crawlerActivity.error ? (
        <WidgetMessage>{crawlerActivity.error}</WidgetMessage>
      ) : chartData.length === 0 ? (
        <WidgetMessage>Connect Cloudflare to see AI crawler activity.</WidgetMessage>
      ) : (
        <VStack align="stretch" gap="3" p="4" minH="252px" h="full">
          <SimpleGrid columns={2} gap="2">
            {CLOUDFLARE_CRAWLER_METRICS.map(metric => (
              <WorkspaceMiniStat
                key={metric.key}
                label={metric.label}
                value={formatCompactNumber(totals[metric.key])}
              />
            ))}
          </SimpleGrid>
          <Box h="86px">
            <WorkspaceChart
              kind="single-area"
              data={chartData}
              dataKey="requests"
              xKey="date"
              color="var(--chakra-colors-status-info-fg)"
              fill="workspaceCrawlerSignalsFill"
              yFloor={1}
            />
          </Box>
          {topCrawlers.length > 0 ? (
            <VStack align="stretch" gap="1.5">
              {topCrawlers.map(row => (
                <HStack
                  key={`${row.crawler_operator}-${row.crawler_name}`}
                  justify="space-between"
                  gap="3"
                >
                  <Text fontSize="xs" color="ui.text.secondary" fontWeight="800" lineClamp={1}>
                    {row.crawler_name || row.crawler_operator || 'Unknown crawler'}
                  </Text>
                  <Text fontSize="xs" color="ui.text.primary" fontWeight="850">
                    {formatCompactNumber(row.requests)}
                  </Text>
                </HStack>
              ))}
            </VStack>
          ) : null}
        </VStack>
      )}
    </WorkspaceWidgetShell>
  );
}

function VisibilityOverTimeWidget({ brand }: { brand: WorkspaceHomeBrandState }) {
  const chartData = useMemo(
    () =>
      brand.trendPoints.map(point => ({
        ...point,
        dateLabel: formatChartDate(point.date),
      })),
    [brand.trendPoints]
  );
  const hasTrend =
    chartData.filter(point => point.visibilityPct > 0 || point.top3SovPct > 0).length >= 2;

  return (
    <WorkspaceWidgetShell
      icon={<TrendingUp />}
      title="Visibility Over Time"
      href={brand.brandHref}
      actionLabel="Open Brand"
    >
      {!brand.hasConfiguredBrand ? (
        <WidgetMessage>Choose a brand to start tracking progress.</WidgetMessage>
      ) : brand.loading && !brand.hasData ? (
        <WidgetLoading label="Loading visibility trend…" />
      ) : brand.error && !brand.hasData ? (
        <WidgetMessage>{brand.error}</WidgetMessage>
      ) : !hasTrend ? (
        <WidgetMessage>Run another scan to start your visibility trend.</WidgetMessage>
      ) : (
        <VStack align="stretch" gap="3" p="4" minH="252px" h="full">
          <HStack gap="4" flexWrap="wrap">
            <HStack gap="1.5">
              <Box boxSize="2.5" borderRadius="full" bg={BRAND_VISIBILITY_COLOR} />
              <Text fontSize="xs" color="ui.text.secondary" fontWeight="800">
                Visibility
              </Text>
            </HStack>
            <HStack gap="1.5">
              <Box boxSize="2.5" borderRadius="full" bg="var(--chakra-colors-status-warning-fg)" />
              <Text fontSize="xs" color="ui.text.secondary" fontWeight="800">
                Top-3 SOV
              </Text>
            </HStack>
          </HStack>
          <Box h="176px">
            <WorkspaceChart
              kind="visibility-area"
              data={chartData}
              visibilityColor={BRAND_VISIBILITY_COLOR}
              tooltip={
                <BrandHomeTrendTooltip
                  metrics={[
                    {
                      key: 'visibilityPct',
                      label: 'Visibility',
                      color: BRAND_VISIBILITY_COLOR,
                      fill: 'workspaceVisibilityTrendFill',
                      formatter: formatPercent,
                      domain: [0, 100],
                    },
                    {
                      key: 'top3SovPct',
                      label: 'Top-3 SOV',
                      color: 'var(--chakra-colors-status-warning-fg)',
                      fill: 'workspaceTop3TrendFill',
                      formatter: formatPercent,
                      domain: [0, 100],
                    },
                  ]}
                />
              }
            />
          </Box>
        </VStack>
      )}
    </WorkspaceWidgetShell>
  );
}

function TopConsiderationLeaksWidget({
  opportunities,
  opportunityRows,
}: {
  opportunities: ReturnType<typeof useWorkspaceOpportunities>;
  opportunityRows: WebsiteOpportunityRow[];
}) {
  const rows = opportunityRows.slice(0, 5);

  return (
    <WorkspaceWidgetShell
      icon={<Search />}
      title="Top Consideration Leaks"
      href="/app/workspace?mode=tasks&tab=tasks"
      actionLabel="View all gaps"
    >
      {opportunities.loading && rows.length === 0 ? (
        <WidgetLoading label="Loading opportunities…" />
      ) : opportunities.error && rows.length === 0 ? (
        <WidgetMessage>{opportunities.error}</WidgetMessage>
      ) : rows.length === 0 ? (
        <WidgetMessage>All clear—no consideration leaks in this range.</WidgetMessage>
      ) : (
        <Box overflowX="auto">
          <Box minW="980px">
            <Grid
              templateColumns="1.1fr 1fr 2.8fr 0.7fr 0.8fr 1.5fr"
              gap="3"
              px="4"
              py="2.5"
              borderBottomWidth="1px"
              borderColor="ui.border.subtle"
            >
              {['Theme', 'Leak type', 'Evidence', 'Impact', 'Risk', 'Opportunity'].map(label => (
                <Text
                  key={label}
                  fontSize="10px"
                  color="ui.text.tertiary"
                  fontWeight="900"
                  textTransform="uppercase"
                  letterSpacing="0.12em"
                >
                  {label}
                </Text>
              ))}
            </Grid>
            {rows.map(row => {
              const severity = opportunitySeverity(row.severity);
              const evidence = row.evidence?.[0] || row.suggestedAction || row.title || '';
              return (
                <Grid
                  key={row.id || `${row.title}-${row.affected}`}
                  templateColumns="1.1fr 1fr 2.8fr 0.7fr 0.8fr 1.5fr"
                  gap="3"
                  alignItems="center"
                  px="4"
                  py="3"
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                  _last={{ borderBottomWidth: '0' }}
                >
                  <Text fontSize="sm" color="ui.text.primary" fontWeight="850" lineClamp={1}>
                    {row.affected || row.title || 'Opportunity'}
                  </Text>
                  <Badge
                    borderRadius="glassPill"
                    bg="ui.surface.selected"
                    color="ui.text.secondary"
                    w="fit-content"
                  >
                    {opportunityLeakType(row.title)}
                  </Badge>
                  <Text fontSize="sm" color="ui.text.secondary" lineClamp={2}>
                    {evidence}
                  </Text>
                  <Text
                    fontSize="sm"
                    color="ui.text.primary"
                    fontWeight="850"
                    fontVariantNumeric="tabular-nums"
                  >
                    {formatCompactNumber(row.impact)}
                  </Text>
                  <StatusBadge tone={severityTone(severity)}>{severity}</StatusBadge>
                  <Text fontSize="sm" color="ui.text.secondary" lineClamp={2}>
                    {row.suggestedAction || 'Review gap'}
                  </Text>
                </Grid>
              );
            })}
          </Box>
        </Box>
      )}
    </WorkspaceWidgetShell>
  );
}

function WorkspaceWidgetShell({
  icon,
  title,
  href,
  actionLabel,
  headerSlot,
  children,
  pairedMetrics = false,
  bodyMinH = '156px',
}: {
  icon: ReactNode;
  title: string;
  href: string;
  actionLabel: string;
  headerSlot?: ReactNode;
  children: ReactNode;
  pairedMetrics?: boolean;
  bodyMinH?: string;
}) {
  return (
    <Box
      data-section="workspaceHomeWidget"
      layerStyle="surfacePanel"
      bg="ui.surface.card"
      boxShadow="none"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="12px"
      overflow="hidden"
      minH="0"
      h="full"
      display="flex"
      flexDirection="column"
    >
      <Flex
        justify="space-between"
        align="center"
        px="4"
        py="2.5"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        gap="1.5"
        flexWrap="wrap"
        flexShrink={0}
      >
        <HStack gap="2" minW={0} flex="1">
          <Icon boxSize="3.5" color="ui.text.secondary" flexShrink={0}>
            {icon}
          </Icon>
          <Text fontSize="sm" color="ui.text.primary" fontWeight="600" lineClamp={1}>
            {title}
          </Text>
        </HStack>
        <HStack gap="1" flexShrink={0}>
          {headerSlot}
          <ChakraLink
            as={NextLink}
            href={href}
            display="inline-flex"
            alignItems="center"
            minH="28px"
            px="1.5"
            borderRadius="glassInput"
            fontSize="xs"
            color="ui.text.secondary"
            whiteSpace="nowrap"
            _hover={{ color: 'ui.text.primary', bg: 'ui.surface.selected', textDecoration: 'none' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'ui.border.strong',
              outlineOffset: '2px',
            }}
          >
            {actionLabel}
          </ChakraLink>
        </HStack>
      </Flex>
      <Box
        flex="1"
        minH={pairedMetrics ? { base: '560px', sm: '280px' } : bodyMinH}
        css={
          pairedMetrics
            ? {
                '@container app-content (max-width: 47.9975rem)': { minHeight: '560px' },
              }
            : undefined
        }
      >
        {children}
      </Box>
    </Box>
  );
}

function WorkspaceMetricTrend<Key extends string>({
  metric,
  data,
  total,
  divided,
}: {
  metric: WorkspaceTrendMetric<Key>;
  data: Array<{ date: string } & Record<Key, number>>;
  total: number;
  divided: boolean;
}) {
  const firstPoint = data[0];
  const lastPoint = data[data.length - 1];

  return (
    <VStack
      align="stretch"
      gap="2"
      px="4"
      py="4"
      minH="176px"
      h="full"
      borderLeftWidth={{ base: '0', sm: divided ? '1px' : '0' }}
      borderLeftColor="ui.border.subtle"
      borderTopWidth={{ base: divided ? '1px' : '0', sm: '0' }}
      borderTopColor="ui.border.subtle"
      css={{
        '@container app-content (max-width: 47.9975rem)': {
          borderLeftWidth: '0',
          borderTopWidth: divided ? '1px' : '0',
        },
      }}
    >
      <HStack justify="space-between" align="start" gap="3">
        <Box minW="0">
          <Text
            fontSize="xs"
            color="ui.text.tertiary"
            fontWeight="600"
            letterSpacing="0.01em"
            lineClamp={1}
          >
            {metric.label}
          </Text>
          <Text
            fontSize="2xl"
            color="ui.text.primary"
            fontWeight="700"
            fontVariantNumeric="tabular-nums"
            lineHeight="1.05"
            mt="1"
          >
            {formatCompactNumber(total)}
          </Text>
        </Box>
      </HStack>

      <Box h="74px" mt="auto">
        {data.length > 1 ? (
          <WorkspaceChart
            kind="metric-area"
            data={data}
            metric={metric}
            tooltip={<WorkspaceTrendTooltip metric={metric} />}
            xKey="date"
            yFloor={1}
          />
        ) : (
          <Flex
            align="center"
            justify="center"
            h="full"
            borderTopWidth="1px"
            borderColor="ui.border.subtle"
          >
            <Text fontSize="xs" color="ui.text.tertiary">
              More daily data is needed to show a trend
            </Text>
          </Flex>
        )}
      </Box>

      <HStack justify="space-between" color="ui.text.tertiary" fontSize="xs" fontWeight="700">
        <Text>{firstPoint?.date ?? ''}</Text>
        <Text>{lastPoint?.date ?? ''}</Text>
      </HStack>
    </VStack>
  );
}

function WorkspaceTrendTooltip<Key extends string>({
  active,
  payload,
  metric,
}: {
  active?: boolean;
  payload?: Array<{ value?: number | string; payload?: { date: string } & Record<Key, number> }>;
  metric: WorkspaceTrendMetric<Key>;
}) {
  const point = payload?.[0]?.payload;
  const value = payload?.[0]?.value;
  if (!active || !point || value === undefined || value === null) return null;

  return (
    <Box
      bg="ui.surface.popover"
      borderWidth="1px"
      borderColor="ui.border.default"
      borderRadius="glassInput"
      boxShadow="none"
      backdropFilter="blur(12px)"
      px="3"
      py="2.5"
      minW="152px"
    >
      <Text fontSize="xs" color="ui.text.tertiary" fontWeight="800" mb="1.5">
        {point.date}
      </Text>
      <HStack justify="space-between" gap="4">
        <HStack gap="1.5" minW="0">
          <Box boxSize="2" borderRadius="full" bg={metric.color} flexShrink={0} />
          <Text fontSize="xs" color="ui.text.secondary" lineClamp={1}>
            {metric.label}
          </Text>
        </HStack>
        <Text
          fontSize="xs"
          color="ui.text.primary"
          fontWeight="800"
          fontVariantNumeric="tabular-nums"
        >
          {formatCompactNumber(value)}
        </Text>
      </HStack>
    </Box>
  );
}

function WidgetMessage({ children }: { children: ReactNode }) {
  return (
    <Flex align="center" justify="center" minH="156px" h="full" p="4">
      <Text fontSize="sm" color="ui.text.secondary" textAlign="center">
        {children}
      </Text>
    </Flex>
  );
}

function WidgetLoading({ label }: { label: string }) {
  return (
    <VStack
      role="status"
      align="center"
      justify="center"
      minH="156px"
      h="full"
      gap="2.5"
      p="4"
      aria-busy="true"
    >
      <InlineSpinner decorative />
      <Text fontSize="xs" color="ui.text.tertiary" textAlign="center">
        {label}
      </Text>
    </VStack>
  );
}

type OnboardingChatTarget = {
  projectId?: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
};

function getProjectOnboardingTarget({
  project,
  userId,
  manageableWorkspaces,
}: {
  project: ReturnType<typeof useProject>['currentProject'];
  userId?: string | null;
  manageableWorkspaces: ReturnType<typeof useProject>['manageableWorkspaces'];
}): OnboardingChatTarget | null {
  if (!project || isProjectWorkspaceReady(project)) return null;

  const personalWorkspaceId = userId ? `personal_${userId}` : null;
  const workspaceId =
    project.workspace_id && project.workspace_id !== personalWorkspaceId
      ? project.workspace_id
      : null;
  const manageableWorkspace = workspaceId
    ? manageableWorkspaces.find(workspace => workspace.id === workspaceId) || null
    : null;
  if (workspaceId && !manageableWorkspace) return null;

  return {
    projectId: project.id,
    workspaceId,
    workspaceName: manageableWorkspace?.name || null,
  };
}

function buildOnboardingSessionPromptText(
  onboardingChatTarget: OnboardingChatTarget | null,
  projectIdOverride?: string | null
): string {
  if (!onboardingChatTarget) return '';
  const projectId =
    typeof projectIdOverride === 'string' && projectIdOverride.trim().length > 0
      ? projectIdOverride.trim()
      : onboardingChatTarget.projectId || null;
  return [
    projectId
      ? `Continue onboarding for existing project id: ${projectId}. Use this pre-created project shell; do not create a duplicate project unless the user explicitly asks to start a different brand.`
      : 'New project onboarding conversation. The UI must create a pre-created project shell before starting the chat session.',
    onboardingChatTarget.workspaceId
      ? `Target workspace id: ${onboardingChatTarget.workspaceId}`
      : 'Target workspace id: personal workspace if project creation needs one.',
    onboardingChatTarget.workspaceName
      ? `Target workspace name: ${onboardingChatTarget.workspaceName}`
      : null,
    'Use the normal research chat harness and hosted Attune MCP tools. Do not use the legacy onboarding harness.',
  ]
    .filter(Boolean)
    .join('\n');
}

function getCreatedReportReceiptId(message: Pick<Message, 'toolUsage'>): string | null {
  for (const tool of message.toolUsage || []) {
    const createdReportId = tool.metadata?.createdReportId;
    if (typeof createdReportId === 'string' && /^report-[A-Za-z0-9_-]+$/.test(createdReportId)) {
      return createdReportId;
    }
  }
  return null;
}

class StaleCreatedDocReceiptError extends Error {
  constructor() {
    super('The previously created Doc is no longer available.');
    this.name = 'StaleCreatedDocReceiptError';
  }
}

function WorkspaceStartupLoadingShell() {
  return <PageLoader variant="section" minH="100%" />;
}

export function WorkspacePageClient(
  props: {
    routeMode?: WorkspaceMode;
    routeAgentTab?: BackgroundTaskTab;
  } = {}
) {
  return (
    <WorkspaceSetupEntry>
      <WorkspacePageContent {...props} />
    </WorkspaceSetupEntry>
  );
}

function WorkspacePageContent({
  routeMode,
  routeAgentTab,
}: {
  routeMode?: WorkspaceMode;
  routeAgentTab?: BackgroundTaskTab;
} = {}) {
  const { user } = useAuth();
  const {
    selectReport: selectLivingDoc,
    getActiveReportId,
    getWebsitePreviewContextSnapshot,
    getWebsitePreviewContextReference,
    clearWebsitePreviewSelection,
    getCanvasContextSnapshot,
    getCanvasContextReference,
    clearCanvasSelection,
  } = useLivingDocsPanelActions();
  const {
    activeCanvas: composingCanvas,
    open: editorPanelOpen,
    view: editorPanelView,
  } = useLivingDocsPanel();
  const {
    currentProject,
    projects,
    manageableWorkspaces,
    canCreatePersonalProject,
    loading: projectLoading,
    isReady: projectReady,
    fetchProjects,
    switchProject,
    createOnboardingProjectShell,
  } = useProject();
  const router = useRouter();
  const isDesktop = useDesktopViewport();
  const openLivingDoc = useCallback(
    (reportId: string) => {
      if (isDesktop) {
        selectLivingDoc(reportId);
        return;
      }
      router.push(`/app/docs/${encodeURIComponent(reportId)}`);
    },
    [isDesktop, router, selectLivingDoc]
  );
  const openCreatedLivingDoc = useCallback(
    async (reportId: string) => {
      if (!user) throw new Error('Sign in to open the created Doc.');
      const token = await user.getIdToken();
      const response = await fetch(`/api/reports/${encodeURIComponent(reportId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = (await response.json().catch(() => null)) as {
        report?: { id?: string; status?: string; content?: { content?: unknown[] } };
        error?: string;
      } | null;
      if (response.status === 404) throw new StaleCreatedDocReceiptError();
      const report = payload?.report;
      if (
        !response.ok ||
        report?.id !== reportId ||
        report.status !== 'ready' ||
        !Array.isArray(report.content?.content) ||
        report.content.content.length === 0
      ) {
        throw new Error(payload?.error || 'The Doc was not ready to open.');
      }
      openLivingDoc(reportId);
    },
    [openLivingDoc, user]
  );
  const searchParams = useSearchParams();
  const detailRequestRef = useRef(0);
  const issuesRequestRef = useRef(0);
  const issuesFirstPageLoadingRef = useRef(false);
  const loadedGrowthTaskListQueryKeyRef = useRef<string | null>(null);
  const feedRequestRef = useRef(0);
  const automationsRequestRef = useRef(0);
  const scanRunsRequestRef = useRef(0);
  const deliveryConnectionsRequestRef = useRef(0);
  const requestedWorkspaceId = searchParams.get('workspace_id')?.trim() || null;
  const onboardingResetToken = searchParams.get('reset')?.trim() || null;
  const onboardingWorkspaceIntent = getOnboardingWorkspaceIntent({
    newProjectRequested: searchParams.get('new_project') === 'true',
    continueOnboardingRequested: searchParams.get('onboarding_chat') === 'true',
    resetRequested: Boolean(onboardingResetToken),
  });
  const onboardingWorkspaceRequested = onboardingWorkspaceIntent !== null;
  const forceNewProjectOnboarding = onboardingWorkspaceIntent === 'create';
  const sessionDeepLinkId = searchParams.get('session') || searchParams.get('sessionId');
  const issueDeepLinkId = searchParams.get('issue');
  const northStarDeepLink = searchParams.get('northStar') === '1';
  const cmoSetupDeepLink = searchParams.get('cmoSetup') === '1';
  const urlPromptId = searchParams.get('promptId');
  const urlJobId = searchParams.get('jobId') || searchParams.get('job_id');
  const urlJobIds = (searchParams.get('jobIds') || searchParams.get('job_ids') || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);
  const urlEngine = searchParams.get('engine');
  const urlQuestion = searchParams.get('question');
  const urlSkill = searchParams.get('skill');
  const urlComposerSkill = searchParams.get('composer_skill');
  const urlComposerDraft = searchParams.get('composer_draft') || '';
  const urlContext = searchParams.get('ctx');
  const urlMode = searchParams.get('mode');
  const workspaceUrlSearch = searchParams.toString();
  const urlTaskTab = searchParams.get('tab');
  const urlAgentTemplateId = searchParams.get('template');
  const urlBackgroundTaskRequested = searchParams.get('background_task') === 'true';
  const urlApprovalId = searchParams.get('approvalId');
  const approvalProjectId =
    urlApprovalId || searchParams.get('taskId') ? searchParams.get('projectId') : null;
  useEffect(() => {
    if (
      approvalProjectId &&
      approvalProjectId !== currentProject?.id &&
      projects.some(project => project.id === approvalProjectId)
    ) {
      switchProject(approvalProjectId, { preserveRoute: true });
    }
  }, [approvalProjectId, currentProject?.id, projects, switchProject]);
  const urlEditor = searchParams.get('editor');
  const activeEditor =
    urlEditor === 'canvas' || urlEditor === 'website' || urlEditor === 'design-system'
      ? urlEditor
      : null;
  const newChatRequested = searchParams.get('new_chat') === 'true';
  const session = useResearchSession({
    sessionId: sessionDeepLinkId,
    promptId: urlPromptId,
    jobId: urlJobId,
    jobIds: urlJobIds.length > 0 ? urlJobIds : null,
    engine: urlEngine,
    question: urlQuestion,
    context: urlContext,
    mode: urlMode,
    loadSessionList: false,
  });
  const modelSelection = useResearchModelSelection({ apiCall: session.apiCall });
  const setSelectedResearchModelId = modelSelection.setSelectedModelId;
  const [executionMode, setExecutionMode] = useState<ResearchExecutionMode>('standard');
  const [workspaceComposerIntent, setWorkspaceComposerIntent] = useState<WorkspaceComposerIntent>(
    routeMode === 'agents' ? 'background_task' : 'research'
  );
  const [backgroundTaskDraft, setBackgroundTaskDraft] = useState('');
  const [growthPlanComposerDraft, setGrowthPlanComposerDraft] = useState('');
  const onboardingModels = useMemo(
    () => onboardingResearchModels(modelSelection.models),
    [modelSelection.models]
  );
  const onboardingSelectedModel = useMemo(
    () => onboardingSelectedResearchModel(modelSelection.models, modelSelection.selectedModelId),
    [modelSelection.models, modelSelection.selectedModelId]
  );
  const shouldUseOnboardingModel =
    session.mode === 'onboarding' ||
    urlMode === 'onboarding' ||
    onboardingWorkspaceRequested ||
    forceNewProjectOnboarding;
  const activeResearchModelId = shouldUseOnboardingModel
    ? onboardingSelectedModel?.id || ''
    : modelSelection.selectedModelId;
  const reasoningEffortModelIds = useMemo(
    () =>
      modelSelection.models.filter(model => model.supportsReasoningEffort).map(model => model.id),
    [modelSelection.models]
  );
  const chat = useResearchChat({
    sessionId: session.sessionId,
    runtimeOwnerKey: user?.uid || null,
    messages: session.messages,
    apiCall: session.apiCall,
    selectedModelId: activeResearchModelId,
    reasoningEffortModelIds,
    setMessages: session.setMessages,
    setSessionUpdatedAt: session.setSessionUpdatedAt,
    setContextUsage: session.setContextUsage,
    setSessionName: session.setSessionName,
    getActiveReportId,
    getWebsitePreviewContextSnapshot,
    getWebsitePreviewContextReference,
    clearWebsitePreviewSelection,
    getCanvasContextSnapshot,
    getCanvasContextReference,
    clearCanvasSelection,
    canvasEditingActive: activeEditor === 'canvas',
    designSystemEditingActive: activeEditor === 'design-system',
  });
  useEffect(() => {
    setExecutionMode('standard');
  }, [session.sessionId]);
  const handleRetryMessage = useCallback(
    (messageId: string) => {
      void chat.retryMessage(messageId);
    },
    [chat.retryMessage]
  );

  const aiCmoBetaEnabled = isAiCmoBetaEnabled(currentProject);
  // Task Approvals is the external-delivery queue. AI CMO/Growth Program
  // decisions stay isolated to the feature-gated Growth Plan surface.
  const [workspaceMode, setWorkspaceModeState] = useState<WorkspaceMode>(() =>
    workspaceModeFromUrl(urlMode)
  );
  const mode = routeMode ?? workspaceMode;
  const setMode = useCallback(
    (nextMode: WorkspaceMode) => {
      if (!routeMode) setWorkspaceModeState(nextMode);
    },
    [routeMode]
  );
  const [growthWorkTab, setGrowthWorkTab] = useState<GrowthWorkTab>(() =>
    growthWorkTabFromUrl(urlTaskTab)
  );
  const [growthTaskListQuery, setGrowthTaskListQuery] = useState<GrowthTaskListQuery>(
    defaultGrowthTaskListQuery
  );
  const [backgroundTaskTabState, setBackgroundTaskTabState] = useState<BackgroundTaskTab>('tasks');
  const backgroundTaskTab = routeAgentTab ?? backgroundTaskTabState;
  const setBackgroundTaskTab = useCallback(
    (nextTab: BackgroundTaskTab) => {
      if (!routeAgentTab) setBackgroundTaskTabState(nextTab);
    },
    [routeAgentTab]
  );
  const [issues, setIssues] = useState<GrowthIssueRecord[]>([]);
  const [loadedGrowthWorkTab, setLoadedGrowthWorkTab] = useState<GrowthIssueListTab | null>(null);
  const loadedGrowthWorkTabRef = useRef<GrowthIssueListTab | null>(null);
  const [growthWorkLoadErrorTab, setGrowthWorkLoadErrorTab] = useState<GrowthIssueListTab | null>(
    null
  );
  const [issuePagination, setIssuePagination] = useState<GrowthIssuePaginationRecord | null>(null);
  const [growthSummary, setGrowthSummary] = useState<GrowthInboxSummaryRecord | null>(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [homeTaskIssues, setHomeTaskIssues] = useState<GrowthIssueRecord[]>([]);
  const [homeTaskIssuesLoading, setHomeTaskIssuesLoading] = useState(true);
  const [homeIssuesError, setHomeIssuesError] = useState(false);
  const [homeActivityError, setHomeActivityError] = useState(false);
  const [loadingMoreIssues, setLoadingMoreIssues] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<GrowthIssueDetail | null>(null);
  const [linkedRun, setLinkedRun] = useState<ScanRunRecord | null>(null);
  const [verificationRun, setVerificationRun] = useState<ScanRunRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [templates, setTemplates] = useState<ScanAutomationTemplate[]>([]);
  const [prompts, setPrompts] = useState<ScanPromptTemplate[]>([]);
  const [automations, setAutomations] = useState<ScanAutomationRecord[]>([]);
  const [homeAiCmoStatus, setHomeAiCmoStatus] = useState<{
    projectId: string | null;
    configured: boolean | null | undefined;
    error: boolean;
  }>({ projectId: null, configured: undefined, error: false });
  const [scanRuns, setScanRuns] = useState<ScanRunRecord[]>([]);
  const [scanRunsLoading, setScanRunsLoading] = useState(false);
  const [scanRunStatusFilter, setScanRunStatusFilter] = useState<ScanRunStatus | ''>('');
  const [lastRuns, setLastRuns] = useState<Record<string, ScanRunRecord | null>>({});
  const [northStar, setNorthStar] = useState<NorthStarDocumentRecord | null>(null);
  const [deliveryConnections, setDeliveryConnections] = useState<AgentDeliveryConnections>({
    webflow: false,
    shopify: false,
    wordpress: false,
    github: false,
    customMcp: false,
    loading: false,
  });
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] = useState<string | null>(null);
  const [automationStatusFilter, setAutomationStatusFilter] = useState<AutomationStatus | ''>('');
  const [automationTriggerFilter, setAutomationTriggerFilter] = useState<TriggerType | ''>('');
  const [feedEvents, setFeedEvents] = useState<GrowthActivityEventRecord[]>([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [busyAgent, setBusyAgent] = useState<AgentBusyState>(null);
  const [runNowSourceJobId, setRunNowSourceJobId] = useState('');
  const [editRunNowSourceJobId, setEditRunNowSourceJobId] = useState('');
  const [taskSlackDelivery, setTaskSlackDelivery] = useState<TaskSlackDeliveryInput | null>(null);
  const [taskSlackDeliveryBaseline, setTaskSlackDeliveryBaseline] =
    useState<TaskSlackDeliveryInput | null>(null);
  const taskSlackDeliveryChanged =
    JSON.stringify(taskSlackDelivery) !== JSON.stringify(taskSlackDeliveryBaseline);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editAutomationOpen, setEditAutomationOpen] = useState(false);
  const [editingAutomationId, setEditingAutomationId] = useState<string | null>(null);
  const [editingModelId, setEditingModelId] = useState('');
  const [composerMode, setComposerMode] = useState<ComposerMode>('preset');
  const [selectedTemplateId, setSelectedTemplateId] = useState('growth_orchestrator_task_v1');
  const [automationName, setAutomationName] = useState('');
  const [automationDescription, setAutomationDescription] = useState('');
  const [customPromptBody, setCustomPromptBody] = useState(DEFAULT_CUSTOM_PROMPT);
  const [triggerType, setTriggerType] = useState<TriggerType>('daily_scan');
  const [delayMinutes, setDelayMinutes] = useState(60);
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(1);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);
  const [scheduleSecondDayOfMonth, setScheduleSecondDayOfMonth] = useState(15);
  const [dataSources, setDataSources] = useState<ScanDataSource[]>(['ai_search']);
  const [prepared, setPrepared] = useState<PreparedAutomation | null>(null);
  const [preparedDraftInput, setPreparedDraftInput] = useState<Record<string, unknown> | null>(
    null
  );
  const [agentContractOpen, setAgentContractOpen] = useState(false);
  const [northStarOpen, setNorthStarOpen] = useState(false);
  const [northStarTitle, setNorthStarTitle] = useState('COMPANY.md');
  const [northStarBody, setNorthStarBody] = useState('');
  const [preparedNorthStar, setPreparedNorthStar] = useState<PreparedNorthStar | null>(null);
  const [northStarBusy, setNorthStarBusy] = useState<'prepare' | 'confirm' | null>(null);
  const [pendingInitialMessage, setPendingInitialMessage] =
    useState<PendingWorkspaceMessage | null>(null);
  const [composerDocumentRestoration, setComposerDocumentRestoration] =
    useState<ComposerDocumentRestoration | null>(null);
  const [loadingWorkspaceSession, setLoadingWorkspaceSession] = useState(false);
  const [unavailableSessionId, setUnavailableSessionId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [forkingSharedSession, setForkingSharedSession] = useState(false);
  const [turningIntoReportMessageId, setTurningIntoReportMessageId] = useState<string | null>(null);
  const [sessionDeepLinkRetryTick, setSessionDeepLinkRetryTick] = useState(0);
  const sessionDeepLinkHandledRef = useRef<string | null>(null);
  const sessionDeepLinkProjectSwitchedRef = useRef<string | null>(null);
  const activeWorkspaceSessionLoadRef = useRef<string | null>(null);
  const suppressedSessionDeepLinkRef = useRef<string | null>(null);
  const sessionDeepLinkRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const issueDeepLinkHandledRef = useRef<string | null>(null);
  const pendingIssueTabResolutionRef = useRef<string | null>(null);
  const northStarDeepLinkHandledRef = useRef(false);
  const questionDeepLinkHandledRef = useRef<string | null>(null);
  const homeTaskIssuesRequestRef = useRef(0);
  const homeDashboardRequestRef = useRef<{
    projectId: string;
    promise: Promise<void>;
  } | null>(null);
  const homeDashboardSnapshotRef = useRef({ issues: '', activity: '' });
  const newChatRequestHandledRef = useRef(false);
  const onboardingResetHandledRef = useRef<string | null>(null);
  const freshOnboardingEntryHandledRef = useRef(false);
  const onboardingExitStartedRef = useRef<string | null>(null);
  const previousActiveChatSessionCountRef = useRef(0);
  const onboardingFlowIdentityRef = useRef(0);
  const onboardingShellCreateRef = useRef<{
    scopeKey: string;
    promise: Promise<string | null>;
  } | null>(null);
  const onboardingShellScopeRef = useRef<string | null>(null);
  const onboardingShellProjectRef = useRef<{
    scopeKey: string;
    projectId: string;
  } | null>(null);
  const pendingCreatedDocSourceMessageRef = useRef<{
    sourceMessageId: string;
    messageCountAtStart: number;
  } | null>(null);

  const projectId = currentProject?.id || null;
  const homeAiCmoConfigured =
    homeAiCmoStatus.projectId === projectId ? homeAiCmoStatus.configured : undefined;
  const homeAiCmoStatusError =
    homeAiCmoStatus.projectId === projectId ? homeAiCmoStatus.error : false;
  const handleHomeAiCmoStatusResolved = useCallback(
    (configured: boolean) => {
      setHomeAiCmoStatus(current =>
        current.projectId === projectId &&
        current.configured === configured &&
        current.error === false
          ? current
          : { projectId, configured, error: false }
      );
    },
    [projectId]
  );
  const handleHomeAiCmoStatusLoadError = useCallback(() => {
    setHomeAiCmoStatus(current => {
      if (current.projectId === projectId && current.configured !== undefined) return current;
      if (current.projectId === projectId && current.error) return current;
      return { projectId, configured: undefined, error: true };
    });
  }, [projectId]);

  useEffect(() => {
    const controller = new AbortController();
    if (!aiCmoBetaEnabled || !user || !projectId) {
      setHomeAiCmoStatus({ projectId, configured: null, error: false });
      return () => controller.abort();
    }

    setHomeAiCmoStatus(current =>
      current.projectId === projectId ? current : { projectId, configured: undefined, error: false }
    );
    void (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/growth-programs/home-status', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectId }),
          signal: controller.signal,
        });
        const payload = (await response.json().catch(() => ({}))) as {
          configured?: boolean;
          error?: string;
        };
        if (!response.ok) throw new Error(payload.error || 'Unable to load Growth Plan status');
        if (!controller.signal.aborted) {
          handleHomeAiCmoStatusResolved(payload.configured === true);
        }
      } catch {
        if (!controller.signal.aborted) handleHomeAiCmoStatusLoadError();
      }
    })();

    return () => controller.abort();
  }, [
    aiCmoBetaEnabled,
    handleHomeAiCmoStatusLoadError,
    handleHomeAiCmoStatusResolved,
    projectId,
    user,
  ]);
  const workspaceProjectReady = projectReady && !projectLoading && Boolean(projectId);
  const workspaceProjectIdRef = useRef(projectId);
  const workspaceShellTelemetryRef = useRef<string | null>(null);
  const projectName =
    currentProject?.name || currentProject?.brand_settings?.brand_name || 'Attune';

  useEffect(() => {
    if (!workspaceProjectReady || !projectId) return;
    if (workspaceShellTelemetryRef.current === projectId) return;
    workspaceShellTelemetryRef.current = projectId;

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const hardWorkspaceNavigation = navigation
      ? !workspaceHardNavigationTimingCaptured &&
        new URL(navigation.name).pathname === '/app/workspace'
      : false;
    if (hardWorkspaceNavigation) workspaceHardNavigationTimingCaptured = true;
    capture('workspace_shell_visible', {
      project_id: projectId,
      entry_mode: urlMode || (sessionDeepLinkId ? 'thread-deeplink' : 'chat'),
      navigation_type: hardWorkspaceNavigation ? navigation?.type || 'navigate' : 'spa',
      page_load_ms: hardWorkspaceNavigation ? Math.round(performance.now()) : null,
    });
  }, [projectId, sessionDeepLinkId, urlMode, workspaceProjectReady]);
  const handleTurnIntoReport = useCallback(
    async (messageId: string) => {
      if (
        !user ||
        !projectId ||
        !session.sessionId ||
        chat.isLoading ||
        turningIntoReportMessageId
      ) {
        return;
      }
      const sourceMessage = session.messages.find(message => message.id === messageId);
      const existingDocId = sourceMessage ? getCreatedReportReceiptId(sourceMessage) : null;
      if (existingDocId) {
        try {
          await openCreatedLivingDoc(existingDocId);
          pendingCreatedDocSourceMessageRef.current = null;
          toast.success('Opened the existing doc.');
          return;
        } catch (error) {
          if (!(error instanceof StaleCreatedDocReceiptError)) {
            toast.error(error instanceof Error ? error.message : 'Failed to open the existing doc');
            return;
          }
        }
      }
      setTurningIntoReportMessageId(messageId);
      pendingCreatedDocSourceMessageRef.current = {
        sourceMessageId: messageId,
        messageCountAtStart: session.messages.length,
      };
      try {
        await chat.sendMessageDirect(
          `Create a new polished Attune Doc from assistant message ${messageId}. Retrieve that exact message with get_session_transcript using message_id, preserve all substantive content and sources, and use docs.create with sourceMessageId ${messageId} and native typed blocks. Choose the right document form—report, article, brief, or plan—based on the source. This button click is the user's explicit instruction: create the Doc now without asking for confirmation. Return the Doc link when finished.`,
          undefined,
          [{ skillId: LIVING_REPORT_BUILDER_SKILL_ID }]
        );
      } catch (error) {
        pendingCreatedDocSourceMessageRef.current = null;
        toast.error(error instanceof Error ? error.message : 'Failed to create doc');
      } finally {
        setTurningIntoReportMessageId(null);
      }
    },
    [
      chat.isLoading,
      chat.sendMessageDirect,
      openCreatedLivingDoc,
      projectId,
      session.sessionId,
      session.messages,
      turningIntoReportMessageId,
      user,
    ]
  );

  const currentProjectOnboardingTarget = useMemo(
    () =>
      getProjectOnboardingTarget({
        project: currentProject,
        userId: user?.uid || null,
        manageableWorkspaces,
      }),
    [currentProject, manageableWorkspaces, user?.uid]
  );

  useEffect(() => {
    loadedGrowthWorkTabRef.current = loadedGrowthWorkTab;
  }, [loadedGrowthWorkTab]);
  const onboardingChatTarget: OnboardingChatTarget | null = useMemo(() => {
    if (!sessionDeepLinkId || session.mode !== 'onboarding') return null;
    if (forceNewProjectOnboarding) {
      return getHybridOnboardingTarget({
        hasProject: projects.length > 0,
        forceNewProject: true,
        canCreatePersonalProject,
        manageableWorkspaces,
        requestedWorkspaceId,
      });
    }
    if (onboardingWorkspaceIntent === 'continue') {
      const requestedWorkspace = requestedWorkspaceId
        ? manageableWorkspaces.find(workspace => workspace.id === requestedWorkspaceId) || null
        : null;
      return {
        projectId:
          session.mode === 'onboarding' && session.sessionProjectId
            ? session.sessionProjectId
            : null,
        workspaceId: requestedWorkspace?.id || null,
        workspaceName: requestedWorkspace?.name || null,
      };
    }
    if (currentProjectOnboardingTarget) {
      return currentProjectOnboardingTarget;
    }
    if (currentProject) return null;
    return getHybridOnboardingTarget({
      hasProject: projects.length > 0,
      forceNewProject: false,
      canCreatePersonalProject,
      manageableWorkspaces,
      requestedWorkspaceId,
    });
  }, [
    canCreatePersonalProject,
    currentProject,
    currentProjectOnboardingTarget,
    forceNewProjectOnboarding,
    manageableWorkspaces,
    onboardingWorkspaceIntent,
    projects.length,
    requestedWorkspaceId,
    session.mode,
    session.sessionProjectId,
    sessionDeepLinkId,
  ]);
  const liveOnboardingCompletionReceipt = useMemo(() => {
    const timelineTools = chat.streamTimeline.filter(
      (item): item is Extract<TraceTimelineItem, { type: 'tool' }> => item.type === 'tool'
    );
    return getOnboardingCompletionReceipt(
      [...chat.activeTools, ...timelineTools],
      session.sessionProjectId
    );
  }, [chat.activeTools, chat.streamTimeline, session.sessionProjectId]);
  const persistedOnboardingCompletionReceipt = useMemo(
    () =>
      getOnboardingCompletionReceipt(
        session.messages.flatMap(message => message.toolUsage || []),
        session.sessionProjectId
      ),
    [session.messages, session.sessionProjectId]
  );
  const workspaceExportsEnabled = isGoogleWorkspaceExportDeliveryEnabledClient();
  const sessionTitle = session.sessionName || session.promptText || 'New chat';
  const isWorkspaceViewer = session.accessRole === 'workspace_viewer';
  const selectedIssue = useMemo(
    () => issues.find(issue => issue.id === selectedIssueId) || null,
    [issues, selectedIssueId]
  );
  const workspaceTaskIssues = useMemo(() => issues, [issues]);
  const visibleAutomations = useMemo(
    () =>
      automations.filter(automation => {
        if (automation.setup_pending) return false;
        if (automationStatusFilter && automation.status !== automationStatusFilter) return false;
        if (automationTriggerFilter && automation.trigger.type !== automationTriggerFilter) {
          return false;
        }
        return true;
      }),
    [automationStatusFilter, automationTriggerFilter, automations]
  );
  const selectedAutomation = useMemo(
    () =>
      visibleAutomations.find(automation => automation.id === selectedAutomationId) ||
      visibleAutomations[0] ||
      null,
    [selectedAutomationId, visibleAutomations]
  );
  const editingAutomation = useMemo(
    () => automations.find(automation => automation.id === editingAutomationId) || null,
    [automations, editingAutomationId]
  );
  const editingModels = useMemo(
    () => researchModelsWithPersistedCurrent(modelSelection.models, editingModelId),
    [editingModelId, modelSelection.models]
  );
  const editingSelectedModel = useMemo(
    () => editingModels.find(model => model.id === editingModelId) || null,
    [editingModelId, editingModels]
  );
  const editingDraftTrigger = useMemo<ScanAutomationRecord['trigger']>(
    () => ({
      type: triggerType,
      delay_minutes: delayMinutes,
      ...(triggerType === 'weekly_scan' ? { day_of_week: scheduleDayOfWeek } : {}),
      ...(triggerType === 'twice_monthly_scan'
        ? {
            days_of_month: normalizeTwiceMonthlyDays([
              scheduleDayOfMonth,
              scheduleSecondDayOfMonth,
            ]),
          }
        : {}),
      ...(triggerType === 'monthly_scan' ? { day_of_month: scheduleDayOfMonth } : {}),
    }),
    [delayMinutes, scheduleDayOfMonth, scheduleDayOfWeek, scheduleSecondDayOfMonth, triggerType]
  );
  const editingRunTriggerType = editingAutomation?.program_context?.specialist_id
    ? editingAutomation.trigger.type
    : triggerType;
  const selectedRun = selectedAutomation ? lastRuns[selectedAutomation.id] || null : null;
  const activeChatSessionKey = chat.activeSessionIds.join('|');
  const refreshOpenThread = useCallback(async () => {
    if (!session.sessionId) return;
    await session.loadSessionById(session.sessionId, { redirectOnError: false });
  }, [session.loadSessionById, session.sessionId]);
  const promptById = useMemo(() => {
    const next = new Map<string, ScanPromptTemplate>();
    for (const prompt of prompts) next.set(prompt.id, prompt);
    return next;
  }, [prompts]);
  const visibleTemplates = useMemo(
    () => templates.filter(template => !isLegacyAutomationTemplate(template)),
    [templates]
  );
  const selectedTemplate =
    visibleTemplates.find(template => template.id === selectedTemplateId) || visibleTemplates[0];
  const selectedTemplatePrompt = selectedTemplate
    ? promptById.get(selectedTemplate.prompt_template_id)
    : null;
  const executionConnectionsReady =
    deliveryConnections.webflow ||
    deliveryConnections.shopify ||
    deliveryConnections.wordpress ||
    deliveryConnections.github ||
    deliveryConnections.customMcp;
  const selectedTemplateLocked = Boolean(
    composerMode === 'preset' &&
      selectedTemplate &&
      isExecutionAgent(selectedTemplate.kind) &&
      !executionConnectionsReady
  );
  const marketplaceSections = useMemo(
    () => [
      {
        category: 'monitoring' as AgentMarketplaceCategory,
        title: 'Monitoring Tasks',
        description: 'Find insights in data and turn them into work orders.',
        templates: visibleTemplates.filter(
          template => agentMarketplaceCategory(template.kind) === 'monitoring'
        ),
      },
      {
        category: 'execution' as AgentMarketplaceCategory,
        title: 'Execution Tasks',
        description: 'Take work orders and execute them through connected delivery tools.',
        templates: visibleTemplates.filter(
          template => agentMarketplaceCategory(template.kind) === 'execution'
        ),
      },
    ],
    [visibleTemplates]
  );
  const onboardingNorthStarDraft =
    (
      currentProject as
        | {
            analysis_settings?: {
              north_star_draft?: { title?: string; bodyMarkdown?: string };
            };
          }
        | null
        | undefined
    )?.analysis_settings?.north_star_draft || null;
  const onboardingBlueprint =
    (
      currentProject as
        | {
            analysis_settings?: {
              onboarding_blueprint?: { north_star_thesis?: string };
            };
          }
        | null
        | undefined
    )?.analysis_settings?.onboarding_blueprint || null;
  const scheduledScanDate =
    triggerType === 'daily_scan'
      ? nextDateForDaily()
      : triggerType === 'weekly_scan'
        ? nextDateForWeekday(scheduleDayOfWeek)
        : triggerType === 'twice_monthly_scan'
          ? nextDateForTwiceMonthly(
              normalizeTwiceMonthlyDays([scheduleDayOfMonth, scheduleSecondDayOfMonth])
            )
          : triggerType === 'monthly_scan'
            ? nextDateForMonthDay(scheduleDayOfMonth)
            : null;
  const replaceWorkspaceSearch = useCallback(
    (mutate: (query: URLSearchParams) => void) => {
      const query = new URLSearchParams(window.location.search);
      mutate(query);
      const suffix = query.toString();
      window.history.replaceState(
        null,
        '',
        `${routeMode === 'agents' ? '/app/agents' : '/app/workspace'}${suffix ? `?${suffix}` : ''}`
      );
      window.dispatchEvent(new Event(WORKSPACE_URL_CHANGE_EVENT));
    },
    [routeMode]
  );
  useEffect(() => {
    const pendingCreate = pendingCreatedDocSourceMessageRef.current;
    if (!pendingCreate) return;
    const postSourceModelMessages = session.messages
      .slice(pendingCreate.messageCountAtStart)
      .filter(message => message.role === 'model');
    const reportId = [...postSourceModelMessages]
      .reverse()
      .map(getCreatedReportReceiptId)
      .find((value): value is string => Boolean(value));
    if (!reportId) {
      if (postSourceModelMessages.length > 0) pendingCreatedDocSourceMessageRef.current = null;
      return;
    }
    pendingCreatedDocSourceMessageRef.current = null;
    void openCreatedLivingDoc(reportId).catch(error => {
      toast.error(error instanceof Error ? error.message : 'The Doc was not ready to open.');
    });
  }, [openCreatedLivingDoc, session.messages]);
  const clearWorkspaceSearchParams = useCallback(
    (keys: string[]) => {
      replaceWorkspaceSearch(query => {
        for (const key of keys) query.delete(key);
      });
    },
    [replaceWorkspaceSearch]
  );
  const setWorkspaceSessionSearch = useCallback(
    (sessionId: string) => {
      replaceWorkspaceSearch(query => {
        query.set('session', sessionId);
        if (query.get('new_project') === 'true') {
          query.delete('new_project');
          query.delete('reset');
          query.set('onboarding_chat', 'true');
        }
        for (const key of CHAT_SESSION_QUERY_KEYS_TO_CLEAR) {
          query.delete(key);
        }
      });
    },
    [replaceWorkspaceSearch]
  );
  const setWorkspaceMode = useCallback(
    (nextMode: WorkspaceMode) => {
      if (routeMode) {
        router.push(workspaceModeHref(nextMode), { scroll: false });
        return;
      }
      setMode(nextMode);
      replaceWorkspaceSearch(query => {
        if (nextMode === 'chat') {
          query.delete('mode');
          query.delete('tab');
          return;
        }
        query.set('mode', nextMode);
        if (nextMode !== 'tasks' && nextMode !== 'agents') query.delete('tab');
      });
    },
    [replaceWorkspaceSearch, routeMode, router, setMode]
  );
  const setWorkspaceTaskSearch = useCallback(
    (tab: GrowthWorkTab, issueId?: string | null) => {
      if (routeMode) {
        const query = new URLSearchParams({ mode: 'tasks', tab });
        if (issueId) query.set('issue', issueId);
        router.push(`/app/workspace?${query.toString()}`);
        return;
      }
      replaceWorkspaceSearch(query => {
        query.set('mode', 'tasks');
        query.set('tab', tab);
        query.delete('new_chat');
        query.delete('command');
        query.delete('question');
        query.delete('approvalId');
        if (issueId) {
          query.set('issue', issueId);
        } else {
          query.delete('issue');
        }
      });
    },
    [replaceWorkspaceSearch, routeMode, router]
  );
  const setWorkspaceBackgroundTaskSearch = useCallback(
    (tab: BackgroundTaskTab) => {
      if (routeMode === 'agents') {
        router.push(getAgentPageHref(tab), { scroll: false });
        return;
      }
      setBackgroundTaskTab(tab);
      replaceWorkspaceSearch(query => {
        query.set('mode', 'agents');
        query.set('tab', tab);
        query.delete('new_chat');
        query.delete('command');
        query.delete('question');
      });
    },
    [replaceWorkspaceSearch, routeMode, router, setBackgroundTaskTab]
  );
  const selectGrowthIssue = useCallback(
    (issueOrId: GrowthIssueRecord | string, options: { openDrawer?: boolean } = {}) => {
      const issue =
        typeof issueOrId === 'string'
          ? workspaceTaskIssues.find(item => item.id === issueOrId) || null
          : issueOrId;
      const issueId = typeof issueOrId === 'string' ? issueOrId : issueOrId.id;
      const nextTab = issue ? growthWorkTabForIssue(issue) : 'tasks';
      issueDeepLinkHandledRef.current = issueId;
      setMode('tasks');
      setWorkspaceTaskSearch(nextTab, issueId);
      if (issue) {
        pendingIssueTabResolutionRef.current = null;
        setGrowthWorkTab(nextTab);
      } else {
        pendingIssueTabResolutionRef.current = issueId;
        setGrowthWorkTab('tasks');
      }
      setSelectedIssueId(issueId);
      if (options.openDrawer) setTaskDrawerOpen(true);
    },
    [setMode, setWorkspaceTaskSearch, workspaceTaskIssues]
  );
  const handleWorkspaceSessionLoadError = useCallback(
    (sessionId: string, error: unknown) => {
      const activeSessionId = session.sessionId;
      sessionDeepLinkHandledRef.current = sessionId;
      activeWorkspaceSessionLoadRef.current = null;
      setLoadingWorkspaceSession(false);
      setPendingInitialMessage(null);
      if (isTransientWorkspaceRefreshError(error)) {
        if (activeSessionId) {
          suppressedSessionDeepLinkRef.current = null;
          sessionDeepLinkHandledRef.current = activeSessionId;
          setWorkspaceSessionSearch(activeSessionId);
        } else {
          suppressedSessionDeepLinkRef.current = null;
          sessionDeepLinkHandledRef.current = sessionId;
          if (!sessionDeepLinkRetryTimerRef.current) {
            sessionDeepLinkRetryTimerRef.current = setTimeout(() => {
              sessionDeepLinkRetryTimerRef.current = null;
              if (sessionDeepLinkHandledRef.current === sessionId) {
                sessionDeepLinkHandledRef.current = null;
                setSessionDeepLinkRetryTick(current => current + 1);
              }
            }, 5000);
          }
        }
        toast.error(formatWorkspaceRefreshError(error, 'Failed to load session'));
        return;
      }
      suppressedSessionDeepLinkRef.current = sessionId;
      setUnavailableSessionId(sessionId);
      setShareDialogOpen(false);
      session.resetSession({ skipNavigation: true, preserveCurrentSession: false });
    },
    [session, setWorkspaceSessionSearch]
  );
  const handleUnavailableSessionDismiss = useCallback(() => {
    setUnavailableSessionId(null);
    sessionDeepLinkHandledRef.current = null;
    sessionDeepLinkProjectSwitchedRef.current = null;
    activeWorkspaceSessionLoadRef.current = null;
    suppressedSessionDeepLinkRef.current = null;
    setLoadingWorkspaceSession(false);
    session.resetSession({ skipNavigation: true, preserveCurrentSession: false });
    clearWorkspaceSearchParams(['session', 'sessionId', 'question']);
  }, [clearWorkspaceSearchParams, session]);

  useEffect(() => {
    if (!newChatRequested) {
      newChatRequestHandledRef.current = false;
      return;
    }
    if (newChatRequestHandledRef.current) return;

    newChatRequestHandledRef.current = true;
    setMode('chat');
    setPendingInitialMessage(null);
    sessionDeepLinkHandledRef.current = null;
    activeWorkspaceSessionLoadRef.current = null;
    suppressedSessionDeepLinkRef.current = null;
    setLoadingWorkspaceSession(false);
    setUnavailableSessionId(null);
    session.resetSession({ skipNavigation: true });
    clearWorkspaceSearchParams(['new_chat']);
  }, [clearWorkspaceSearchParams, newChatRequested, session, setMode]);

  useEffect(() => {
    const previousProjectId = workspaceProjectIdRef.current;
    if (previousProjectId === projectId) {
      return;
    }

    workspaceProjectIdRef.current = projectId;
    if (!previousProjectId) {
      return;
    }

    setPendingInitialMessage(null);
    setGrowthPlanComposerDraft('');
    setIssues([]);
    setHomeTaskIssues([]);
    setHomeTaskIssuesLoading(true);
    setHomeIssuesError(false);
    setHomeActivityError(false);
    homeTaskIssuesRequestRef.current += 1;
    homeDashboardRequestRef.current = null;
    homeDashboardSnapshotRef.current = { issues: '', activity: '' };
    loadedGrowthWorkTabRef.current = null;
    setLoadedGrowthWorkTab(null);
    setGrowthWorkLoadErrorTab(null);
    issuesFirstPageLoadingRef.current = false;
    loadedGrowthTaskListQueryKeyRef.current = null;
    setIssuesLoading(false);
    setLoadingMoreIssues(false);
    setIssuePagination(null);
    setGrowthSummary(null);
    setGrowthWorkTab('tasks');
    setGrowthTaskListQuery(defaultGrowthTaskListQuery());
    setSelectedIssueId(null);
    setTaskDrawerOpen(false);
    setDetail(null);
    setLinkedRun(null);
    setVerificationRun(null);
    setAutomations([]);
    setLastRuns({});
    setFeedEvents([]);
    setFeedLoading(true);
    setNorthStar(null);
    setDeliveryConnections({
      webflow: false,
      shopify: false,
      wordpress: false,
      github: false,
      customMcp: false,
      loading: false,
    });
    activeWorkspaceSessionLoadRef.current = null;
    setLoadingWorkspaceSession(false);
    const loadedDeepLinkMatchesProject =
      Boolean(sessionDeepLinkId) &&
      session.sessionId === sessionDeepLinkId &&
      (session.sessionProjectId === projectId ||
        (session.sessionScope?.kind === 'workspace' &&
          session.sessionScope.workspaceId === currentProject?.workspace_id));
    if (loadedDeepLinkMatchesProject) {
      suppressedSessionDeepLinkRef.current = null;
      sessionDeepLinkHandledRef.current = sessionDeepLinkId;
      return;
    }
    session.cancelSessionLoad();
    session.resetSession({ skipNavigation: true, preserveCurrentSession: false });
    suppressedSessionDeepLinkRef.current = null;
    sessionDeepLinkHandledRef.current = null;
    sessionDeepLinkProjectSwitchedRef.current = null;
    setUnavailableSessionId(null);
    clearWorkspaceSearchParams(['session', 'sessionId', 'question']);
  }, [
    clearWorkspaceSearchParams,
    handleWorkspaceSessionLoadError,
    projectId,
    session,
    sessionDeepLinkId,
  ]);

  const postAttune = useCallback(
    async <T,>(path: string, body: Record<string, unknown> = {}): Promise<T> => {
      if (!user || !projectId) throw new Error('Project is not ready');
      const token = await user.getIdToken();
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId, ...body }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw createAttuneClientRequestError(response, payload);
      return payload as T;
    },
    [projectId, user]
  );

  const loadHomeDashboardData = useCallback(() => {
    if (!user || !projectId || projectLoading) return Promise.resolve();
    const activeRequest = homeDashboardRequestRef.current;
    if (activeRequest?.projectId === projectId) return activeRequest.promise;
    const requestId = ++homeTaskIssuesRequestRef.current;
    const requestProjectId = projectId;
    const isLatestRequest = () =>
      homeTaskIssuesRequestRef.current === requestId &&
      workspaceProjectIdRef.current === requestProjectId;
    setHomeTaskIssuesLoading(true);
    setFeedLoading(true);
    const promise = (async () => {
      const startedAt = performance.now();
      let outcome: 'success' | 'error' = 'success';
      let issueCount = 0;
      let activityCount = 0;
      try {
        const payload = await postAttune<{
          issues?: { rows?: GrowthIssueRecord[] };
          activity?: { rows?: GrowthActivityEventRecord[] };
        }>('/api/attune/growth-inbox/home');
        if (!isLatestRequest()) return;
        setHomeIssuesError(!payload.issues);
        setHomeActivityError(!payload.activity);
        if (payload.issues) {
          const nextIssues = payload.issues.rows || [];
          issueCount = nextIssues.length;
          const issuesSnapshot = JSON.stringify(nextIssues);
          if (issuesSnapshot !== homeDashboardSnapshotRef.current.issues) {
            homeDashboardSnapshotRef.current.issues = issuesSnapshot;
            setHomeTaskIssues(nextIssues);
          }
        }
        if (payload.activity) {
          const nextEvents = payload.activity.rows || [];
          activityCount = nextEvents.length;
          const activitySnapshot = JSON.stringify(nextEvents);
          if (activitySnapshot !== homeDashboardSnapshotRef.current.activity) {
            homeDashboardSnapshotRef.current.activity = activitySnapshot;
            setFeedEvents(nextEvents);
          }
        }
      } catch {
        outcome = 'error';
        if (isLatestRequest()) {
          setHomeIssuesError(true);
          setHomeActivityError(true);
        }
        // Preserve the last successful data when the combined Home request is unavailable.
      } finally {
        if (isLatestRequest()) {
          setHomeTaskIssuesLoading(false);
          setFeedLoading(false);
          capture('workspace_home_data_loaded', {
            project_id: requestProjectId,
            outcome,
            duration_ms: Math.round(performance.now() - startedAt),
            issue_count: issueCount,
            activity_count: activityCount,
          });
        }
      }
    })().finally(() => {
      if (homeDashboardRequestRef.current?.promise === promise) {
        homeDashboardRequestRef.current = null;
      }
    });
    homeDashboardRequestRef.current = { projectId, promise };
    return promise;
  }, [postAttune, projectId, projectLoading, user]);

  const loadIssuesForTab = useCallback(
    async (tab: GrowthIssueListTab, mode: WorkspaceRefreshMode = 'interactive') => {
      if (!user || !projectId || projectLoading) return;
      const statuses = statusesForGrowthWorkTab(tab);
      if (!statuses) return;
      const requestId = ++issuesRequestRef.current;
      const requestProjectId = projectId;
      const requestQueryKey = growthTaskListQueryKey(growthTaskListQuery);
      const isLatestRequest = () =>
        issuesRequestRef.current === requestId &&
        workspaceProjectIdRef.current === requestProjectId;
      issuesFirstPageLoadingRef.current = true;
      setLoadingMoreIssues(false);
      setIssuePagination(null);
      setIssuesLoading(true);
      if (mode === 'interactive') {
        setGrowthWorkLoadErrorTab(null);
      }
      try {
        const payload = await postAttune<{
          rows?: GrowthIssueRecord[];
          summary?: GrowthInboxSummaryRecord;
          pagination?: GrowthIssuePaginationRecord;
        }>('/api/attune/growth-inbox/list', {
          limit: 250,
          offset: 0,
          includeStats: false,
          statuses,
          compact: true,
          severity:
            growthTaskListQuery.riskFilter === 'all' ? undefined : growthTaskListQuery.riskFilter,
          updatedFrom:
            growthTaskListQuery.dateRange.key === 'all'
              ? undefined
              : growthTaskListQuery.dateRange.startDate,
          updatedTo:
            growthTaskListQuery.dateRange.key === 'all'
              ? undefined
              : growthTaskListQuery.dateRange.endDate,
          sortField: growthTaskListQuery.sortField,
          sortDirection: growthTaskListQuery.sortDirection,
        });
        if (!isLatestRequest()) return;
        setIssues(payload.rows || []);
        loadedGrowthTaskListQueryKeyRef.current = requestQueryKey;
        loadedGrowthWorkTabRef.current = tab;
        setLoadedGrowthWorkTab(tab);
        setGrowthWorkLoadErrorTab(null);
        setGrowthSummary(payload.summary || null);
        setIssuePagination(payload.pagination || null);
      } catch (error) {
        if (!isLatestRequest()) return;
        if (
          mode === 'passive' &&
          loadedGrowthWorkTabRef.current === tab &&
          loadedGrowthTaskListQueryKeyRef.current === requestQueryKey
        ) {
          return;
        }
        setIssues([]);
        loadedGrowthWorkTabRef.current = tab;
        setLoadedGrowthWorkTab(tab);
        setGrowthWorkLoadErrorTab(tab);
        setGrowthSummary(null);
        setIssuePagination(null);
        showWorkspaceRefreshError(error, 'Failed to load tasks', mode);
      } finally {
        if (isLatestRequest()) {
          issuesFirstPageLoadingRef.current = false;
          setIssuesLoading(false);
        }
      }
    },
    [growthTaskListQuery, postAttune, projectId, projectLoading, user]
  );

  const loadIssues = useCallback(
    async (mode: WorkspaceRefreshMode = 'interactive') => {
      if (growthWorkTab === 'activity' || growthWorkTab === 'approvals') return;
      await loadIssuesForTab(growthWorkTab, mode);
    },
    [growthWorkTab, loadIssuesForTab]
  );

  const loadMoreIssues = useCallback(async () => {
    if (
      !user ||
      !projectId ||
      projectLoading ||
      issuesFirstPageLoadingRef.current ||
      !issuePagination?.hasMore ||
      loadingMoreIssues
    )
      return;
    const statuses = statusesForGrowthWorkTab(growthWorkTab);
    if (!statuses) return;
    const requestId = ++issuesRequestRef.current;
    const requestProjectId = projectId;
    const isLatestRequest = () =>
      issuesRequestRef.current === requestId && workspaceProjectIdRef.current === requestProjectId;
    setLoadingMoreIssues(true);
    try {
      const payload = await postAttune<{
        rows?: GrowthIssueRecord[];
        summary?: GrowthInboxSummaryRecord;
        pagination?: GrowthIssuePaginationRecord;
      }>('/api/attune/growth-inbox/list', {
        limit: issuePagination.limit || 250,
        offset: issuePagination.offset + issuePagination.limit,
        includeStats: false,
        includeSummary: false,
        statuses,
        compact: true,
        severity:
          growthTaskListQuery.riskFilter === 'all' ? undefined : growthTaskListQuery.riskFilter,
        updatedFrom:
          growthTaskListQuery.dateRange.key === 'all'
            ? undefined
            : growthTaskListQuery.dateRange.startDate,
        updatedTo:
          growthTaskListQuery.dateRange.key === 'all'
            ? undefined
            : growthTaskListQuery.dateRange.endDate,
        sortField: growthTaskListQuery.sortField,
        sortDirection: growthTaskListQuery.sortDirection,
        snapshotId: issuePagination.snapshotId,
      });
      if (!isLatestRequest()) return;
      setIssues(current => mergeIssueRows(current, payload.rows || []));
      if (growthWorkTab !== 'activity' && growthWorkTab !== 'approvals') {
        setLoadedGrowthWorkTab(growthWorkTab);
      }
      setGrowthSummary(payload.summary || growthSummary);
      setIssuePagination(payload.pagination || null);
    } catch (error) {
      if (!isLatestRequest()) return;
      if (
        isAttuneClientRequestError(error) &&
        error.code === 'growth_issue_list_snapshot_expired'
      ) {
        if (growthWorkTab !== 'activity' && growthWorkTab !== 'approvals') {
          await loadIssuesForTab(growthWorkTab);
        }
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Failed to load more tasks');
    } finally {
      if (isLatestRequest()) {
        setLoadingMoreIssues(false);
      }
    }
  }, [
    growthSummary,
    growthTaskListQuery,
    growthWorkTab,
    issuePagination?.hasMore,
    issuePagination?.limit,
    issuePagination?.offset,
    issuePagination?.snapshotId,
    loadIssuesForTab,
    loadingMoreIssues,
    postAttune,
    projectId,
    projectLoading,
    user,
  ]);

  const loadIssueDetail = useCallback(
    async (issueId: string): Promise<GrowthIssueDetail | null> => {
      if (!user || !projectId || projectLoading) return null;
      const requestId = (detailRequestRef.current += 1);
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        detailRequestRef.current === requestId &&
        workspaceProjectIdRef.current === requestProjectId;
      setDetail(null);
      setLinkedRun(null);
      setVerificationRun(null);
      setDetailLoading(true);
      try {
        const payload = await postAttune<{ detail?: GrowthIssueDetail | null }>(
          '/api/attune/growth-inbox/detail',
          { issueId }
        );
        if (!isLatestRequest()) return null;
        const nextDetail = payload.detail?.issue.id === issueId ? payload.detail : null;
        setDetail(nextDetail);

        const runId = nextDetail?.issue.source.scan_run_id;
        const verificationRunId = nextDetail?.issue.latest_verification?.comparison_scan_run_id;
        const [linkedRunPayload, verificationRunPayload] = await Promise.all([
          runId
            ? postAttune<{ run?: ScanRunRecord | null }>('/api/attune/scan-runs/detail', {
                runId,
              }).catch(() => ({ run: null }))
            : Promise.resolve({ run: null }),
          verificationRunId && verificationRunId !== runId
            ? postAttune<{ run?: ScanRunRecord | null }>('/api/attune/scan-runs/detail', {
                runId: verificationRunId,
              }).catch(() => ({ run: null }))
            : Promise.resolve({ run: null }),
        ]);
        const nextLinkedRun = linkedRunPayload.run || null;
        const nextVerificationRun =
          verificationRunId === runId ? nextLinkedRun : verificationRunPayload.run || null;
        if (!isLatestRequest()) return null;
        setLinkedRun(nextLinkedRun);
        setVerificationRun(nextVerificationRun);
        return nextDetail;
      } catch (error) {
        if (!isLatestRequest()) return null;
        setDetail(null);
        setLinkedRun(null);
        setVerificationRun(null);
        toast.error(error instanceof Error ? error.message : 'Failed to load task detail');
        return null;
      } finally {
        if (isLatestRequest()) {
          setDetailLoading(false);
        }
      }
    },
    [postAttune, projectId, projectLoading, user]
  );

  const loadFeed = useCallback(
    async (mode: WorkspaceRefreshMode = 'interactive') => {
      if (!user || !projectId || projectLoading) return;
      const requestId = ++feedRequestRef.current;
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        feedRequestRef.current === requestId && workspaceProjectIdRef.current === requestProjectId;
      setFeedLoading(true);
      try {
        const payload = await postAttune<{ rows?: GrowthActivityEventRecord[] }>(
          '/api/attune/growth-inbox/work-feed',
          { limit: 60, includeSummary: false }
        );
        if (!isLatestRequest()) return;
        setFeedEvents(payload.rows || []);
      } catch (error) {
        if (!isLatestRequest()) return;
        if (!isOptionalAttunePreloadError(error)) {
          showWorkspaceRefreshError(error, 'Failed to load activity', mode);
        }
      } finally {
        if (isLatestRequest()) {
          setFeedLoading(false);
        }
      }
    },
    [postAttune, projectId, projectLoading, user]
  );

  const refreshGrowthIssueContext = useCallback(
    async (issueId: string) => {
      const nextDetail = await loadIssueDetail(issueId);
      const nextTaskBucket = nextDetail?.issue ? growthTaskBucket(nextDetail.issue) : null;
      const nextWorkTab = nextDetail?.issue ? growthWorkTabForIssue(nextDetail.issue) : null;
      const currentListTab: GrowthIssueListTab =
        growthWorkTab === 'activity' || growthWorkTab === 'approvals' ? 'tasks' : growthWorkTab;
      const listTab = nextTaskBucket || currentListTab;
      if (
        growthWorkTab !== 'activity' &&
        growthWorkTab !== 'approvals' &&
        nextWorkTab &&
        nextWorkTab !== growthWorkTab
      ) {
        setGrowthWorkTab(nextWorkTab);
      }
      await Promise.all([loadFeed(), loadIssuesForTab(listTab)]);
    },
    [growthWorkTab, loadFeed, loadIssueDetail, loadIssuesForTab]
  );

  const loadAutomations = useCallback(
    async (mode: WorkspaceRefreshMode = 'interactive') => {
      if (!user || !projectId || projectLoading) return;
      const requestId = ++automationsRequestRef.current;
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        automationsRequestRef.current === requestId &&
        workspaceProjectIdRef.current === requestProjectId;
      setAgentsLoading(true);
      try {
        const [templateResult, promptResult, automationResult, northStarResult] =
          await Promise.allSettled([
            postAttune<{ templates?: ScanAutomationTemplate[] }>(
              '/api/attune/scan-automations/templates'
            ),
            postAttune<{ prompts?: ScanPromptTemplate[] }>('/api/attune/scan-automations/prompts'),
            postAttune<{ automations?: ScanAutomationRecord[] }>(
              '/api/attune/scan-automations/list'
            ),
            postAttune<{ document?: NorthStarDocumentRecord | null }>('/api/attune/north-star/get'),
          ]);
        if (!isLatestRequest()) return;

        if (templateResult.status === 'fulfilled') {
          setTemplates(templateResult.value.templates || []);
        }
        if (promptResult.status === 'fulfilled') {
          setPrompts(promptResult.value.prompts || []);
        }
        if (northStarResult.status === 'fulfilled') {
          setNorthStar(northStarResult.value.document || null);
        }
        if (automationResult.status === 'fulfilled') {
          const nextAutomations = automationResult.value.automations || [];
          setAutomations(nextAutomations);
          const automationsWithRuns = nextAutomations.filter(
            (automation): automation is ScanAutomationRecord & { last_run_id: string } =>
              Boolean(automation.last_run_id)
          );
          const automationRunBatches = Array.from(
            { length: Math.ceil(automationsWithRuns.length / 100) },
            (_, index) => automationsWithRuns.slice(index * 100, (index + 1) * 100)
          );
          setLastRuns(current =>
            Object.fromEntries(
              automationsWithRuns.flatMap(automation => {
                const currentRun = current[automation.id];
                return currentRun?.id === automation.last_run_id
                  ? ([[automation.id, currentRun]] as const)
                  : [];
              })
            )
          );
          if (automationsWithRuns.length > 0) {
            void (async () => {
              const runsById = new Map<string, ScanRunRecord>();
              for (const automationBatch of automationRunBatches) {
                const payload = await postAttune<{ runs?: ScanRunRecord[] }>(
                  '/api/attune/scan-runs/list',
                  {
                    runIds: automationBatch.map(automation => automation.last_run_id),
                    summaryOnly: true,
                  }
                );
                if (!isLatestRequest()) return;
                for (const run of payload.runs || []) runsById.set(run.id, run);
              }
              if (!isLatestRequest()) return;
              setLastRuns(
                Object.fromEntries(
                  automationsWithRuns.map(automation => [
                    automation.id,
                    runsById.get(automation.last_run_id) || null,
                  ])
                )
              );
            })().catch(error => {
              if (!isLatestRequest()) return;
              setLastRuns(current => {
                const next = { ...current };
                for (const automation of automationsWithRuns) {
                  if (next[automation.id] === undefined) next[automation.id] = null;
                }
                return next;
              });
              if (isOptionalAttunePreloadError(error)) return;
              showWorkspaceRefreshError(
                error,
                'Failed to load Background Task run summaries',
                mode
              );
            });
          }
        }

        const isRequiredFailure = (result: PromiseSettledResult<unknown>) =>
          result.status === 'rejected' && !isOptionalAttunePreloadError(result.reason);
        if (mode === 'interactive') {
          if (isRequiredFailure(templateResult)) setTemplates([]);
          if (isRequiredFailure(promptResult)) setPrompts([]);
          if (isRequiredFailure(northStarResult)) setNorthStar(null);
          if (isRequiredFailure(automationResult)) {
            setAutomations([]);
            setLastRuns({});
          }
        }

        const failedResult = [templateResult, promptResult, automationResult, northStarResult].find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected' && !isOptionalAttunePreloadError(result.reason)
        );
        if (failedResult) {
          showWorkspaceRefreshError(failedResult.reason, 'Failed to load background tasks', mode);
        }
      } catch (error) {
        if (!isLatestRequest()) return;
        if (mode === 'interactive') {
          setTemplates([]);
          setPrompts([]);
          setAutomations([]);
          setLastRuns({});
          setNorthStar(null);
        }
        if (!isOptionalAttunePreloadError(error)) {
          showWorkspaceRefreshError(error, 'Failed to load background tasks', mode);
        }
      } finally {
        if (isLatestRequest()) {
          setAgentsLoading(false);
        }
      }
    },
    [postAttune, projectId, projectLoading, user]
  );

  const loadScanRuns = useCallback(
    async (mode: WorkspaceRefreshMode = 'interactive') => {
      if (!user || !projectId || projectLoading) return;
      const requestId = ++scanRunsRequestRef.current;
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        scanRunsRequestRef.current === requestId &&
        workspaceProjectIdRef.current === requestProjectId;
      setScanRunsLoading(true);
      try {
        const payload = await postAttune<{ runs?: ScanRunRecord[] }>('/api/attune/scan-runs/list', {
          limit: 50,
          ...(scanRunStatusFilter ? { status: scanRunStatusFilter } : {}),
        });
        if (!isLatestRequest()) return;
        setScanRuns(payload.runs || []);
      } catch (error) {
        if (!isLatestRequest()) return;
        if (mode === 'interactive') {
          const fallback = Object.values(lastRuns).filter(
            (run): run is ScanRunRecord =>
              Boolean(run) && (!scanRunStatusFilter || run?.status === scanRunStatusFilter)
          );
          setScanRuns(fallback);
          if (fallback.length === 0 && !isOptionalAttunePreloadError(error)) {
            showWorkspaceRefreshError(error, 'Failed to load Background Task runs', mode);
          }
        }
      } finally {
        if (isLatestRequest()) setScanRunsLoading(false);
      }
    },
    [lastRuns, postAttune, projectId, projectLoading, scanRunStatusFilter, user]
  );

  const loadDeliveryConnections = useCallback(
    async (mode: WorkspaceRefreshMode = 'interactive') => {
      const requestId = ++deliveryConnectionsRequestRef.current;
      const requestProjectId = projectId;
      const isLatestRequest = () =>
        deliveryConnectionsRequestRef.current === requestId &&
        workspaceProjectIdRef.current === requestProjectId;
      if (!user || !projectId || projectLoading) {
        if (isLatestRequest()) {
          setDeliveryConnections({
            webflow: false,
            shopify: false,
            wordpress: false,
            github: false,
            customMcp: false,
            loading: false,
          });
        }
        return;
      }
      setDeliveryConnections(current => ({ ...current, loading: true }));
      try {
        const token = await user.getIdToken();
        const [webflow, shopify, wordpress, github, customMcp] = await Promise.all([
          readWorkspaceJson<WebflowStatusSummary>(
            `/api/integrations/webflow/status?projectId=${encodeURIComponent(projectId)}`,
            token
          ),
          readWorkspaceJson<ShopifyStatusSummary>(
            `/api/integrations/shopify/status?projectId=${encodeURIComponent(projectId)}`,
            token
          ),
          isWordPressMcpEnabledClient()
            ? readWorkspaceJson<WordPressStatusSummary>(
                `/api/integrations/wordpress/status?projectId=${encodeURIComponent(projectId)}`,
                token
              ).catch(() => null)
            : Promise.resolve(null),
          isGitHubWebsiteEnabledClient()
            ? readWorkspaceJson<GitHubStatusSummary>(
                `/api/integrations/github/status?projectId=${encodeURIComponent(projectId)}`,
                token
              ).catch(() => null)
            : Promise.resolve(null),
          readWorkspaceJson<CustomMcpStatusResponse>(
            `/api/integrations/custom-mcp/status?projectId=${encodeURIComponent(projectId)}`,
            token
          ),
        ]);
        if (!isLatestRequest()) return;
        setDeliveryConnections({
          webflow: Boolean(webflow?.writable),
          shopify: Boolean(shopify?.connected),
          wordpress: Boolean(wordpress?.connected && wordpress.binding?.canWrite),
          github: githubIsLive(github),
          customMcp: customMcpIsLive(customMcp),
          loading: false,
        });
      } catch {
        if (isLatestRequest()) {
          if (mode === 'interactive') {
            setDeliveryConnections({
              webflow: false,
              shopify: false,
              wordpress: false,
              github: false,
              customMcp: false,
              loading: false,
            });
          } else {
            setDeliveryConnections(current => ({ ...current, loading: false }));
          }
        }
      }
    },
    [projectId, projectLoading, user]
  );

  const refreshAgentWorkspace = useCallback(async () => {
    const activeSessionId = session.sessionId;
    await Promise.allSettled([
      loadAutomations('interactive'),
      loadScanRuns('interactive'),
      loadDeliveryConnections('interactive'),
      loadFeed('interactive'),
      session.refreshSessions(),
      activeSessionId
        ? session.loadSessionById(activeSessionId, { redirectOnError: false }).catch(error => {
            showWorkspaceRefreshError(error, 'Failed to refresh session', 'interactive');
          })
        : Promise.resolve(),
    ]);
  }, [loadAutomations, loadDeliveryConnections, loadFeed, loadScanRuns, session]);

  useEffect(() => {
    const activeCount = chat.activeSessionIds.length;
    const previousActiveCount = previousActiveChatSessionCountRef.current;
    previousActiveChatSessionCountRef.current = activeCount;

    if (activeCount === 0) {
      if (previousActiveCount > 0) {
        void session.refreshSessions();
      }
      return;
    }

    void session.refreshSessions();
    const refreshTimer = setInterval(() => {
      void session.refreshSessions();
    }, LIVE_SESSION_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(refreshTimer);
    };
  }, [activeChatSessionKey, chat.activeSessionIds.length, session.refreshSessions]);

  useEffect(() => {
    if (
      !user ||
      !workspaceProjectReady ||
      mode !== 'tasks' ||
      growthWorkTab === 'activity' ||
      growthWorkTab === 'approvals'
    )
      return;
    void loadIssues('passive');
  }, [growthWorkTab, loadIssues, mode, user, workspaceProjectReady]);

  useEffect(() => {
    if (!user || !workspaceProjectReady || mode !== 'tasks' || growthWorkTab !== 'activity') {
      return;
    }
    void loadFeed('passive');
  }, [growthWorkTab, loadFeed, mode, user, workspaceProjectReady]);

  useEffect(() => {
    if (!user || !workspaceProjectReady || mode !== 'agents') return;
    void loadAutomations('interactive');
    void loadDeliveryConnections();
  }, [loadAutomations, loadDeliveryConnections, mode, user, workspaceProjectReady]);

  useEffect(() => {
    if (!user || !workspaceProjectReady || mode !== 'agents' || backgroundTaskTab !== 'runs') {
      return;
    }
    void loadScanRuns('interactive');
  }, [backgroundTaskTab, loadScanRuns, mode, scanRunStatusFilter, user, workspaceProjectReady]);

  useEffect(() => {
    if (routeMode) return;
    const currentUrlMode = new URLSearchParams(window.location.search).get('mode');
    setMode(workspaceModeFromUrl(currentUrlMode));
  }, [routeMode, setMode, workspaceUrlSearch]);

  useEffect(() => {
    if (projectLoading || mode !== 'growth-program') return;
    if (!currentProject) {
      setMode('chat');
      return;
    }
    if (aiCmoBetaEnabled) return;
    setMode('chat');
    const currentUrlMode = new URLSearchParams(window.location.search).get('mode');
    if (currentUrlMode === 'growth-program') {
      router.replace('/app/workspace?new_chat=true', { scroll: false });
    }
  }, [aiCmoBetaEnabled, currentProject, mode, projectLoading, router, setMode]);

  useLayoutEffect(() => {
    if (urlMode !== 'tasks') return;
    const nextTab = growthWorkTabFromUrl(urlTaskTab);
    setGrowthWorkTab(nextTab);
    if (urlTaskTab !== 'approvals' || nextTab === 'approvals') {
      return;
    }
    replaceWorkspaceSearch(query => {
      if (query.get('mode') === 'tasks' && query.get('tab') === 'approvals') {
        query.delete('tab');
      }
    });
  }, [replaceWorkspaceSearch, urlMode, urlTaskTab]);

  useEffect(() => {
    if (routeAgentTab) return;
    if (urlMode !== 'agents') return;
    if (urlTaskTab === 'tasks' || urlTaskTab === 'runs' || urlTaskTab === 'templates') {
      setBackgroundTaskTab(urlTaskTab);
      return;
    }
    setBackgroundTaskTab('tasks');
  }, [routeAgentTab, setBackgroundTaskTab, urlMode, urlTaskTab]);

  useEffect(() => {
    if (!sessionDeepLinkId) {
      sessionDeepLinkHandledRef.current = null;
      sessionDeepLinkProjectSwitchedRef.current = null;
      activeWorkspaceSessionLoadRef.current = null;
      suppressedSessionDeepLinkRef.current = null;
      if (sessionDeepLinkRetryTimerRef.current) {
        clearTimeout(sessionDeepLinkRetryTimerRef.current);
        sessionDeepLinkRetryTimerRef.current = null;
      }
      setLoadingWorkspaceSession(false);
      setUnavailableSessionId(null);
      return;
    }
    if (!user || projectLoading) return;
    if (suppressedSessionDeepLinkRef.current === sessionDeepLinkId) return;
    if (sessionDeepLinkHandledRef.current === sessionDeepLinkId) {
      return;
    }
    if (
      urlMode !== 'tasks' &&
      urlMode !== 'growth-program' &&
      urlMode !== 'agents' &&
      urlMode !== 'integrations'
    ) {
      setMode('chat');
    }
    if (session.sessionId === sessionDeepLinkId) {
      sessionDeepLinkHandledRef.current = sessionDeepLinkId;
      setLoadingWorkspaceSession(false);
      return;
    }
    sessionDeepLinkHandledRef.current = sessionDeepLinkId;
    activeWorkspaceSessionLoadRef.current = sessionDeepLinkId;
    setLoadingWorkspaceSession(true);
    void session
      .loadSessionById(sessionDeepLinkId, {
        preferBootstrap: true,
        redirectOnError: false,
      })
      .then(loaded => {
        if (!loaded && activeWorkspaceSessionLoadRef.current === sessionDeepLinkId) {
          sessionDeepLinkHandledRef.current = null;
          setSessionDeepLinkRetryTick(current => current + 1);
        }
        if (loaded) {
          setUnavailableSessionId(null);
        }
      })
      .catch(error => {
        handleWorkspaceSessionLoadError(sessionDeepLinkId, error);
      })
      .finally(() => {
        if (activeWorkspaceSessionLoadRef.current === sessionDeepLinkId) {
          activeWorkspaceSessionLoadRef.current = null;
          setLoadingWorkspaceSession(false);
        }
      });
  }, [
    handleWorkspaceSessionLoadError,
    onboardingChatTarget,
    projectId,
    projectLoading,
    session,
    session.sessionId,
    sessionDeepLinkId,
    sessionDeepLinkRetryTick,
    setMode,
    urlMode,
    user,
  ]);

  useEffect(() => {
    if (unavailableSessionId && unavailableSessionId !== sessionDeepLinkId) {
      setUnavailableSessionId(null);
    }
  }, [sessionDeepLinkId, unavailableSessionId]);

  useEffect(() => {
    if (!sessionDeepLinkId || session.sessionId !== sessionDeepLinkId) return;
    if (session.sessionScope?.kind === 'workspace') return;
    if (sessionDeepLinkProjectSwitchedRef.current === sessionDeepLinkId) return;
    const targetProjectId = session.sessionProjectId;
    if (!targetProjectId) return;
    if (targetProjectId === projectId) {
      sessionDeepLinkProjectSwitchedRef.current = sessionDeepLinkId;
      return;
    }
    if (!projects.some(project => project.id === targetProjectId)) return;
    sessionDeepLinkProjectSwitchedRef.current = sessionDeepLinkId;
    switchProject(targetProjectId, { preserveRoute: true });
  }, [
    projectId,
    projects,
    session.sessionId,
    session.sessionProjectId,
    session.sessionScope,
    sessionDeepLinkId,
    switchProject,
  ]);

  const refreshSharedSession = session.loadSessionById;
  useEffect(() => {
    if (
      (session.accessRole !== 'workspace_viewer' && !session.sessionOrigin) ||
      !session.sessionId ||
      chat.isLoading ||
      forkingSharedSession
    )
      return;
    const sharedSessionId = session.sessionId;
    let refreshing = false;
    const handleFocus = () => {
      if (document.visibilityState !== 'visible' || refreshing) return;
      refreshing = true;
      void refreshSharedSession(sharedSessionId, {
        redirectOnError: false,
        backgroundRefresh: true,
      })
        .catch(error => handleWorkspaceSessionLoadError(sharedSessionId, error))
        .finally(() => {
          refreshing = false;
        });
    };
    window.addEventListener('focus', handleFocus);
    // Active turns stream through the gateway. Idle history needs only an
    // occasional checkpoint; a full refresh rechecks live Slack membership.
    const timer = session.sessionOrigin ? window.setInterval(handleFocus, 60000) : null;
    return () => {
      window.removeEventListener('focus', handleFocus);
      if (timer !== null) window.clearInterval(timer);
    };
  }, [
    handleWorkspaceSessionLoadError,
    refreshSharedSession,
    session.sessionId,
    session.accessRole,
    session.sessionOrigin,
    chat.isLoading,
    forkingSharedSession,
  ]);

  useEffect(() => {
    if (!session.sessionId || session.sessionScope?.kind !== 'workspace') return;
    const workspaceId = session.sessionScope.workspaceId;
    replaceWorkspaceSearch(query => {
      if (
        query.get('session') !== session.sessionId &&
        query.get('sessionId') !== session.sessionId
      )
        return;
      query.set('session_scope', 'workspace');
      query.set('workspace_id', workspaceId);
    });
  }, [session.sessionId, session.sessionScope, replaceWorkspaceSearch]);

  useEffect(() => {
    if (!issueDeepLinkId) return;
    if (issueDeepLinkHandledRef.current === issueDeepLinkId) return;
    issueDeepLinkHandledRef.current = issueDeepLinkId;
    router.replace(`/app/tasks/${encodeURIComponent(issueDeepLinkId)}`);
  }, [issueDeepLinkId, router]);

  useEffect(() => {
    return () => {
      if (sessionDeepLinkRetryTimerRef.current) {
        clearTimeout(sessionDeepLinkRetryTimerRef.current);
        sessionDeepLinkRetryTimerRef.current = null;
      }
    };
  }, []);

  const sessionDeepLinkPending = Boolean(
    sessionDeepLinkId &&
      session.sessionId !== sessionDeepLinkId &&
      suppressedSessionDeepLinkRef.current !== sessionDeepLinkId
  );
  const canvasInitialBuildPending = Boolean(
    editorPanelOpen &&
      editorPanelView === 'canvas' &&
      composingCanvas &&
      !composingCanvas.lastSuccessfulVersion &&
      (composingCanvas.status === 'generating' || composingCanvas.status === 'building')
  );
  const workspaceSessionPending =
    Boolean(pendingInitialMessage) ||
    session.isCreating ||
    loadingWorkspaceSession ||
    sessionDeepLinkPending;

  useEffect(() => {
    if (
      !urlQuestion ||
      !session.sessionId ||
      session.accessRole === 'workspace_viewer' ||
      chat.isLoading ||
      workspaceSessionPending
    )
      return;
    const key = `${session.sessionId}:${urlQuestion}`;
    if (questionDeepLinkHandledRef.current === key) return;
    if (session.messages.length > 0) return;
    questionDeepLinkHandledRef.current = key;
    setMode('chat');
    void chat
      .sendMessageDirect(urlQuestion, undefined, urlSkill ? [{ skillId: urlSkill }] : undefined)
      .then(() => {
        clearWorkspaceSearchParams(['question', 'skill']);
      });
  }, [
    chat,
    clearWorkspaceSearchParams,
    session.messages.length,
    session.sessionId,
    session.accessRole,
    setMode,
    urlQuestion,
    urlSkill,
    workspaceSessionPending,
  ]);

  useEffect(() => {
    if (selectedIssueId) {
      void loadIssueDetail(selectedIssueId);
    }
  }, [loadIssueDetail, selectedIssueId]);

  useEffect(() => {
    const issue = detail?.issue;
    if (!issue || pendingIssueTabResolutionRef.current !== issue.id) return;
    pendingIssueTabResolutionRef.current = null;
    const resolvedTab = growthWorkTabForIssue(issue);
    setGrowthWorkTab(resolvedTab);
    setWorkspaceTaskSearch(resolvedTab, issue.id);
  }, [detail, setWorkspaceTaskSearch]);

  useEffect(() => {
    if (
      selectedAutomationId &&
      visibleAutomations.some(automation => automation.id === selectedAutomationId)
    ) {
      return;
    }
    setSelectedAutomationId(visibleAutomations[0]?.id || null);
  }, [selectedAutomationId, visibleAutomations]);

  useEffect(() => {
    if (selectedTemplateId && visibleTemplates.some(template => template.id === selectedTemplateId))
      return;
    setSelectedTemplateId(visibleTemplates[0]?.id || 'growth_orchestrator_task_v1');
  }, [selectedTemplateId, visibleTemplates]);

  useEffect(() => {
    setRunNowSourceJobId('');
  }, [selectedAutomationId]);

  const consumeComposerDocumentRestoration = useCallback((id: string) => {
    setComposerDocumentRestoration(current => (current?.id === id ? null : current));
  }, []);

  useEffect(() => {
    if (!pendingInitialMessage || !session.sessionId || chat.isLoading) return;
    const message = pendingInitialMessage;
    const targetSessionId = session.sessionId;
    setPendingInitialMessage(null);
    const restoreDocuments = () => {
      if (!message.documents?.length) return;
      setComposerDocumentRestoration({
        id: crypto.randomUUID(),
        documents: message.documents,
        sessionId: targetSessionId,
        projectId: session.mode === 'onboarding' ? null : projectId,
      });
    };
    void chat
      .sendMessageDirect(
        message.content,
        message.images,
        message.selectedSkills,
        message.executionMode || 'standard',
        message.documents,
        true,
        message.context
      )
      .then(streamAccepted => {
        if (!streamAccepted) restoreDocuments();
      })
      .catch(restoreDocuments);
  }, [chat, pendingInitialMessage, projectId, session.mode, session.sessionId]);

  const handleSendMessage = useCallback(
    (
      content: string,
      images?: ImageAttachment[],
      selectedSkills?: Array<{ skillId: string; versionId?: string }>,
      requestedMode?: ResearchExecutionMode,
      documents?: DocumentAttachment[],
      _restoreDocumentsOnReject?: boolean,
      context?: ComposerMessageContext
    ) => {
      if (workspaceSessionPending || canvasInitialBuildPending) return false;
      const directive = resolveExecutionDirective(content, requestedMode || executionMode);
      if (directive.command === 'select_mode' && !images?.length && !documents?.length) {
        setExecutionMode(directive.executionMode);
        return true;
      }
      if (directive.command === 'cancel_run' && !images?.length && !documents?.length) {
        const activeRun = chat.runStatus;
        if (activeRun?.mode !== directive.executionMode) {
          toast.error(`No active ${directive.executionMode} run to cancel.`);
          return true;
        }
        setExecutionMode('standard');
        void chat.cancelRun(activeRun).catch(error => {
          toast.error(error instanceof Error ? error.message : 'Unable to cancel run.');
        });
        return true;
      }
      const effectiveContent =
        directive.message ||
        (documents?.length
          ? '(See attached document)'
          : images?.length
            ? '(See attached image)'
            : content);
      setExecutionMode('standard');
      if (session.sessionId) {
        return chat.sendMessageDirect(
          effectiveContent,
          images,
          selectedSkills,
          directive.executionMode,
          documents,
          true,
          context
        );
      }
      setPendingInitialMessage({
        content: effectiveContent,
        images,
        documents,
        selectedSkills,
        executionMode: directive.executionMode,
        context,
      });
      void session
        .bootstrapWorkspaceSession({
          editor:
            activeEditor === 'canvas' ||
            activeEditor === 'website' ||
            activeEditor === 'design-system'
              ? activeEditor
              : undefined,
        })
        .then(sessionId => {
          if (!sessionId) {
            setPendingInitialMessage(null);
            documents?.forEach(document => {
              void deleteUploadedDocument(document).catch(() => undefined);
            });
          }
        });
      return true;
    },
    [activeEditor, canvasInitialBuildPending, chat, executionMode, session, workspaceSessionPending]
  );

  useEffect(() => {
    const handlePreviewLaunch = (event: Event) => {
      const detail = (event as CustomEvent<WorkspaceWebsitePreviewLaunchDetail>).detail;
      if (!detail || detail.projectId !== projectId || detail.actorUserId !== user?.uid) return;
      detail.onAccepted?.();

      void (async () => {
        if (!user) throw new Error('Authentication is required');
        let sourceSessionId =
          session.sessionProjectId === projectId && session.accessRole === 'owner'
            ? session.sessionId
            : null;
        if (!sourceSessionId) {
          // Create lineage without applying an empty chat. The launch result
          // navigates to this session only after preview startup succeeds.
          const created = await session.apiCall({
            action: 'create_session',
            promptId: '__workspace_default__',
            promptText: 'Website preview',
            projectId,
            deferHydration: true,
          });
          const payload = (await created.json()) as {
            sessionId?: string;
            error?: { message?: string };
          };
          if (!created.ok || !payload.sessionId) {
            throw new Error(payload.error?.message || 'Unable to create a preview session');
          }
          sourceSessionId = payload.sessionId;
        }
        const token = await user.getIdToken();
        const response = await fetch('/api/attune/github/preview-start', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ projectId, sourceSessionId }),
        });
        const payload = (await response.json().catch(() => ({}))) as {
          preview?: import('@/components/github/GitHubLivePreview').WebsitePreviewMetadata;
          error?: string;
        };
        if (!response.ok || !payload.preview) {
          throw new Error(payload.error || 'Unable to launch the website preview right now.');
        }
        detail.onResult?.({ preview: payload.preview, sourceSessionId });
      })().catch(error => {
        detail.onResult?.(null);
        toast.error(
          error instanceof Error ? error.message : 'Unable to launch the website preview right now.'
        );
      });
    };

    window.addEventListener(WORKSPACE_WEBSITE_PREVIEW_LAUNCH_EVENT, handlePreviewLaunch);
    return () =>
      window.removeEventListener(WORKSPACE_WEBSITE_PREVIEW_LAUNCH_EVENT, handlePreviewLaunch);
  }, [projectId, session, user]);

  const activeOnboardingSessionProjectId = getReusableOnboardingSessionProjectId({
    sessionId: session.sessionId,
    deepLinkSessionId: sessionDeepLinkId,
    mode: session.mode,
    projectId: session.sessionProjectId,
  });

  const ensureOnboardingProjectShell = useCallback(
    async (targetWorkspaceId: string | null): Promise<string | null> => {
      const existingProjectId =
        onboardingChatTarget?.projectId || activeOnboardingSessionProjectId || null;
      if (existingProjectId) return existingProjectId;
      const scopeKey = `${targetWorkspaceId || 'personal'}:${
        onboardingResetToken || 'default'
      }:${onboardingFlowIdentityRef.current}`;
      onboardingShellScopeRef.current = scopeKey;
      if (onboardingShellProjectRef.current?.scopeKey === scopeKey) {
        return onboardingShellProjectRef.current.projectId;
      }
      if (onboardingShellCreateRef.current?.scopeKey === scopeKey) {
        return onboardingShellCreateRef.current.promise;
      }

      const createPromise = (async () => {
        try {
          const newProject = targetWorkspaceId
            ? await createOnboardingProjectShell({ workspace_id: targetWorkspaceId })
            : await createOnboardingProjectShell();
          if (onboardingShellScopeRef.current !== scopeKey) return null;
          onboardingShellProjectRef.current = {
            scopeKey,
            projectId: newProject.id,
          };
          return newProject.id;
        } catch (error) {
          if (onboardingShellScopeRef.current === scopeKey) {
            toast.error(
              error instanceof Error ? error.message : 'Failed to create onboarding project'
            );
          }
          return null;
        } finally {
          if (onboardingShellCreateRef.current?.scopeKey === scopeKey) {
            onboardingShellCreateRef.current = null;
          }
        }
      })();

      onboardingShellCreateRef.current = { scopeKey, promise: createPromise };
      return createPromise;
    },
    [
      activeOnboardingSessionProjectId,
      createOnboardingProjectShell,
      onboardingChatTarget?.projectId,
      onboardingResetToken,
    ]
  );

  const resolveOnboardingAttachmentProjectId = useCallback(
    () => ensureOnboardingProjectShell(onboardingChatTarget?.workspaceId || null),
    [ensureOnboardingProjectShell, onboardingChatTarget?.workspaceId]
  );

  const handleOnboardingSendMessage = useCallback(
    (
      content: string,
      images?: ImageAttachment[],
      selectedSkills?: Array<{ skillId: string; versionId?: string }>,
      _requestedMode?: ResearchExecutionMode,
      documents?: DocumentAttachment[],
      _restoreDocumentsOnReject?: boolean,
      context?: ComposerMessageContext
    ) => {
      if (workspaceSessionPending) return false;
      const effectiveContent =
        content.trim() ||
        (documents?.length
          ? '(See attached document)'
          : images?.length
            ? '(See attached image)'
            : '');
      if (!effectiveContent) return false;
      const pendingMessage: PendingWorkspaceMessage = {
        content: effectiveContent,
        images,
        documents,
        selectedSkills,
        executionMode: 'standard',
        context,
      };
      const needsOnboardingSession = !session.sessionId;
      if (needsOnboardingSession) setPendingInitialMessage(pendingMessage);
      setExecutionMode('standard');
      return (async () => {
        const targetWorkspaceId = onboardingChatTarget?.workspaceId || null;
        const onboardingProjectId = await ensureOnboardingProjectShell(targetWorkspaceId);
        if (!onboardingProjectId) {
          if (needsOnboardingSession) setPendingInitialMessage(null);
          documents?.forEach(document => {
            void deleteUploadedDocument(document).catch(() => undefined);
          });
          return true;
        }

        const canReuseOnboardingSession =
          session.sessionId &&
          session.mode === 'onboarding' &&
          session.sessionProjectId === onboardingProjectId;
        if (canReuseOnboardingSession) {
          if (needsOnboardingSession) setPendingInitialMessage(null);
          return chat.sendMessageDirect(
            effectiveContent,
            images,
            selectedSkills,
            'standard',
            documents,
            true,
            context
          );
        }
        if (session.sessionId) {
          session.resetSession({ skipNavigation: true });
        }
        if (!needsOnboardingSession) setPendingInitialMessage(pendingMessage);
        void session
          .bootstrapWorkspaceSession({
            mode: 'onboarding',
            promptText: buildOnboardingSessionPromptText(onboardingChatTarget, onboardingProjectId),
            projectId: onboardingProjectId,
            visibilityContext: targetWorkspaceId
              ? { onboarding_workspace_id: targetWorkspaceId }
              : undefined,
            skipNavigation: true,
          })
          .then(sessionId => {
            if (!sessionId) {
              setPendingInitialMessage(null);
              documents?.forEach(document => {
                void deleteUploadedDocument(document).catch(() => undefined);
              });
              return;
            }
            setWorkspaceSessionSearch(sessionId);
          });
        return true;
      })();
    },
    [
      chat,
      ensureOnboardingProjectShell,
      onboardingChatTarget,
      onboardingChatTarget?.projectId,
      onboardingChatTarget?.workspaceId,
      session,
      setWorkspaceSessionSearch,
      workspaceSessionPending,
    ]
  );

  useEffect(() => {
    if (!onboardingChatTarget || onboardingWorkspaceRequested || onboardingExitStartedRef.current) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('onboarding_chat', 'true');
    router.replace(`/app/workspace?${params.toString()}`);
  }, [onboardingChatTarget, onboardingWorkspaceRequested, router, searchParams]);

  useEffect(() => {
    if (!forceNewProjectOnboarding) {
      freshOnboardingEntryHandledRef.current = false;
      return;
    }
    if (onboardingResetToken || sessionDeepLinkId || freshOnboardingEntryHandledRef.current) {
      return;
    }

    freshOnboardingEntryHandledRef.current = true;
    onboardingFlowIdentityRef.current += 1;
    onboardingShellCreateRef.current = null;
    onboardingShellScopeRef.current = null;
    onboardingShellProjectRef.current = null;
    onboardingExitStartedRef.current = null;
    setPendingInitialMessage(null);
    session.resetSession({ skipNavigation: true });
  }, [chat, forceNewProjectOnboarding, onboardingResetToken, session, sessionDeepLinkId]);

  useEffect(() => {
    if (!onboardingChatTarget || !onboardingResetToken) return;
    if (onboardingResetHandledRef.current === onboardingResetToken) return;

    onboardingResetHandledRef.current = onboardingResetToken;
    onboardingFlowIdentityRef.current += 1;
    onboardingShellCreateRef.current = null;
    onboardingShellScopeRef.current = null;
    onboardingShellProjectRef.current = null;
    onboardingExitStartedRef.current = null;
    setPendingInitialMessage(null);
    session.resetSession({ skipNavigation: true });
  }, [chat, onboardingChatTarget, onboardingResetToken, session]);

  useEffect(() => {
    if (onboardingChatTarget) return;
    onboardingFlowIdentityRef.current += 1;
    onboardingShellCreateRef.current = null;
    onboardingShellScopeRef.current = null;
    onboardingShellProjectRef.current = null;
  }, [onboardingChatTarget]);

  useEffect(() => {
    if (!onboardingChatTarget) return;
    if (
      !sessionDeepLinkId ||
      session.sessionId !== sessionDeepLinkId ||
      session.mode !== 'onboarding'
    ) {
      return;
    }
    const receipt = liveOnboardingCompletionReceipt || persistedOnboardingCompletionReceipt;
    if (!receipt) return;
    const receiptKey = receipt.workflowId || `project:${receipt.projectId}`;
    if (onboardingExitStartedRef.current === receiptKey) return;

    onboardingExitStartedRef.current = receiptKey;
    router.replace(getCompletedOnboardingWorkspaceHref(session.sessionId!), { scroll: false });
    void Promise.resolve(fetchProjects(true)).catch(error => {
      console.warn('[Onboarding] Failed to refresh projects after workspace unlock', error);
    });
  }, [
    fetchProjects,
    liveOnboardingCompletionReceipt,
    onboardingChatTarget,
    persistedOnboardingCompletionReceipt,
    router,
    session.mode,
    session.sessionId,
    sessionDeepLinkId,
  ]);

  const resetPrepared = useCallback(() => {
    setPrepared(null);
    setPreparedDraftInput(null);
    setAgentContractOpen(false);
  }, []);

  const applyTemplateAssignment = useCallback(
    (template: ScanAutomationTemplate) => {
      resetPrepared();
      const templateTrigger = template.default_trigger;
      const newTaskTriggerType =
        templateTrigger.type === 'manual' || templateTrigger.type === 'sampler_completed_delay'
          ? 'daily_scan'
          : templateTrigger.type;
      const [firstMonthlyDay, secondMonthlyDay] = normalizeTwiceMonthlyDays(
        templateTrigger.days_of_month
      );
      setSelectedTemplateId(template.id);
      setAutomationName(template.name);
      setAutomationDescription(template.description);
      setTriggerType(newTaskTriggerType);
      setDelayMinutes(templateTrigger.delay_minutes || 60);
      setScheduleDayOfWeek(
        templateTrigger.type === 'weekly_scan' ? clampWeekday(templateTrigger.day_of_week) : 1
      );
      setScheduleDayOfMonth(
        templateTrigger.type === 'twice_monthly_scan'
          ? firstMonthlyDay
          : templateTrigger.type === 'monthly_scan'
            ? clampMonthDay(templateTrigger.day_of_month, 28)
            : clampMonthDay(new Date().getDate())
      );
      setScheduleSecondDayOfMonth(secondMonthlyDay);
      setDataSources(template.data_sources);
    },
    [resetPrepared]
  );

  const openComposer = useCallback(
    (nextMode: ComposerMode, templateOverride?: ScanAutomationTemplate) => {
      setTaskSlackDelivery(null);
      setTaskSlackDeliveryBaseline(null);
      setSelectedResearchModelId(RECOMMENDED_RESEARCH_MODEL_ID);
      setEditAutomationOpen(false);
      setEditingAutomationId(null);
      setEditingModelId('');
      setComposerMode(nextMode);
      resetPrepared();
      const presetTemplate = templateOverride || selectedTemplate;
      if (nextMode === 'preset' && presetTemplate) {
        applyTemplateAssignment(presetTemplate);
      }
      if (nextMode === 'custom') {
        setAutomationName('Custom Background Task');
        setAutomationDescription(
          'Approved custom task that reviews current Attune evidence and returns the requested output.'
        );
        setCustomPromptBody(DEFAULT_CUSTOM_PROMPT);
        setTriggerType('daily_scan');
        setDelayMinutes(60);
        setScheduleDayOfWeek(1);
        setScheduleDayOfMonth(1);
        setScheduleSecondDayOfMonth(15);
        setDataSources(
          (Object.keys(DATA_SOURCE_LABELS) as ScanDataSource[]).filter(
            source => source !== 'sampler'
          )
        );
      }
      setComposerOpen(true);
    },
    [applyTemplateAssignment, resetPrepared, selectedTemplate, setSelectedResearchModelId]
  );

  const handleBackgroundTaskIntentChange = useCallback(
    (active: boolean) => {
      setWorkspaceComposerIntent(active ? 'background_task' : 'research');
      if (active) {
        setExecutionMode('standard');
        setSelectedResearchModelId(RECOMMENDED_RESEARCH_MODEL_ID);
      }
    },
    [setSelectedResearchModelId]
  );
  const consumeBackgroundTaskDraft = useCallback(() => setBackgroundTaskDraft(''), []);
  const consumeGrowthPlanComposerDraft = useCallback(() => setGrowthPlanComposerDraft(''), []);
  const consumeInitialComposerIntent = useCallback(
    () => clearWorkspaceSearchParams(['composer_skill', 'composer_draft']),
    [clearWorkspaceSearchParams]
  );
  const consumeChatComposerIntent = useCallback(() => {
    consumeGrowthPlanComposerDraft();
    consumeInitialComposerIntent();
  }, [consumeGrowthPlanComposerDraft, consumeInitialComposerIntent]);
  const editGrowthPlanInComposer = useCallback(() => {
    session.resetSession({ skipNavigation: true });
    setPendingInitialMessage(null);
    setWorkspaceComposerIntent('research');
    setBackgroundTaskDraft('');
    setGrowthPlanComposerDraft('Revise the pending Growth Plan: ');
    setWorkspaceMode('chat');
  }, [chat, session, setWorkspaceMode]);

  const startBackgroundTaskCreation = useCallback(
    (template?: ScanAutomationTemplate) => {
      if (routeMode === 'agents' && routeAgentTab !== 'tasks') {
        const query = new URLSearchParams();
        if (template) query.set('template', template.id);
        else query.set('background_task', 'true');
        router.push(`/app/agents?${query.toString()}`);
        return;
      }
      session.resetSession({ skipNavigation: true });
      setPendingInitialMessage(null);
      setComposerOpen(false);
      setEditAutomationOpen(false);
      setWorkspaceComposerIntent('background_task');
      setSelectedResearchModelId(RECOMMENDED_RESEARCH_MODEL_ID);
      setExecutionMode('standard');
      setBackgroundTaskDraft(
        template
          ? `Set up the “${template.name}” Background Task template for ${projectName}. Keep its required Skills and prepare the complete setup for review.`
          : ''
      );
      setMode('chat');
      replaceWorkspaceSearch(query => {
        query.delete('session');
        query.delete('mode');
        query.delete('tab');
        for (const key of CHAT_SESSION_QUERY_KEYS_TO_CLEAR) query.delete(key);
        query.delete('new_chat');
        query.delete('template');
        query.delete('background_task');
      });
    },
    [
      chat,
      projectName,
      replaceWorkspaceSearch,
      routeMode,
      routeAgentTab,
      router,
      session,
      setMode,
      setSelectedResearchModelId,
    ]
  );

  const backgroundTaskDeepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (!urlBackgroundTaskRequested) {
      backgroundTaskDeepLinkHandledRef.current = false;
      return;
    }
    if (
      (routeMode && routeMode !== 'agents') ||
      (routeMode === 'agents' && routeAgentTab !== 'tasks') ||
      backgroundTaskDeepLinkHandledRef.current
    )
      return;
    backgroundTaskDeepLinkHandledRef.current = true;
    startBackgroundTaskCreation();
  }, [routeMode, routeAgentTab, startBackgroundTaskCreation, urlBackgroundTaskRequested]);

  const agentTemplateDeepLinkLoadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlAgentTemplateId) {
      agentTemplateDeepLinkLoadRef.current = null;
      return;
    }
    if (!user || !projectId || !workspaceProjectReady || mode === 'agents') return;
    if (visibleTemplates.some(template => template.id === urlAgentTemplateId)) return;
    const requestKey = `${projectId}:${urlAgentTemplateId}`;
    if (agentTemplateDeepLinkLoadRef.current === requestKey) return;
    agentTemplateDeepLinkLoadRef.current = requestKey;
    const requestProjectId = projectId;
    void postAttune<{ templates?: ScanAutomationTemplate[] }>(
      '/api/attune/scan-automations/templates'
    )
      .then(payload => {
        if (workspaceProjectIdRef.current !== requestProjectId) return;
        setTemplates(payload.templates || []);
      })
      .catch(error => {
        if (workspaceProjectIdRef.current !== requestProjectId) return;
        agentTemplateDeepLinkLoadRef.current = null;
        if (!isOptionalAttunePreloadError(error)) {
          showWorkspaceRefreshError(
            error,
            'Failed to load Background Task template',
            'interactive'
          );
        }
      });
  }, [
    mode,
    postAttune,
    projectId,
    urlAgentTemplateId,
    user,
    visibleTemplates,
    workspaceProjectReady,
  ]);

  const agentTemplateDeepLinkHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlAgentTemplateId) {
      agentTemplateDeepLinkHandledRef.current = null;
      return;
    }
    if (agentTemplateDeepLinkHandledRef.current === urlAgentTemplateId) return;
    const template = visibleTemplates.find(item => item.id === urlAgentTemplateId);
    if (!template) return;
    agentTemplateDeepLinkHandledRef.current = urlAgentTemplateId;
    startBackgroundTaskCreation(template);
  }, [startBackgroundTaskCreation, urlAgentTemplateId, visibleTemplates]);

  const openAutomationEditor = useCallback(
    (automation: ScanAutomationRecord) => {
      resetPrepared();
      setComposerOpen(false);
      setTaskSlackDelivery(automation.slack_delivery || null);
      setTaskSlackDeliveryBaseline(automation.slack_delivery || null);
      setEditingAutomationId(automation.id);
      setEditingModelId(automation.model_id || modelSelection.selectedModelId || '');
      setComposerMode(automation.kind === 'custom_prompt' ? 'custom' : 'preset');
      setSelectedTemplateId(automation.template_id);
      setAutomationName(automationDisplayName(automation));
      setAutomationDescription(automation.description);
      setCustomPromptBody(automation.prompt_snapshot?.body || DEFAULT_CUSTOM_PROMPT);
      setTriggerType(automation.trigger.type);
      setDelayMinutes(automation.trigger.delay_minutes || 60);
      setScheduleDayOfWeek(clampWeekday(automation.trigger.day_of_week));
      const [firstMonthlyDay, secondMonthlyDay] = normalizeTwiceMonthlyDays(
        automation.trigger.days_of_month
      );
      setScheduleDayOfMonth(
        automation.trigger.type === 'twice_monthly_scan'
          ? firstMonthlyDay
          : clampMonthDay(automation.trigger.day_of_month, 31)
      );
      setScheduleSecondDayOfMonth(secondMonthlyDay);
      setEditRunNowSourceJobId('');
      setDataSources(automation.data_sources);
      setEditAutomationOpen(true);
    },
    [modelSelection, resetPrepared]
  );

  useEffect(() => {
    setTaskSlackDelivery(null);
    setTaskSlackDeliveryBaseline(null);
    setComposerOpen(false);
    setEditAutomationOpen(false);
  }, [projectId, user?.uid]);

  const taskSettingsId = searchParams.get('taskId');
  const taskSettingsHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!taskSettingsId) {
      taskSettingsHandledRef.current = null;
      return;
    }
    if (
      !workspaceProjectReady ||
      taskSettingsHandledRef.current === `${projectId}:${taskSettingsId}`
    )
      return;
    const task = visibleAutomations.find(item => item.id === taskSettingsId);
    if (!task) return;
    taskSettingsHandledRef.current = `${projectId}:${taskSettingsId}`;
    openAutomationEditor(task);
    clearWorkspaceSearchParams(['taskId']);
  }, [
    taskSettingsId,
    projectId,
    workspaceProjectReady,
    visibleAutomations,
    openAutomationEditor,
    clearWorkspaceSearchParams,
  ]);

  const openNorthStarEditor = useCallback(() => {
    setNorthStarTitle(northStar?.title || onboardingNorthStarDraft?.title || 'COMPANY.md');
    setNorthStarBody(
      northStar?.body_markdown ||
        onboardingNorthStarDraft?.bodyMarkdown ||
        (onboardingBlueprint?.north_star_thesis
          ? `# ${projectName}\n\n${onboardingBlueprint.north_star_thesis}`
          : '')
    );
    setPreparedNorthStar(null);
    setNorthStarOpen(true);
  }, [
    northStar?.body_markdown,
    northStar?.title,
    onboardingBlueprint?.north_star_thesis,
    onboardingNorthStarDraft?.bodyMarkdown,
    onboardingNorthStarDraft?.title,
    projectName,
  ]);

  useEffect(() => {
    if (!northStarDeepLink) {
      northStarDeepLinkHandledRef.current = false;
      return;
    }
    if (northStarDeepLinkHandledRef.current || agentsLoading) return;
    northStarDeepLinkHandledRef.current = true;
    setMode('agents');
    openNorthStarEditor();
    clearWorkspaceSearchParams(['northStar']);
  }, [agentsLoading, clearWorkspaceSearchParams, northStarDeepLink, openNorthStarEditor, setMode]);

  const prepareNorthStar = useCallback(async () => {
    setNorthStarBusy('prepare');
    try {
      const payload = await postAttune<PreparedNorthStar>('/api/attune/north-star/prepare-update', {
        title: northStarTitle,
        bodyMarkdown: northStarBody,
      });
      setPreparedNorthStar(payload);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to prepare North Star');
    } finally {
      setNorthStarBusy(null);
    }
  }, [northStarBody, northStarTitle, postAttune]);

  const confirmNorthStar = useCallback(async () => {
    if (!preparedNorthStar) return;
    setNorthStarBusy('confirm');
    try {
      await postAttune('/api/attune/north-star/confirm-update', {
        title: preparedNorthStar.normalizedDraft.title,
        bodyMarkdown: preparedNorthStar.normalizedDraft.bodyMarkdown,
        preflightToken: preparedNorthStar.preflightToken,
      });
      setNorthStarOpen(false);
      setPreparedNorthStar(null);
      if (cmoSetupDeepLink && aiCmoBetaEnabled) {
        try {
          await postAttune('/api/attune/growth-programs/setup-start');
          if (projectId) {
            window.sessionStorage.removeItem(`attune-ai-cmo-setup:${projectId}`);
          }
          setWorkspaceMode('growth-program');
          clearWorkspaceSearchParams(['cmoSetup']);
          toast.success('North Star updated. Growth audit started.');
        } catch (error) {
          setWorkspaceMode('integrations');
          toast.error(
            error instanceof Error
              ? `North Star updated. ${error.message}`
              : 'North Star updated. Start the growth audit from AI CMO setup.'
          );
        }
      } else {
        toast.success('North Star updated');
      }
      await loadAutomations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update North Star');
    } finally {
      setNorthStarBusy(null);
    }
  }, [
    clearWorkspaceSearchParams,
    aiCmoBetaEnabled,
    cmoSetupDeepLink,
    loadAutomations,
    postAttune,
    preparedNorthStar,
    projectId,
    setWorkspaceMode,
  ]);

  const toggleDataSource = useCallback(
    (source: ScanDataSource) => {
      resetPrepared();
      setDataSources(current => {
        if (current.includes(source)) {
          return current.length === 1 ? current : current.filter(item => item !== source);
        }
        return [...current, source];
      });
    },
    [resetPrepared]
  );

  const buildComposerInput = useCallback((): Record<string, unknown> => {
    if (composerMode === 'custom') {
      return {
        mode: 'custom',
        templateId: 'custom_growth_scan_v1',
        name: automationName,
        description: automationDescription,
        promptBody: customPromptBody,
        modelId: modelSelection.selectedModelId || undefined,
        triggerType,
        delayMinutes,
        dayOfWeek: scheduleDayOfWeek,
        dayOfMonth: scheduleDayOfMonth,
        daysOfMonth: normalizeTwiceMonthlyDays([scheduleDayOfMonth, scheduleSecondDayOfMonth]),
        dataSources,
        allowedTools: DEFAULT_ALLOWED_TOOLS.split(',')
          .map(tool => tool.trim())
          .filter(Boolean),
        createdVia: 'ui',
        slackDelivery: taskSlackDelivery,
      };
    }
    return {
      mode: 'preset',
      templateId: selectedTemplateId,
      name: automationName || selectedTemplate?.name,
      description: automationDescription || selectedTemplate?.description,
      modelId: modelSelection.selectedModelId || undefined,
      triggerType,
      delayMinutes,
      dayOfWeek: scheduleDayOfWeek,
      dayOfMonth: scheduleDayOfMonth,
      daysOfMonth: normalizeTwiceMonthlyDays([scheduleDayOfMonth, scheduleSecondDayOfMonth]),
      createdVia: 'ui',
      slackDelivery: taskSlackDelivery,
    };
  }, [
    taskSlackDelivery,
    automationDescription,
    automationName,
    composerMode,
    customPromptBody,
    dataSources,
    delayMinutes,
    modelSelection.selectedModelId,
    scheduleDayOfMonth,
    scheduleDayOfWeek,
    scheduleSecondDayOfMonth,
    selectedTemplate?.description,
    selectedTemplate?.name,
    selectedTemplateId,
    triggerType,
  ]);

  const prepareAutomation = useCallback(async () => {
    if (selectedTemplateLocked) {
      toast.error(
        'Connect WordPress.com, Webflow, Shopify, or Custom MCP before creating execution tasks'
      );
      return;
    }
    const draftInput = buildComposerInput();
    setBusyAgent({ id: 'prepare-automation', action: 'prepare' });
    try {
      const payload = await postAttune<PreparedAutomation>(
        '/api/attune/scan-automations/prepare-create',
        draftInput
      );
      setPrepared(payload);
      setPreparedDraftInput(draftInput);
      setAgentContractOpen(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to prepare background task');
    } finally {
      setBusyAgent(null);
    }
  }, [buildComposerInput, postAttune, selectedTemplateLocked]);

  const confirmPrepared = useCallback(async () => {
    if (!prepared || !preparedDraftInput) return;
    setBusyAgent({ id: 'confirm-automation', action: 'confirm' });
    try {
      const payload = await postAttune<{ automation?: ScanAutomationRecord }>(
        '/api/attune/scan-automations/confirm-create',
        {
          ...preparedDraftInput,
          preflightToken: prepared.preflightToken,
        }
      );
      setComposerOpen(false);
      setAgentContractOpen(false);
      setPrepared(null);
      setPreparedDraftInput(null);
      if (payload.automation?.id) setSelectedAutomationId(payload.automation.id);
      toast.success('Background task created');
      await loadAutomations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create background task');
    } finally {
      setBusyAgent(null);
    }
  }, [loadAutomations, postAttune, prepared, preparedDraftInput]);

  const handleRunNow = useCallback(
    async (automation: ScanAutomationRecord) => {
      const sourceJobId =
        automation.trigger.type === 'sampler_completed_delay' ? runNowSourceJobId.trim() : '';
      if (automation.trigger.type === 'sampler_completed_delay' && !sourceJobId) return;
      setBusyAgent({ id: automation.id, action: 'run' });
      try {
        const payload = await postAttune<{ run?: ScanRunRecord | null }>(
          '/api/attune/scan-automations/run-now',
          {
            requestId: crypto.randomUUID(),
            automationId: automation.id,
            modelId: automation.model_id || modelSelection.selectedModelId || undefined,
            ...(sourceJobId ? { sourceJobId } : {}),
          }
        );
        if (payload.run) {
          setLastRuns(current => ({ ...current, [automation.id]: payload.run || null }));
        }
        await Promise.all([loadAutomations(), loadFeed()]);
        toast.success('Background task started');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to run background task');
      } finally {
        setBusyAgent(null);
      }
    },
    [loadAutomations, loadFeed, modelSelection.selectedModelId, postAttune, runNowSourceJobId]
  );

  const handleAutomationModelChange = useCallback(
    async (automation: ScanAutomationRecord, modelId: string) => {
      if (!modelId || automation.model_id === modelId) return;
      setBusyAgent({ id: automation.id, action: 'model' });
      try {
        const payload = await postAttune<{ automation?: ScanAutomationRecord | null }>(
          '/api/attune/scan-automations/update-model',
          {
            automationId: automation.id,
            modelId,
          }
        );
        if (payload.automation) {
          setAutomations(current =>
            current.map(item => (item.id === automation.id ? payload.automation! : item))
          );
        }
        toast.success('Background Task model updated');
        await loadAutomations();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to update Background Task model'
        );
      } finally {
        setBusyAgent(null);
      }
    },
    [loadAutomations, postAttune]
  );

  const persistEditedAutomation = useCallback(
    async (automation: ScanAutomationRecord) => {
      const payload = await postAttune<{ automation?: ScanAutomationRecord | null }>(
        '/api/attune/scan-automations/update-config',
        {
          automationId: automation.id,
          ...(taskSlackDeliveryChanged ? { slackDelivery: taskSlackDelivery } : {}),
          name: automationName,
          description: automationDescription,
          modelId: editingModelId || automation.model_id,
          triggerType,
          delayMinutes,
          dayOfWeek: scheduleDayOfWeek,
          dayOfMonth: scheduleDayOfMonth,
          daysOfMonth: normalizeTwiceMonthlyDays([scheduleDayOfMonth, scheduleSecondDayOfMonth]),
          dataSources,
          ...(automation.kind === 'custom_prompt' ? { promptBody: customPromptBody } : {}),
        }
      );
      if (taskSlackDeliveryChanged) setTaskSlackDeliveryBaseline(taskSlackDelivery);
      const savedAutomation = payload.automation || automation;
      if (payload.automation) {
        setAutomations(current =>
          current.map(item => (item.id === automation.id ? payload.automation! : item))
        );
      }
      return savedAutomation;
    },
    [
      taskSlackDelivery,
      taskSlackDeliveryChanged,
      automationDescription,
      automationName,
      customPromptBody,
      dataSources,
      delayMinutes,
      editingModelId,
      postAttune,
      scheduleDayOfMonth,
      scheduleDayOfWeek,
      scheduleSecondDayOfMonth,
      triggerType,
    ]
  );

  const handleSaveAutomation = useCallback(async () => {
    if (!editingAutomation) return;
    setBusyAgent({ id: editingAutomation.id, action: 'edit' });
    try {
      await persistEditedAutomation(editingAutomation);
      setEditAutomationOpen(false);
      setEditingAutomationId(null);
      setEditingModelId('');
      toast.success('Background task updated');
      await loadAutomations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update background task');
    } finally {
      setBusyAgent(null);
    }
  }, [editingAutomation, loadAutomations, persistEditedAutomation]);

  const handleRunEditedAutomation = useCallback(async () => {
    if (!editingAutomation) return;
    const runsApprovedProgramConfiguration = Boolean(
      editingAutomation.program_context?.specialist_id
    );
    const sourceJobId =
      editingRunTriggerType === 'sampler_completed_delay' ? editRunNowSourceJobId.trim() : '';
    if (editingRunTriggerType === 'sampler_completed_delay' && !sourceJobId) return;

    setBusyAgent({ id: editingAutomation.id, action: 'run' });
    try {
      const automationToRun = runsApprovedProgramConfiguration
        ? editingAutomation
        : await persistEditedAutomation(editingAutomation);
      const runModelId = runsApprovedProgramConfiguration
        ? automationToRun.model_id
        : automationToRun.model_id || editingModelId;
      const payload = await postAttune<{ run?: ScanRunRecord | null }>(
        '/api/attune/scan-automations/run-now',
        {
          requestId: crypto.randomUUID(),
          automationId: automationToRun.id,
          modelId: runModelId || undefined,
          ...(sourceJobId ? { sourceJobId } : {}),
        }
      );
      if (payload.run) {
        setLastRuns(current => ({ ...current, [automationToRun.id]: payload.run || null }));
      }
      await Promise.all([loadAutomations(), loadFeed()]);
      toast.success('Background task started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to run background task');
    } finally {
      setBusyAgent(null);
    }
  }, [
    editingAutomation,
    editingModelId,
    editRunNowSourceJobId,
    editingRunTriggerType,
    loadAutomations,
    loadFeed,
    persistEditedAutomation,
    postAttune,
  ]);

  const handleToggleAutomationStatus = useCallback(
    async (automation: ScanAutomationRecord) => {
      const action = automation.status === 'enabled' ? 'pause' : 'resume';
      setBusyAgent({ id: automation.id, action });
      try {
        await postAttune(`/api/attune/scan-automations/${action}`, {
          automationId: automation.id,
        });
        await loadAutomations();
        toast.success(action === 'pause' ? 'Background task paused' : 'Background task resumed');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to update background task');
      } finally {
        setBusyAgent(null);
      }
    },
    [loadAutomations, postAttune]
  );

  const handleFeedIssueOpen = useCallback(
    (event: GrowthActivityEventRecord) => {
      if (event.issue?.id) {
        router.push(`/app/tasks/${encodeURIComponent(event.issue.id)}`);
        return;
      }
      pendingIssueTabResolutionRef.current = null;
      setMode('tasks');
      setGrowthWorkTab('activity');
      setWorkspaceTaskSearch('activity');
      issueDeepLinkHandledRef.current = null;
      setSelectedIssueId(null);
      setTaskDrawerOpen(false);
    },
    [router, setMode, setWorkspaceTaskSearch]
  );

  const handleHomeTaskIssueOpen = useCallback(
    (issue: GrowthIssueRecord) => {
      router.push(`/app/tasks/${encodeURIComponent(issue.id)}`);
    },
    [router]
  );

  const openActivityFeed = useCallback(() => {
    pendingIssueTabResolutionRef.current = null;
    issueDeepLinkHandledRef.current = null;
    setGrowthWorkTab('activity');
    setMode('tasks');
    setWorkspaceTaskSearch('activity');
    setSelectedIssueId(null);
    setTaskDrawerOpen(false);
  }, [setMode, setWorkspaceTaskSearch]);

  const hasWorkspaceChatMessages = useMemo(
    () => session.messages.some(isRenderableMessage),
    [session.messages]
  );
  const showWorkspaceHome =
    isDesktop === true &&
    !isWorkspaceViewer &&
    !activeEditor &&
    !sessionDeepLinkId &&
    !session.sessionId &&
    !hasWorkspaceChatMessages &&
    !chat.streamingText &&
    !chat.thinkingText &&
    chat.streamTimeline.length === 0 &&
    chat.activeTools.length === 0 &&
    chat.scheduledCommands.length === 0 &&
    !loadingWorkspaceSession &&
    !chat.isLoading;
  const handleSessionVisibilityChange = useCallback(
    async (visibility: 'private' | 'workspace') => {
      await session.updateSessionSharing(visibility);
    },
    [session]
  );
  const handleForkSharedSession = useCallback(async () => {
    if (forkingSharedSession) return;
    setForkingSharedSession(true);
    try {
      if (await session.forkCurrentSession()) toast.success('Private fork created');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to fork thread');
    } finally {
      setForkingSharedSession(false);
    }
  }, [forkingSharedSession, session]);
  useEffect(() => {
    if (!user || !workspaceProjectReady || mode !== 'data') return;
    void loadHomeDashboardData();
  }, [loadHomeDashboardData, mode, user, workspaceProjectReady]);
  const handleTaskDrawerOpenChange = useCallback(
    (open: boolean) => {
      setTaskDrawerOpen(open);
      if (!open && issueDeepLinkId) {
        clearWorkspaceSearchParams(['issue']);
      }
    },
    [clearWorkspaceSearchParams, issueDeepLinkId]
  );

  const slackDeliveryControl = (
    <SlackTaskDeliveryField
      key={`${projectId}:${user?.uid}:${editingAutomationId || 'new'}`}
      value={taskSlackDelivery}
      onChange={value => {
        setTaskSlackDelivery(value);
        resetPrepared();
      }}
      load={input => postAttune('/api/attune/scan-automations/slack-destinations', input)}
    />
  );

  const scheduleControls = (
    <Box
      bg="ui.surface.card"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="glassInput"
      p="3.5"
    >
      <Box mb="3">
        <Text fontWeight="800" fontSize="sm" color="ui.text.primary">
          Schedule
        </Text>
        <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35">
          Choose when this task runs.
        </Text>
      </Box>
      <SimpleGrid columns={{ base: 2, md: 4 }} gap="2" role="group" aria-label="Task schedule">
        {[
          ...(editingAutomation &&
          (editingAutomation.trigger.type === 'manual' ||
            editingAutomation.trigger.type === 'sampler_completed_delay')
            ? LEGACY_SCHEDULE_OPTIONS
            : []),
          ...NEW_TASK_SCHEDULE_OPTIONS,
        ].map(option => {
          const selected = triggerType === option.value;
          return (
            <Button
              key={option.value}
              type="button"
              variant="plain"
              aria-pressed={selected}
              display="block"
              w="full"
              h="auto"
              whiteSpace="normal"
              textAlign="left"
              px="3"
              py="2.5"
              borderWidth="1px"
              borderColor={selected ? 'ui.border.accent' : 'ui.border.subtle'}
              borderRadius="glassInput"
              bg={selected ? 'ui.surface.selected' : 'ui.surface.input'}
              color="ui.text.primary"
              minH="44px"
              _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.default' }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.accent' }}
              onClick={() => {
                resetPrepared();
                setTriggerType(option.value);
                if (option.value === 'sampler_completed_delay') setDelayMinutes(60);
              }}
            >
              <HStack justify="space-between" align="start" gap="2">
                <Text fontSize="sm" fontWeight="800" lineHeight="1.2">
                  {option.label}
                </Text>
                {selected ? (
                  <Icon boxSize="4" color="status.success.fg">
                    <CheckCircle2 />
                  </Icon>
                ) : null}
              </HStack>
            </Button>
          );
        })}
      </SimpleGrid>

      {triggerType === 'weekly_scan' ? (
        <Box mt="3">
          <Text
            id="agent-schedule-weekday-label"
            mb="1.5"
            fontSize="xs"
            color="ui.text.tertiary"
            textTransform="uppercase"
          >
            Day
          </Text>
          <HStack
            gap="1.5"
            flexWrap="wrap"
            role="group"
            aria-labelledby="agent-schedule-weekday-label"
          >
            {WEEKDAYS.map((day, index) => {
              const selected = scheduleDayOfWeek === index;
              return (
                <Button
                  key={day}
                  type="button"
                  size="xs"
                  minH="44px"
                  variant={selected ? 'solid' : 'outline'}
                  aria-pressed={selected}
                  onClick={() => {
                    resetPrepared();
                    setScheduleDayOfWeek(index);
                  }}
                >
                  {day}
                </Button>
              );
            })}
          </HStack>
        </Box>
      ) : null}

      {triggerType === 'twice_monthly_scan' ? (
        <SimpleGrid mt="3" columns={{ base: 1, sm: 2 }} gap="3" maxW="540px">
          <Field.Root>
            <Field.Label mb="1.5" fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
              First Day
            </Field.Label>
            <AttuneSelect
              ariaLabel="First day of twice-monthly schedule"
              value={String(scheduleDayOfMonth)}
              onValueChange={value => {
                resetPrepared();
                const nextDay = clampMonthDay(Number(value || 1), 28);
                setScheduleDayOfMonth(nextDay);
                if (nextDay === scheduleSecondDayOfMonth) {
                  setScheduleSecondDayOfMonth(nextDay === 15 ? 16 : 15);
                }
              }}
              options={NEW_TASK_MONTH_DAYS.map(day => ({
                value: String(day),
                label: `Day ${day}`,
              }))}
              size="sm"
              w="full"
            />
          </Field.Root>
          <Field.Root>
            <Field.Label mb="1.5" fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
              Second Day
            </Field.Label>
            <AttuneSelect
              ariaLabel="Second day of twice-monthly schedule"
              value={String(scheduleSecondDayOfMonth)}
              onValueChange={value => {
                resetPrepared();
                const nextDay = clampMonthDay(Number(value || 15), 28);
                setScheduleSecondDayOfMonth(nextDay);
                if (nextDay === scheduleDayOfMonth) {
                  setScheduleDayOfMonth(nextDay === 1 ? 2 : 1);
                }
              }}
              options={NEW_TASK_MONTH_DAYS.map(day => ({
                value: String(day),
                label: `Day ${day}`,
              }))}
              size="sm"
              w="full"
            />
          </Field.Root>
        </SimpleGrid>
      ) : triggerType === 'monthly_scan' ? (
        <Field.Root mt="3" maxW="260px">
          <Field.Label mb="1.5" fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
            Day of Month
          </Field.Label>
          <AttuneSelect
            ariaLabel="Day of month"
            value={String(scheduleDayOfMonth)}
            onValueChange={value => {
              resetPrepared();
              setScheduleDayOfMonth(clampMonthDay(Number(value || 1), editingAutomation ? 31 : 28));
            }}
            options={(editingAutomation ? LEGACY_MONTH_DAYS : NEW_TASK_MONTH_DAYS).map(day => ({
              value: String(day),
              label: `Day ${day}`,
            }))}
            size="sm"
            w="full"
          />
        </Field.Root>
      ) : null}

      <Box mt="3">
        {triggerType === 'manual' ? (
          <Text fontSize="xs" color="ui.text.tertiary" lineHeight="1.45">
            Runs only when you start it.
          </Text>
        ) : triggerType === 'sampler_completed_delay' ? (
          <Text fontSize="xs" color="ui.text.tertiary" lineHeight="1.45">
            Legacy schedule: runs after a completed data refresh.
          </Text>
        ) : (
          <Text fontSize="xs" color="ui.text.tertiary" lineHeight="1.45">
            {scheduledScanDate
              ? `Next scheduled run: ${formatCalendarDay(scheduledScanDate)}.`
              : 'The next run will use the latest completed project data.'}
          </Text>
        )}
      </Box>
    </Box>
  );
  const agentComposerModelControl = (
    <Box
      bg="ui.surface.card"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="glassInput"
      p="3.5"
    >
      <HStack gap="3" justify="space-between" align="center" flexWrap="wrap">
        <Box minW={0}>
          <Text fontWeight="800" fontSize="sm" color="ui.text.primary">
            Model
          </Text>
          <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35">
            {researchModelLabel(modelSelection.models, modelSelection.selectedModelId)}
          </Text>
        </Box>
        <ModelSelector
          models={modelSelection.models}
          selectedModel={modelSelection.selectedModel}
          selectedModelId={modelSelection.selectedModelId}
          disabled={modelSelection.models.length === 0}
          onChange={modelId => {
            resetPrepared();
            modelSelection.setSelectedModelId(modelId);
          }}
        />
      </HStack>
    </Box>
  );
  const editAutomationModelControl = (
    <Box
      bg="ui.surface.card"
      borderWidth="1px"
      borderColor="ui.border.subtle"
      borderRadius="glassInput"
      p="3.5"
    >
      <HStack gap="3" justify="space-between" align="center" flexWrap="wrap">
        <Box minW={0}>
          <Text fontWeight="800" fontSize="sm" color="ui.text.primary">
            Model
          </Text>
          <Text mt="0.5" fontSize="xs" color="ui.text.secondary" lineHeight="1.35">
            {researchModelLabel(editingModels, editingModelId)}
          </Text>
        </Box>
        <ModelSelector
          models={editingModels}
          selectedModel={editingSelectedModel}
          selectedModelId={editingModelId}
          disabled={editingModels.length === 0}
          onChange={setEditingModelId}
        />
      </HStack>
    </Box>
  );
  const onboardingNavigation = onboardingChatTarget ? (
    <AppShellToolbarTabs>
      <AppShellToolbarTabButton
        active
        icon={<Sparkles size={16} />}
        label="Onboarding"
        onClick={() => {}}
      >
        <HStack gap="1.5" minW={0}>
          <Text as="span">Onboarding</Text>
          {onboardingChatTarget.workspaceName ? (
            <Text as="span" color="ui.text.tertiary" fontSize="0.75rem" fontWeight="600">
              {onboardingChatTarget.workspaceName}
            </Text>
          ) : null}
        </HStack>
      </AppShellToolbarTabButton>
    </AppShellToolbarTabs>
  ) : null;

  if (!user) return <WorkspaceStartupLoadingShell />;
  const projectStartupPending = !projectReady || projectLoading;
  const standardHomeRequested =
    (!urlMode || urlMode === 'chat') && !sessionDeepLinkId && !onboardingWorkspaceRequested;
  if (projectStartupPending && (!currentProject || !standardHomeRequested)) {
    return <WorkspaceStartupLoadingShell />;
  }

  if (onboardingChatTarget) {
    return (
      <Box h="full" minH={0} overflow="hidden">
        <AppShellToolbarPortal navigation={onboardingNavigation} />
        <ChatPanel
          projectId={null}
          attachmentProjectId={
            onboardingChatTarget.projectId || activeOnboardingSessionProjectId || null
          }
          resolveAttachmentProjectId={resolveOnboardingAttachmentProjectId}
          sessionId={session.sessionId}
          promptId={session.promptId}
          promptText={session.promptText || 'Onboarding'}
          messages={session.messages}
          streamingText={chat.streamingText}
          thinkingText={chat.thinkingText}
          responsePhase={chat.responsePhase}
          streamTimeline={chat.streamTimeline}
          activeTools={chat.activeTools}
          subagents={chat.subagents}
          onCancelSubagent={chat.cancelSubagent}
          onReviewSubagentApproval={chat.reviewSubagentApproval}
          isLoading={chat.isLoading}
          isSessionPending={workspaceSessionPending}
          onSendMessage={handleOnboardingSendMessage}
          onQueueMessage={handleOnboardingSendMessage}
          onSteerMessage={chat.steerMessageDirect}
          activeTurnId={chat.activeTurnId}
          activeTurnSelectedSkills={chat.activeTurnSelectedSkills}
          activeTurnExecutionMode={chat.activeTurnExecutionMode}
          activeTurnModelId={chat.activeTurnModelId}
          activeTurnReasoningEffort={chat.activeTurnReasoningEffort}
          activeTurnActiveReportId={chat.activeTurnActiveReportId}
          latestAppliedSteerReceipt={chat.latestAppliedSteerReceipt}
          documentRestoration={composerDocumentRestoration}
          onDocumentRestorationConsumed={consumeComposerDocumentRestoration}
          models={onboardingModels}
          selectedModel={onboardingSelectedModel}
          selectedModelId={onboardingSelectedModel?.id || ''}
          onModelChange={modelSelection.setSelectedModelId}
          executionMode={executionMode}
          executionModes={ONBOARDING_EXECUTION_MODES}
          onExecutionModeChange={setExecutionMode}
          runStatus={chat.runStatus}
          onContinueRun={chat.continueRun}
          onCancelRun={chat.cancelRun}
          scheduledCommands={chat.scheduledCommands}
          onSteerScheduledCommand={chat.steerScheduledCommand}
          onCancelScheduledCommand={chat.cancelScheduledCommand}
          onClearScheduledCommands={chat.clearScheduledCommands}
          onStopCurrentResponse={chat.stopCurrentResponse}
          onRetry={handleRetryMessage}
          emptyTitle="Find the GTM work your market is asking for."
          emptySubtitle="Enter your website. Attune will map your market and prepare the first research plan for your approval."
          composerPlaceholder="Want a change? Tell Attune what to update…"
          initialWebsiteCapture
          optimisticMessage={pendingInitialMessage?.content}
        />
      </Box>
    );
  }

  if (!currentProject && session.sessionScope?.kind !== 'workspace' && !sessionDeepLinkId) {
    return (
      <Center h="full" minH="360px" px="4">
        <Box
          layerStyle="dataTableSurface"
          maxW="440px"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          borderRadius="0"
          boxShadow="none"
          p="5"
        >
          <Text fontSize="md" fontWeight="800" color="ui.text.primary">
            No project access
          </Text>
          <Text mt="2" fontSize="sm" color="ui.text.secondary">
            Your account does not have a project or a workspace where a project can be created.
          </Text>
        </Box>
      </Center>
    );
  }

  const chatContent = showWorkspaceHome ? (
    <WorkspaceHome
      projectName={projectName}
      user={user}
      projectId={projectId}
      attachmentProjectId={session.sessionId ? session.sessionProjectId : projectId}
      models={modelSelection.models}
      selectedModel={modelSelection.selectedModel}
      selectedModelId={modelSelection.selectedModelId}
      onModelChange={modelSelection.setSelectedModelId}
      executionMode={executionMode}
      executionModes={modelSelection.executionModes}
      onExecutionModeChange={setExecutionMode}
      loading={chat.isLoading}
      sessionPending={workspaceSessionPending || canvasInitialBuildPending}
      onSend={handleSendMessage}
      documentRestoration={composerDocumentRestoration}
      onDocumentRestorationConsumed={consumeComposerDocumentRestoration}
      onStop={chat.stopCurrentResponse}
      activityEvents={feedEvents}
      activityLoading={feedLoading}
      taskIssues={homeTaskIssues}
      taskIssuesLoading={homeTaskIssuesLoading}
      homeIssuesError={homeIssuesError}
      homeActivityError={homeActivityError}
      onOpenTaskIssue={handleHomeTaskIssueOpen}
      onOpenActivityEvent={handleFeedIssueOpen}
      backgroundTaskIntent={workspaceComposerIntent === 'background_task'}
      backgroundTaskDraft={backgroundTaskDraft}
      growthPlanComposerDraft={growthPlanComposerDraft}
      initialComposerDraft={newChatRequested ? '' : urlComposerDraft}
      initialComposerSkillId={newChatRequested ? null : urlComposerSkill}
      aiCmoEnabled={
        aiCmoBetaEnabled &&
        Boolean(
          currentProject?.onboarding_job_id ||
            currentProject?.onboarding_completed ||
            homeAiCmoConfigured
        )
      }
      aiCmoLoading={homeAiCmoConfigured === undefined}
      aiCmoConfigured={homeAiCmoConfigured}
      aiCmoStatusError={homeAiCmoStatusError}
      onBackgroundTaskIntentChange={handleBackgroundTaskIntentChange}
      onBackgroundTaskDraftConsumed={consumeBackgroundTaskDraft}
      onGrowthPlanComposerDraftConsumed={consumeGrowthPlanComposerDraft}
      onInitialComposerIntentConsumed={consumeInitialComposerIntent}
    />
  ) : (
    <ChatPanel
      projectId={projectId}
      sessionWorkspaceId={
        session.sessionScope?.kind === 'workspace' ? session.sessionScope.workspaceId : null
      }
      attachmentProjectId={session.sessionId ? session.sessionProjectId : projectId}
      sessionId={session.sessionId}
      websitePreviewSessionId={sessionDeepLinkId || session.sessionId}
      promptId={session.promptId}
      promptText={session.promptText || sessionTitle}
      messages={session.messages}
      streamingText={chat.streamingText || slackPublicDelivery(session.slackDelivery).text}
      thinkingText={chat.thinkingText}
      responsePhase={chat.responsePhase}
      streamTimeline={chat.streamTimeline}
      activeTools={chat.activeTools}
      subagents={chat.subagents}
      onCancelSubagent={chat.cancelSubagent}
      onReviewSubagentApproval={chat.reviewSubagentApproval}
      isLoading={chat.isLoading}
      isSessionPending={workspaceSessionPending}
      composerDisabled={
        canvasInitialBuildPending ||
        (!chat.isLoading && slackPublicDelivery(session.slackDelivery).active)
      }
      onSendMessage={handleSendMessage}
      onQueueMessage={handleSendMessage}
      onSteerMessage={chat.steerMessageDirect}
      activeTurnId={chat.activeTurnId}
      activeTurnSelectedSkills={chat.activeTurnSelectedSkills}
      activeTurnExecutionMode={chat.activeTurnExecutionMode}
      activeTurnModelId={chat.activeTurnModelId}
      activeTurnReasoningEffort={chat.activeTurnReasoningEffort}
      activeTurnActiveReportId={chat.activeTurnActiveReportId}
      latestAppliedSteerReceipt={chat.latestAppliedSteerReceipt}
      documentRestoration={composerDocumentRestoration}
      onDocumentRestorationConsumed={consumeComposerDocumentRestoration}
      models={modelSelection.models}
      selectedModel={modelSelection.selectedModel}
      selectedModelId={modelSelection.selectedModelId}
      onModelChange={modelSelection.setSelectedModelId}
      executionMode={executionMode}
      executionModes={modelSelection.executionModes}
      onExecutionModeChange={setExecutionMode}
      runStatus={chat.runStatus}
      onContinueRun={chat.continueRun}
      onCancelRun={chat.cancelRun}
      scheduledCommands={chat.scheduledCommands}
      onSteerScheduledCommand={chat.steerScheduledCommand}
      onCancelScheduledCommand={chat.cancelScheduledCommand}
      onClearScheduledCommands={chat.clearScheduledCommands}
      onStopCurrentResponse={chat.stopCurrentResponse}
      onRetry={handleRetryMessage}
      onRefreshSession={refreshOpenThread}
      aiCmoEnabled={aiCmoBetaEnabled}
      onTurnIntoReport={handleTurnIntoReport}
      turningIntoReportMessageId={turningIntoReportMessageId}
      backgroundTask={{
        active: workspaceComposerIntent === 'background_task',
        draft: backgroundTaskDraft,
        onActiveChange: handleBackgroundTaskIntentChange,
        onDraftConsumed: consumeBackgroundTaskDraft,
      }}
      readOnly={isWorkspaceViewer}
      ownerDisplayName={session.ownerDisplayName}
      onFork={isWorkspaceViewer ? () => void handleForkSharedSession() : undefined}
      isForking={forkingSharedSession}
      initialSelectedSkillId={urlSkill}
      initialComposerDraft={growthPlanComposerDraft || (newChatRequested ? '' : urlComposerDraft)}
      initialComposerSkillId={newChatRequested ? null : urlComposerSkill}
      onInitialComposerIntentConsumed={consumeChatComposerIntent}
      emptyTitle={
        activeEditor === 'canvas'
          ? 'What shall we create today?'
          : activeEditor === 'website'
            ? 'What shall we build today?'
            : activeEditor === 'design-system'
              ? 'How should Attune look and feel?'
              : undefined
      }
      emptySubtitle={
        canvasInitialBuildPending
          ? 'Preparing your Canvas. Chat becomes available when the first build finishes.'
          : activeEditor === 'canvas'
            ? 'Describe the deck, report, chart, one-pager, or interactive asset you want to make.'
            : activeEditor === 'website'
              ? 'Describe the page, section, or website change you want to make.'
              : activeEditor === 'design-system'
                ? 'Ask Attune to create or refine the visual system used by future website and Canvas edits.'
                : undefined
      }
      composerPlaceholder={
        canvasInitialBuildPending
          ? 'Waiting for the initial Canvas build…'
          : activeEditor === 'canvas'
            ? 'Describe what you want to create…'
            : activeEditor === 'website'
              ? 'Describe the website change you want to make…'
              : activeEditor === 'design-system'
                ? 'Describe the visual system you want…'
                : undefined
      }
    />
  );

  return (
    <Box h="full" minH={0} overflow="hidden" bg="transparent">
      <Flex h="full" minH={0} direction="column">
        {(mode === 'chat' || (mode === 'agents' && backgroundTaskTab === 'tasks')) &&
        !showWorkspaceHome ? (
          <AppShellHeaderPortal>
            <ThreadHeader
              key={session.sessionId || 'new-thread'}
              title={sessionTitle}
              titleTooltip={
                isWorkspaceViewer
                  ? `${sessionTitle} · Shared by ${session.ownerDisplayName || 'a workspace member'}`
                  : sessionTitle
              }
              onRename={
                session.sessionId && session.accessRole === 'owner'
                  ? session.renameSession
                  : undefined
              }
              renaming={session.isRenamingSession}
              onShare={
                session.sessionId && session.accessRole === 'owner' && !session.sessionOrigin
                  ? () => setShareDialogOpen(true)
                  : undefined
              }
            />
          </AppShellHeaderPortal>
        ) : null}
        {(mode === 'chat' || (mode === 'agents' && backgroundTaskTab === 'tasks')) &&
          session.sessionOrigin && (
            <SlackConversationBanner
              origin={session.sessionOrigin}
              onPrivateFork={() => void handleForkSharedSession()}
              busy={chat.isLoading || forkingSharedSession}
              progress={slackPublicDelivery(session.slackDelivery).progress}
              deliveryStatus={
                typeof session.slackDelivery?.status === 'string'
                  ? session.slackDelivery.status
                  : undefined
              }
              onStop={() => {
                if (!user || !session.sessionId) return;
                void user
                  .getIdToken()
                  .then(token =>
                    fetch('/api/integrations/slack/threads/stop', {
                      method: 'POST',
                      headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ sessionId: session.sessionId }),
                    })
                  )
                  .then(response => {
                    if (!response.ok)
                      throw new Error(
                        'Unable to stop this conversation. Check your access and retry.'
                      );
                  })
                  .catch(error => toast.error(error.message));
              }}
            />
          )}
        <Box flex="1" minH={0} overflow="hidden">
          {(mode === 'chat' || (mode === 'agents' && backgroundTaskTab === 'tasks')) &&
          unavailableSessionId &&
          unavailableSessionId === sessionDeepLinkId ? (
            <Center h="full" px="4">
              <Box maxW="440px" textAlign="center">
                <Text fontSize="lg" fontWeight="800" color="ui.text.primary">
                  Thread unavailable
                </Text>
                <Text mt="2" fontSize="sm" color="ui.text.secondary">
                  This thread is private, no longer shared, expired, or unavailable to your account.
                </Text>
                <Button mt="4" onClick={handleUnavailableSessionDismiss}>
                  Back to workspace
                </Button>
              </Box>
            </Center>
          ) : mode === 'chat' ? (
            chatContent
          ) : mode === 'data' ? (
            <DataControlCenter />
          ) : mode === 'growth-program' ? (
            <GrowthPlanWorkspace
              user={user}
              projectId={projectId}
              onEditProposal={editGrowthPlanInComposer}
            />
          ) : mode === 'tasks' ? (
            <GrowthWorkCenter
              user={user}
              projectId={projectId}
              issues={workspaceTaskIssues}
              selectedIssue={selectedIssue}
              summary={growthSummary}
              taskListQuery={growthTaskListQuery}
              loading={issuesLoading}
              loadedTab={loadedGrowthWorkTab}
              loadErrorTab={growthWorkLoadErrorTab}
              pagination={issuePagination}
              activeTab={growthWorkTab}
              activityEvents={feedEvents}
              activityLoading={feedLoading}
              loadingMore={loadingMoreIssues}
              onTabChange={tab => {
                setGrowthWorkTab(tab);
                setWorkspaceTaskSearch(tab);
              }}
              onTaskListQueryChange={setGrowthTaskListQuery}
              onOpenActivityIssue={handleFeedIssueOpen}
              onRefresh={() => void loadIssues()}
              onRefreshActivity={() => void loadFeed()}
              onLoadMore={() => void loadMoreIssues()}
              onSetupAgents={() => router.push('/app/agents?background_task=true')}
              initialApprovalId={urlApprovalId}
              canManageApprovals={
                currentProject?.current_user_role === 'owner' ||
                currentProject?.current_user_role === 'admin'
              }
              canEditTasks={['owner', 'admin', 'editor'].includes(
                currentProject?.current_user_role || ''
              )}
            />
          ) : mode === 'integrations' ? (
            <IntegrationsWorkspace
              workspaceId={
                currentProject?.workspace_id ||
                session.sessionScope?.workspaceId ||
                `personal_${user.uid}`
              }
              projectId={projectId}
              canManage={
                currentProject?.current_user_role === 'owner' ||
                currentProject?.current_user_role === 'admin'
              }
              hasProject={Boolean(projectId)}
              workspaceExportsEnabled={workspaceExportsEnabled}
            />
          ) : (
            <AgentWorkspace
              chatContent={chatContent}
              activeTab={backgroundTaskTab}
              automations={visibleAutomations}
              enabledAgentCount={automations.filter(item => item.status === 'enabled').length}
              totalAutomationCount={automations.length}
              templates={visibleTemplates}
              prompts={prompts}
              runs={scanRuns}
              runsLoading={scanRunsLoading}
              runStatusFilter={scanRunStatusFilter}
              models={modelSelection.models}
              selectedModel={modelSelection.selectedModel}
              selectedModelId={modelSelection.selectedModelId}
              selectedAutomation={selectedAutomation}
              selectedRun={selectedRun}
              lastRuns={lastRuns}
              northStar={northStar}
              deliveryConnections={deliveryConnections}
              loading={agentsLoading}
              busyAgent={busyAgent}
              runNowSourceJobId={runNowSourceJobId}
              automationStatusFilter={automationStatusFilter}
              automationTriggerFilter={automationTriggerFilter}
              onTabChange={setWorkspaceBackgroundTaskSearch}
              onRefresh={() => void refreshAgentWorkspace()}
              onCreateTask={() => startBackgroundTaskCreation()}
              onAdvancedSetup={() => openComposer('preset')}
              onUseTemplate={startBackgroundTaskCreation}
              onEditAutomation={openAutomationEditor}
              onEditNorthStar={() => router.push('/app/north-star')}
              onModelChange={modelSelection.setSelectedModelId}
              onAutomationModelChange={(automation, modelId) =>
                void handleAutomationModelChange(automation, modelId)
              }
              onAutomationStatusFilterChange={setAutomationStatusFilter}
              onAutomationTriggerFilterChange={setAutomationTriggerFilter}
              onRunStatusFilterChange={setScanRunStatusFilter}
              onRunNowSourceJobIdChange={setRunNowSourceJobId}
              onRunNow={automation => void handleRunNow(automation)}
              onToggleStatus={automation => void handleToggleAutomationStatus(automation)}
            />
          )}
        </Box>
      </Flex>

      {session.sessionId && session.accessRole === 'owner' ? (
        <SessionShareDialog
          open={shareDialogOpen}
          sessionId={session.sessionId}
          visibility={session.sessionVisibility}
          onOpenChange={setShareDialogOpen}
          onVisibilityChange={handleSessionVisibilityChange}
        />
      ) : null}

      {taskDrawerOpen ? (
        <TaskDetailDrawer
          open
          selectedIssue={selectedIssue}
          detail={detail}
          linkedRun={linkedRun}
          verificationRun={verificationRun}
          detailLoading={detailLoading}
          onOpenChange={handleTaskDrawerOpenChange}
        />
      ) : null}

      <Dialog.Root
        open={composerOpen}
        onOpenChange={event => {
          setComposerOpen(event.open);
          if (!event.open) setAgentContractOpen(false);
        }}
        size="xl"
        placement="center"
        scrollBehavior="inside"
        preventScroll
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content
              layerStyle="drawerSurface"
              maxW="1040px"
              w="calc(100vw - 48px)"
              h="min(820px, calc(100dvh - 48px))"
              maxH="calc(100dvh - 48px)"
              borderRadius="xl"
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <Dialog.Header layerStyle="drawerHeader" px="5" py="4">
                <HStack justify="space-between">
                  <Dialog.Title fontSize="md" fontWeight="800" color="ui.text.primary">
                    {agentContractOpen && prepared ? 'Review task' : 'New background task'}
                  </Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <IconButton aria-label="Close" size="sm" variant="ghost">
                      <X size={16} />
                    </IconButton>
                  </Dialog.CloseTrigger>
                </HStack>
                <Dialog.Description srOnly>
                  Choose what the task should do, set its schedule, and review before creating it.
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body
                p="0"
                overflowY="auto"
                overscrollBehavior="contain"
                minH={0}
                flex="1"
                display="flex"
                flexDirection="column"
                bg="ui.surface.card"
              >
                {agentContractOpen && prepared ? (
                  <VStack align="stretch" gap="4" flex="1" minH={0} overflowY="auto" p="4">
                    <HStack justify="space-between" align="start" gap="3" flexWrap="wrap">
                      <Box minW={0}>
                        <Text fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
                          Task contract
                        </Text>
                        <Text mt="0.5" fontSize="xl" fontWeight="850" color="ui.text.primary">
                          {prepared.normalizedDraft.name}
                        </Text>
                        <Text mt="1" fontSize="sm" color="ui.text.secondary">
                          Review the exact contract before activating this task.
                        </Text>
                      </Box>
                      <StatusBadge tone="success">Ready</StatusBadge>
                    </HStack>

                    {prepared.normalizedDraft.slackDelivery && (
                      <Box p="3" borderWidth="1px" borderColor="ui.border.subtle">
                        <Text fontSize="sm" fontWeight="700">
                          Slack delivery · #{prepared.normalizedDraft.slackDelivery.channelName}
                        </Text>
                        <Text fontSize="xs" color="ui.text.secondary">
                          Executive summary and output links, plus approval and failure
                          notifications.
                          {prepared.normalizedDraft.slackDelivery.external
                            ? ' Shared outside your company through Slack Connect.'
                            : ''}
                        </Text>
                      </Box>
                    )}

                    <SimpleGrid columns={{ base: 1, md: 3 }} gap="3">
                      <Box
                        bg="ui.surface.drawerMuted"
                        borderWidth="1px"
                        borderColor="ui.border.subtle"
                        borderRadius="0"
                        p="3"
                      >
                        <Text fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
                          Schedule
                        </Text>
                        <Text mt="1" fontSize="sm" fontWeight="800" color="ui.text.primary">
                          {formatTrigger(prepared.normalizedDraft.trigger)}
                        </Text>
                        <Text mt="1" fontSize="xs" color="ui.text.secondary">
                          {formatTriggerDetail(prepared.normalizedDraft.trigger)}
                        </Text>
                      </Box>
                      <Box
                        bg="ui.surface.drawerMuted"
                        borderWidth="1px"
                        borderColor="ui.border.subtle"
                        borderRadius="0"
                        p="3"
                      >
                        <Text fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
                          Sources
                        </Text>
                        <Text mt="1" fontSize="sm" color="ui.text.primary" fontWeight="700">
                          Connected Attune data
                        </Text>
                      </Box>
                      <Box
                        bg="ui.surface.drawerMuted"
                        borderWidth="1px"
                        borderColor="ui.border.subtle"
                        borderRadius="0"
                        p="3"
                      >
                        <Text fontSize="xs" color="ui.text.tertiary" textTransform="uppercase">
                          Model
                        </Text>
                        <Text mt="1" fontSize="sm" fontWeight="800" color="ui.text.primary">
                          {researchModelLabel(
                            modelSelection.models,
                            prepared.normalizedDraft.modelId
                          )}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Box
                      as="details"
                      bg="ui.surface.drawerMuted"
                      borderWidth="1px"
                      borderColor="ui.border.subtle"
                      borderRadius="lg"
                      overflow="hidden"
                    >
                      <HStack
                        as="summary"
                        justify="space-between"
                        px="4"
                        py="3"
                        cursor="pointer"
                        listStyle="none"
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
                            Instructions and tool access
                          </Text>
                          <Text mt="0.5" fontSize="xs" color="ui.text.tertiary">
                            Advanced details for this task
                          </Text>
                        </Box>
                        <ChevronDown size={15} />
                      </HStack>
                      <VStack align="stretch" gap="3" px="4" pb="4">
                        <HStack gap="1.5" flexWrap="wrap">
                          {prepared.prompt.allowedTools.map(tool => (
                            <Badge key={tool} borderRadius="glassPill">
                              {tool}
                            </Badge>
                          ))}
                        </HStack>
                        <Textarea
                          name="agentPromptBody"
                          value={prepared.prompt.body}
                          readOnly
                          minH="220px"
                          bg="ui.surface.card"
                          borderColor="ui.border.subtle"
                          fontSize="xs"
                          lineHeight="1.5"
                        />
                      </VStack>
                    </Box>
                  </VStack>
                ) : (
                  <VStack align="stretch" gap="0" flex="1" minH={0}>
                    <HStack px="5" pt="4" gap="1.5">
                      <Button
                        size="sm"
                        variant={composerMode === 'preset' ? 'solid' : 'ghost'}
                        onClick={() => {
                          resetPrepared();
                          setComposerMode('preset');
                        }}
                      >
                        Templates
                      </Button>
                      <Button
                        size="sm"
                        variant={composerMode === 'custom' ? 'solid' : 'ghost'}
                        onClick={() => {
                          resetPrepared();
                          setComposerMode('custom');
                        }}
                      >
                        Custom instructions
                      </Button>
                    </HStack>
                    {composerMode === 'preset' ? (
                      <SimpleGrid columns={{ base: 1, lg: 12 }} gap="0">
                        <Box
                          gridColumn={{ base: 'auto', lg: 'span 5' }}
                          p="5"
                          borderRightWidth={{ base: '0', lg: '1px' }}
                          borderBottomWidth={{ base: '1px', lg: '0' }}
                          borderColor="ui.border.subtle"
                          minH={0}
                          display="flex"
                          flexDirection="column"
                        >
                          <VStack align="stretch" gap="4">
                            <HStack justify="space-between" align="end" gap="3">
                              <Box>
                                <Text
                                  fontSize="xs"
                                  color="ui.text.tertiary"
                                  textTransform="uppercase"
                                >
                                  Choose a task
                                </Text>
                                <Text mt="1" fontSize="sm" color="ui.text.secondary">
                                  Start with a proven job, then choose when it runs.
                                </Text>
                              </Box>
                              <Badge borderRadius="glassPill">
                                {visibleTemplates.length} tasks
                              </Badge>
                            </HStack>
                            {!executionConnectionsReady ? (
                              <HStack
                                justify="space-between"
                                align={{ base: 'start', md: 'center' }}
                                gap="3"
                                p="3"
                                borderWidth="1px"
                                borderColor="ui.border.subtle"
                                borderRadius="0"
                                bg="ui.surface.drawerMuted"
                                flexWrap="wrap"
                              >
                                <HStack gap="2" minW={0}>
                                  <Icon boxSize="4" color="ui.text.secondary">
                                    <Plug />
                                  </Icon>
                                  <Text fontSize="sm" color="ui.text.secondary">
                                    Execution tasks unlock with WordPress.com, Webflow, Shopify, or
                                    Custom MCP.
                                  </Text>
                                </HStack>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => {
                                    setComposerOpen(false);
                                    setWorkspaceMode('integrations');
                                  }}
                                >
                                  Connect
                                </Button>
                              </HStack>
                            ) : null}
                            <VStack data-section="agentMarketplaceScroller" align="stretch" gap="4">
                              {marketplaceSections.map(section =>
                                section.templates.length ? (
                                  <Box
                                    key={section.category}
                                    borderTopWidth="1px"
                                    borderTopStyle="solid"
                                    borderTopColor="ui.border.subtle"
                                    pt="4"
                                  >
                                    <HStack justify="space-between" align="start" mb="3" gap="3">
                                      <Box>
                                        <Text
                                          fontSize="md"
                                          fontWeight="650"
                                          color="ui.text.primary"
                                        >
                                          {section.title}
                                        </Text>
                                        <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
                                          {section.description}
                                        </Text>
                                      </Box>
                                      <Badge borderRadius="glassPill">
                                        {section.templates.length}
                                      </Badge>
                                    </HStack>
                                    <SimpleGrid columns={1} gap="1.5">
                                      {section.templates.map(template => {
                                        const templatePrompt = promptById.get(
                                          template.prompt_template_id
                                        );
                                        const selected = selectedTemplateId === template.id;
                                        const executionAgent = isExecutionAgent(template.kind);
                                        const locked = executionAgent && !executionConnectionsReady;
                                        return (
                                          <Button
                                            key={template.id}
                                            type="button"
                                            variant="plain"
                                            display="block"
                                            w="full"
                                            h="auto"
                                            whiteSpace="normal"
                                            minH="72px"
                                            px="3"
                                            py="2.5"
                                            borderRadius="lg"
                                            textAlign="left"
                                            bg={selected ? 'ui.surface.drawerMuted' : 'transparent'}
                                            boxShadow={
                                              selected
                                                ? 'inset 0 0 0 1px var(--chakra-colors-ui-border-default)'
                                                : 'none'
                                            }
                                            color="ui.text.primary"
                                            opacity={locked ? 0.58 : 1}
                                            cursor={locked ? 'not-allowed' : 'pointer'}
                                            aria-disabled={locked}
                                            aria-pressed={selected}
                                            _hover={
                                              locked
                                                ? undefined
                                                : {
                                                    bg: 'ui.surface.selected',
                                                  }
                                            }
                                            _focusVisible={{
                                              outline: '2px solid',
                                              outlineColor: 'ui.border.accent',
                                            }}
                                            onClick={() => {
                                              if (!locked) applyTemplateAssignment(template);
                                            }}
                                          >
                                            <HStack align="center" gap="3" h="full">
                                              <Box
                                                boxSize="11"
                                                borderRadius="md"
                                                bg="ui.surface.drawerMuted"
                                                borderWidth="1px"
                                                borderStyle="solid"
                                                borderColor="ui.border.subtle"
                                                color="ui.text.primary"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                flexShrink={0}
                                              >
                                                <Icon boxSize="5" color="ui.text.secondary">
                                                  {executionAgent ? <Workflow /> : <Activity />}
                                                </Icon>
                                              </Box>
                                              <Box minW={0} flex="1">
                                                <Text
                                                  fontSize="sm"
                                                  fontWeight="700"
                                                  color="ui.text.primary"
                                                  lineClamp={1}
                                                >
                                                  {agentRoleLabel(template, templatePrompt)}
                                                </Text>
                                                <Text
                                                  fontSize="sm"
                                                  color="ui.text.tertiary"
                                                  mt="0.5"
                                                  lineClamp={1}
                                                >
                                                  {summarizeAgent(
                                                    template.kind,
                                                    template.description
                                                  )}
                                                </Text>
                                              </Box>
                                              {selected ? (
                                                <CheckCircle2
                                                  size={16}
                                                  color="var(--chakra-colors-status-success-fg)"
                                                />
                                              ) : locked ? (
                                                <Badge borderRadius="full">Connect</Badge>
                                              ) : null}
                                            </HStack>
                                          </Button>
                                        );
                                      })}
                                    </SimpleGrid>
                                  </Box>
                                ) : null
                              )}
                            </VStack>
                          </VStack>
                        </Box>

                        <Box
                          gridColumn={{ base: 'auto', lg: 'span 7' }}
                          p="5"
                          bg="ui.surface.card"
                          minH={0}
                        >
                          <VStack align="stretch" gap="3" h="full">
                            <Box
                              bg="ui.surface.drawer"
                              borderWidth="1px"
                              borderColor="ui.border.subtle"
                              borderRadius="lg"
                              p="4"
                            >
                              <HStack justify="space-between" align="start" gap="3">
                                <HStack align="start" gap="3" minW={0}>
                                  <Box
                                    boxSize="10"
                                    borderRadius="md"
                                    bg="ui.surface.drawerMuted"
                                    borderWidth="1px"
                                    borderColor="ui.border.subtle"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                  >
                                    <Icon boxSize="5" color="ui.text.secondary">
                                      <Bot />
                                    </Icon>
                                  </Box>
                                  <Box minW={0}>
                                    <Text
                                      fontSize="xs"
                                      color="ui.text.tertiary"
                                      textTransform="uppercase"
                                    >
                                      Selected task
                                    </Text>
                                    <Text
                                      mt="0.5"
                                      fontSize="lg"
                                      fontWeight="800"
                                      color="ui.text.primary"
                                      lineHeight="1.2"
                                    >
                                      {agentRoleLabel(selectedTemplate, selectedTemplatePrompt)}
                                    </Text>
                                  </Box>
                                </HStack>
                                <Badge borderRadius="full">Template</Badge>
                              </HStack>
                              <Text mt="3" fontSize="sm" color="ui.text.secondary" lineHeight="1.5">
                                {summarizeAgent(
                                  selectedTemplate?.kind,
                                  selectedTemplate?.description
                                )}
                              </Text>
                              <SimpleGrid mt="3" columns={{ base: 1, md: 2 }} gap="2">
                                <Box
                                  bg="ui.surface.drawerMuted"
                                  borderWidth="1px"
                                  borderColor="ui.border.subtle"
                                  borderRadius="md"
                                  p="2.5"
                                >
                                  <Text
                                    fontSize="xs"
                                    color="ui.text.tertiary"
                                    textTransform="uppercase"
                                  >
                                    {isExecutionAgent(selectedTemplate?.kind)
                                      ? 'Executes'
                                      : 'Creates'}
                                  </Text>
                                  <Text
                                    mt="0.5"
                                    fontSize="sm"
                                    fontWeight="700"
                                    color="ui.text.primary"
                                  >
                                    {isExecutionAgent(selectedTemplate?.kind)
                                      ? 'Work orders'
                                      : 'Work orders'}
                                  </Text>
                                </Box>
                                <Box
                                  bg="ui.surface.drawerMuted"
                                  borderWidth="1px"
                                  borderColor="ui.border.subtle"
                                  borderRadius="md"
                                  p="2.5"
                                >
                                  <Text
                                    fontSize="xs"
                                    color="ui.text.tertiary"
                                    textTransform="uppercase"
                                  >
                                    Playbook
                                  </Text>
                                  <Text
                                    mt="0.5"
                                    fontSize="sm"
                                    fontWeight="700"
                                    color="ui.text.primary"
                                  >
                                    Approved template
                                  </Text>
                                </Box>
                              </SimpleGrid>
                              <Box mt="3">
                                <Text
                                  mb="1.5"
                                  fontSize="xs"
                                  color="ui.text.tertiary"
                                  textTransform="uppercase"
                                >
                                  Data access
                                </Text>
                                <Text fontSize="sm" color="ui.text.secondary">
                                  Uses connected Attune sources automatically.
                                </Text>
                              </Box>
                            </Box>
                            <Field.Root>
                              <Field.Label
                                mb="1.5"
                                fontSize="xs"
                                color="ui.text.tertiary"
                                textTransform="uppercase"
                              >
                                Task name
                              </Field.Label>
                              <Input
                                name="agentName"
                                value={automationName}
                                onChange={event => {
                                  resetPrepared();
                                  setAutomationName(event.target.value);
                                }}
                                bg="ui.surface.input"
                                borderColor="ui.border.subtle"
                              />
                            </Field.Root>
                            {agentComposerModelControl}
                            {scheduleControls}
                            {slackDeliveryControl}
                          </VStack>
                        </Box>
                      </SimpleGrid>
                    ) : (
                      <Box p="4">
                        <VStack align="stretch" gap="4">
                          <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
                            <Field.Root>
                              <Field.Label
                                mb="1.5"
                                fontSize="xs"
                                color="ui.text.tertiary"
                                textTransform="uppercase"
                              >
                                Name
                              </Field.Label>
                              <Input
                                name="agentName"
                                type="text"
                                autoComplete="off"
                                value={automationName}
                                onChange={event => {
                                  resetPrepared();
                                  setAutomationName(event.target.value);
                                }}
                                bg="ui.surface.input"
                                borderColor="ui.border.subtle"
                              />
                            </Field.Root>
                            <Field.Root>
                              <Field.Label
                                mb="1.5"
                                fontSize="xs"
                                color="ui.text.tertiary"
                                textTransform="uppercase"
                              >
                                Description
                              </Field.Label>
                              <Input
                                name="agentDescription"
                                type="text"
                                autoComplete="off"
                                value={automationDescription}
                                onChange={event => {
                                  resetPrepared();
                                  setAutomationDescription(event.target.value);
                                }}
                                bg="ui.surface.input"
                                borderColor="ui.border.subtle"
                              />
                            </Field.Root>
                          </SimpleGrid>

                          <Field.Root>
                            <Field.Label
                              mb="1.5"
                              fontSize="sm"
                              fontWeight="700"
                              color="ui.text.primary"
                            >
                              What should this task do?
                            </Field.Label>
                            <Textarea
                              name="agentPrompt"
                              autoComplete="off"
                              value={customPromptBody}
                              onChange={event => {
                                resetPrepared();
                                setCustomPromptBody(event.target.value);
                              }}
                              bg="ui.surface.input"
                              borderColor="ui.border.subtle"
                              fontSize="sm"
                              lineHeight="1.6"
                              minH="260px"
                              placeholder="Describe the outcome, what to review, and what should be returned."
                            />
                            <Field.HelperText color="ui.text.tertiary">
                              The task can use every connected Attune data source automatically.
                            </Field.HelperText>
                          </Field.Root>

                          {agentComposerModelControl}
                          {scheduleControls}
                          {slackDeliveryControl}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer
                p="5"
                borderTopWidth="1px"
                borderColor="ui.border.subtle"
                bg="ui.surface.drawerHeader"
              >
                <HStack justify="space-between" w="full">
                  {agentContractOpen && prepared ? (
                    <>
                      <Button variant="ghost" onClick={() => setAgentContractOpen(false)}>
                        Back
                      </Button>
                      <Button
                        variant="solid"
                        disabled={selectedTemplateLocked}
                        loading={
                          busyAgent?.id === 'confirm-automation' && busyAgent.action === 'confirm'
                        }
                        onClick={() => void confirmPrepared()}
                      >
                        Create task
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setComposerOpen(false);
                          setAgentContractOpen(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="solid"
                        disabled={selectedTemplateLocked}
                        loading={
                          busyAgent?.id === 'prepare-automation' && busyAgent.action === 'prepare'
                        }
                        onClick={() => {
                          if (prepared) {
                            setAgentContractOpen(true);
                            return;
                          }
                          void prepareAutomation();
                        }}
                      >
                        Review task
                      </Button>
                    </>
                  )}
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={editAutomationOpen && Boolean(editingAutomation)}
        onOpenChange={event => {
          setEditAutomationOpen(event.open);
          if (!event.open) {
            setEditingAutomationId(null);
            setEditingModelId('');
            setEditRunNowSourceJobId('');
          }
        }}
        size="lg"
        placement="center"
        scrollBehavior="inside"
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content
              layerStyle="drawerSurface"
              maxW="980px"
              w="calc(100vw - 64px)"
              h="min(860px, calc(100dvh - 64px))"
              maxH="calc(100dvh - 64px)"
              borderRadius="xl"
              overflow="hidden"
              display="flex"
              flexDirection="column"
            >
              <Dialog.Header layerStyle="drawerHeader" px="5" py="4">
                <HStack justify="space-between" gap="3">
                  <Box minW={0}>
                    <Dialog.Title fontSize="md" fontWeight="800" color="ui.text.primary">
                      Edit background task
                    </Dialog.Title>
                    <Dialog.Description mt="0.5" fontSize="sm" color="ui.text.secondary">
                      Changes apply to future runs. Existing run history stays intact.
                    </Dialog.Description>
                  </Box>
                  <Dialog.CloseTrigger asChild>
                    <IconButton aria-label="Close" size="sm" variant="ghost">
                      <X size={16} />
                    </IconButton>
                  </Dialog.CloseTrigger>
                </HStack>
              </Dialog.Header>
              <Dialog.Body
                p="5"
                flex="1"
                minH={0}
                overflowY="auto"
                overscrollBehavior="contain"
                bg="ui.surface.card"
              >
                {editingAutomation ? (
                  <VStack align="stretch" gap="4">
                    <HStack justify="space-between" gap="3" flexWrap="wrap">
                      <HStack gap="2">
                        <StatusBadge tone={statusTone(editingAutomation.status)}>
                          {titleLabel(editingAutomation.status)}
                        </StatusBadge>
                        <Text fontSize="xs" color="ui.text.tertiary">
                          {formatNextAgentRun(editingDraftTrigger, editingAutomation.status)}
                        </Text>
                      </HStack>
                      <HStack gap="1.5">
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={
                            editingAutomation.status !== 'enabled' ||
                            (editingRunTriggerType === 'sampler_completed_delay' &&
                              !editRunNowSourceJobId.trim())
                          }
                          loading={
                            busyAgent?.id === editingAutomation.id && busyAgent.action === 'run'
                          }
                          onClick={() => void handleRunEditedAutomation()}
                        >
                          <PlayCircle size={14} />
                          Run now
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          loading={
                            busyAgent?.id === editingAutomation.id &&
                            (busyAgent.action === 'pause' || busyAgent.action === 'resume')
                          }
                          onClick={() => void handleToggleAutomationStatus(editingAutomation)}
                        >
                          {editingAutomation.status === 'enabled' ? (
                            <PauseCircle size={14} />
                          ) : (
                            <PlayCircle size={14} />
                          )}
                          {editingAutomation.status === 'enabled' ? 'Pause' : 'Resume'}
                        </Button>
                      </HStack>
                    </HStack>

                    {editingRunTriggerType === 'sampler_completed_delay' ? (
                      <Field.Root>
                        <Field.Label fontSize="xs" color="ui.text.tertiary">
                          Data refresh job ID
                        </Field.Label>
                        <Input
                          value={editRunNowSourceJobId}
                          onChange={event => setEditRunNowSourceJobId(event.target.value)}
                          placeholder="Paste the completed sampler job ID"
                          bg="ui.surface.input"
                          borderColor="ui.border.subtle"
                        />
                        <Field.HelperText color="ui.text.tertiary">
                          Required only when running this after-refresh task manually.
                        </Field.HelperText>
                      </Field.Root>
                    ) : null}

                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
                      <Field.Root>
                        <Field.Label fontSize="xs" color="ui.text.tertiary">
                          Name
                        </Field.Label>
                        <Input
                          value={automationName}
                          onChange={event => setAutomationName(event.target.value)}
                          bg="ui.surface.input"
                          borderColor="ui.border.subtle"
                        />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="xs" color="ui.text.tertiary">
                          Description
                        </Field.Label>
                        <Input
                          value={automationDescription}
                          onChange={event => setAutomationDescription(event.target.value)}
                          bg="ui.surface.input"
                          borderColor="ui.border.subtle"
                        />
                      </Field.Root>
                    </SimpleGrid>

                    {editingAutomation.kind === 'custom_prompt' ? (
                      <Field.Root>
                        <Field.Label fontSize="sm" fontWeight="700" color="ui.text.primary">
                          What should this task do?
                        </Field.Label>
                        <Textarea
                          value={customPromptBody}
                          onChange={event => setCustomPromptBody(event.target.value)}
                          minH="280px"
                          bg="ui.surface.input"
                          borderColor="ui.border.subtle"
                          fontSize="sm"
                          lineHeight="1.6"
                        />
                        <Field.HelperText color="ui.text.tertiary">
                          Connected Attune data sources are available automatically.
                        </Field.HelperText>
                      </Field.Root>
                    ) : null}

                    {editAutomationModelControl}
                    {scheduleControls}
                    {slackDeliveryControl}
                  </VStack>
                ) : null}
              </Dialog.Body>
              <Dialog.Footer
                px="5"
                py="4"
                borderTopWidth="1px"
                borderColor="ui.border.subtle"
                bg="ui.surface.drawerHeader"
              >
                <HStack justify="flex-end" gap="2" w="full">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditAutomationOpen(false);
                      setEditingAutomationId(null);
                      setEditingModelId('');
                      setEditRunNowSourceJobId('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    loading={
                      Boolean(editingAutomation) &&
                      busyAgent?.id === editingAutomation?.id &&
                      busyAgent?.action === 'edit'
                    }
                    onClick={() => void handleSaveAutomation()}
                  >
                    Save changes
                  </Button>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={northStarOpen}
        onOpenChange={event => {
          setNorthStarOpen(event.open);
          if (!event.open && northStarDeepLink) {
            clearWorkspaceSearchParams(['northStar']);
          }
        }}
        size="xl"
      >
        <Portal>
          <Dialog.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
          <Dialog.Positioner display="flex" alignItems="center" justifyContent="center" h="100dvh">
            <Dialog.Content
              layerStyle="drawerSurface"
              maxW="900px"
              w="94vw"
              maxH="90dvh"
              overflow="hidden"
            >
              <Dialog.Header layerStyle="drawerHeader" p="4">
                <HStack justify="space-between">
                  <Dialog.Title fontSize="md" fontWeight="800" color="ui.text.primary">
                    North Star / COMPANY.md
                  </Dialog.Title>
                  <Dialog.CloseTrigger asChild>
                    <IconButton aria-label="Close" size="sm" variant="ghost">
                      <X size={16} />
                    </IconButton>
                  </Dialog.CloseTrigger>
                </HStack>
                <Dialog.Description srOnly>
                  Edit the workspace North Star markdown, then prepare and approve the next version.
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body p="4" overflowY="auto" bg="ui.surface.drawer">
                <VStack align="stretch" gap="4">
                  <Field.Root>
                    <Field.Label
                      mb="1.5"
                      fontSize="xs"
                      color="ui.text.tertiary"
                      textTransform="uppercase"
                    >
                      Title
                    </Field.Label>
                    <Input
                      name="northStarTitle"
                      type="text"
                      autoComplete="off"
                      value={northStarTitle}
                      onChange={event => {
                        setPreparedNorthStar(null);
                        setNorthStarTitle(event.target.value);
                      }}
                      bg="ui.surface.input"
                      borderColor="ui.border.subtle"
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label
                      mb="1.5"
                      fontSize="xs"
                      color="ui.text.tertiary"
                      textTransform="uppercase"
                    >
                      Markdown
                    </Field.Label>
                    <Textarea
                      name="northStarMarkdown"
                      autoComplete="off"
                      value={northStarBody}
                      onChange={event => {
                        setPreparedNorthStar(null);
                        setNorthStarBody(event.target.value);
                      }}
                      rows={18}
                      bg="ui.surface.input"
                      borderColor="ui.border.subtle"
                      fontSize="sm"
                      lineHeight="1.6"
                    />
                  </Field.Root>
                  {preparedNorthStar ? (
                    <Box
                      bg="ui.surface.drawerMuted"
                      borderWidth="1px"
                      borderColor="status.success.border"
                      borderRadius="0"
                      p="3"
                    >
                      <HStack gap="2">
                        <Icon boxSize="4" color="status.success.fg">
                          <CheckCircle2 />
                        </Icon>
                        <Text fontSize="sm" fontWeight="800">
                          Version ready for approval
                        </Text>
                      </HStack>
                      <Text mt="1" fontSize="xs" color="ui.text.tertiary">
                        Expires {formatDate(preparedNorthStar.preflightTokenExpiresAt)}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer
                p="4"
                borderTopWidth="1px"
                borderColor="ui.border.subtle"
                bg="ui.surface.drawerHeader"
              >
                <HStack justify="space-between" w="full">
                  <Button variant="ghost" onClick={() => setNorthStarOpen(false)}>
                    Cancel
                  </Button>
                  <HStack>
                    <Button
                      variant="outline"
                      loading={northStarBusy === 'prepare'}
                      disabled={!northStarBody.trim()}
                      onClick={() => void prepareNorthStar()}
                    >
                      Draft update
                    </Button>
                    <Button
                      variant="solid"
                      loading={northStarBusy === 'confirm'}
                      disabled={!preparedNorthStar}
                      onClick={() => void confirmNorthStar()}
                    >
                      Approve update
                    </Button>
                  </HStack>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}
